import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Send, Sparkles, X, MessageSquare, Mic, MicOff, Volume2, VolumeX, ChevronDown, Trash2, HelpCircle, Eye, Link as LinkIcon, Users, Layers, PlayCircle, Lock } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { TID } from "@/constants/testIds";
import { useAiFocus } from "@/lib/aiFocus";
import { toast } from "sonner";
import { resolveVoiceCommand } from "@/lib/voiceCommands";
import { emitCreate, emitAction, useActionListener } from "@/lib/createBus";
import { useIsMobile } from "@/lib/useIsMobile";
import { stripMarkdownForSpeech } from "@/lib/speechText";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useFeature } from "@/lib/entitlements";


// Feb 2026 — infer the GAAP account type from a spoken account name so
// the "create new account" fallback doesn't hardcode "expense" (which
// silently mis-created things like "Rental Income" as an expense).
// Returns either `{type: "revenue" | "expense" | ...}` or, when the
// name is genuinely ambiguous, `{ambiguous: true, options: [...]}` so
// the caller can ask a clarifying question instead of guessing.
function inferAccountTypeFromName(name) {
  const s = String(name || "").toLowerCase();
  // Ambiguous — could go multiple ways. Ask the user.
  if (/\brefund\b/.test(s) || /\breimburs/.test(s) || /\brebate\b/.test(s) || /\bchargeback\b/.test(s)) {
    return { ambiguous: true, options: ["revenue (contra-sales)", "expense"] };
  }
  // Revenue signals — check first so "rental income" beats "rental".
  if (/\b(income|revenue|sales|earnings|proceeds|receipts?|royalt|commission earned|interest earned|dividend)\b/.test(s)) {
    return { type: "revenue" };
  }
  if (/\b(rent(?:al)?|consulting|freelance|service fee|licensing|subscription revenue|tuition|admission|donation|grant)\b/.test(s) && !/\bpaid\b/.test(s)) {
    return { type: "revenue" };
  }
  // COGS
  if (/\b(cogs|cost of goods|cost of sales|direct (?:materials?|labor|labour)|inventory purchases?)\b/.test(s)) {
    return { type: "cogs" };
  }
  // Liability
  if (/\b(loan|line of credit|credit card|payable|owed|note payable|mortgage|liab|deferred revenue|unearned|payroll taxes? owed|sales tax payable)\b/.test(s)) {
    return { type: "liability" };
  }
  // Asset
  if (/\b(equipment|vehicle|building|land|machinery|furniture|computer|laptop|inventory|receivable|deposit paid|prepaid|checking|savings|cash|bank|asset)\b/.test(s)) {
    return { type: "asset" };
  }
  // Equity
  if (/\b(owner'?s?\s+(?:draw|contribution|equity|capital)|retained earnings|distribution|dividend paid|partner (?:capital|draw))\b/.test(s)) {
    return { type: "equity" };
  }
  // Default: expense (covers the vast majority of un-categorized cases —
  // office supplies, meals, subscriptions, utilities, marketing, etc.).
  return { type: "expense" };
}


// Compact confirm card used by the create-account / recategorize / transfer
// flows. Same visual language as BulkApproveCard but generic — takes a title,
// a busy label, and an onConfirm/onDismiss pair.
// Recommendation card for the meaning-first resolver: one primary action
// (use existing / create & categorize), optional "also N similar", up to
// two alternative existing accounts, and an owner-draw escape hatch.
function CategoryRecommendCard({ card, onApply, onDismiss, onShowSimilar, onClearSimilar, onSweepSkip }) {
  const [busy, setBusy] = useState(false);
  const { rec, similar, alternatives, direction, similarMode, setContact } = card;
  const [fixContact, setFixContact] = useState(true);
  const ticked = similarMode?.active ? (similarMode.ticked || []) : null;
  const a = rec.account;
  const isNew = rec.kind === "new";
  const run = async (args) => { if (busy) return; setBusy(true); try { await onApply({ ...args, setContactName: setContact && fixContact ? setContact : null }); } finally { setBusy(false); } };
  const sweep = !!card.sweep;
  const primaryArgs = { ...(isNew ? { newAccount: a } : { accountId: a.id }), ...(sweep && ticked ? { txnIds: ticked } : {}) };
  const nTicked = ticked ? ticked.length : 0;
  return (
    <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 space-y-2" data-testid="category-recommend-card">
      <div className="text-[12px] text-slate-700 flex flex-wrap gap-x-2 gap-y-0.5">
        <span className="font-semibold text-slate-900">{a.code || "new"} {a.name}</span>
        <span className="text-slate-500">· {[...new Set([a.type, a.subtype, a.detail_type].filter(Boolean).map(s => String(s).replace(/_/g, " ")))].join(" › ")}</span>
        {a.parent_name && <span className="text-slate-500">· under {a.parent_name}</span>}
        <span className={`px-1.5 rounded text-[10px] font-semibold uppercase tracking-wide ${isNew ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{isNew ? (rec.subaccount ? "new sub-account" : "new account") : "existing"}</span>
      </div>
      {setContact && (
        <label className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer" data-testid="category-recommend-set-contact">
          <input type="checkbox" checked={fixContact} onChange={e => setFixContact(e.target.checked)} />
          Also set contact to <b>{setContact}</b>{card.txn?.contact_name ? <span className="text-slate-500">(currently {card.txn.contact_name})</span> : null}
        </label>
      )}
      <div className="flex flex-wrap gap-1.5">
        <button type="button" disabled={busy} onClick={() => run(primaryArgs)} data-testid="category-recommend-apply"
                className="text-[12px] font-medium px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
          {busy ? "Applying…" : isNew ? (rec.subaccount ? "Create sub-account & categorize" : "Create account & categorize") : "Use this"}{sweep && nTicked ? ` (${nTicked} ticked)` : ""}
        </button>
        {similar > 0 && !sweep && (
          <button type="button" disabled={busy || !!ticked} onClick={() => onShowSimilar?.(card)} data-testid="category-recommend-show-similar"
                  className="text-[12px] font-medium px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            Show {similar} similar
          </button>
        )}
        {ticked && !sweep && (
          <>
            <button type="button" disabled={busy || ticked.length === 0} onClick={() => run({ ...primaryArgs, txnIds: ticked })}
                    data-testid="category-recommend-apply-ticked"
                    className="text-[12px] font-medium px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              Apply to {ticked.length}
            </button>
            <button type="button" disabled={busy} onClick={() => onClearSimilar?.(card)} data-testid="category-recommend-clear-similar"
                    className="text-[12px] font-medium px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              Clear
            </button>
          </>
        )}
        {similar > 0 && (
          <button type="button" disabled={busy} onClick={() => run({ ...primaryArgs, withSimilar: true })} data-testid="category-recommend-apply-similar"
                  className="text-[12px] font-medium px-3 py-1.5 rounded-md border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
            {sweep ? `Use for ${nTicked || similar + 1} + save rule` : `Also ${similar} similar + save rule`}
          </button>
        )}
        {sweep && (
          <button type="button" disabled={busy} onClick={() => onSweepSkip?.()} data-testid="category-recommend-sweep-skip"
                  className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
            Skip vendor
          </button>
        )}
        <button type="button" disabled={busy} onClick={onDismiss} data-testid="category-recommend-dismiss"
                className="text-[12px] px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
          Not this
        </button>
      </div>
      {(alternatives?.length > 0 || (direction === "money_out" && !/owner/i.test(a.name))) && (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
          <span>Or:</span>
          {alternatives.map(alt => (
            <button key={alt.id} type="button" disabled={busy} onClick={() => run({ accountId: alt.id })}
                    data-testid={`category-recommend-alt-${alt.id}`}
                    className="px-2 py-0.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700">
              {alt.code} {alt.name}
            </button>
          ))}
          {direction === "money_out" && !/owner/i.test(a.name) && (
            <button type="button" disabled={busy} onClick={() => run({ newAccount: { semantic: "owner_draw", name: "Owner's Draw", type: "equity" } })}
                    data-testid="category-recommend-owner-draw"
                    className="px-2 py-0.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700">
              Not a business expense (Owner's Draw)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function InlineConfirmCard({ testId, tone = "fuchsia", confirmLabel = "Yes, do it", cancelLabel = "No, thanks", busyLabel = "Applying…", onConfirm, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [handled, setHandled] = useState(false);
  const toneCls = {
    fuchsia: "border-fuchsia-200 bg-fuchsia-50/60",
    indigo:  "border-indigo-200 bg-indigo-50/60",
    emerald: "border-emerald-200 bg-emerald-50/60",
  }[tone] || "border-slate-200 bg-slate-50";
  const btnCls = {
    fuchsia: "bg-fuchsia-600 hover:bg-fuchsia-700",
    indigo:  "bg-indigo-600 hover:bg-indigo-700",
    emerald: "bg-emerald-600 hover:bg-emerald-700",
  }[tone] || "bg-slate-700 hover:bg-slate-800";

  const confirm = async () => {
    if (busy || handled) return;
    setBusy(true);
    try { await onConfirm(); setHandled(true); } finally { setBusy(false); }
  };
  const dismiss = () => { if (handled) return; setHandled(true); onDismiss?.(); };

  return (
    <div data-testid={testId} className={`mt-2 rounded-md border px-3 py-2 text-[13px] ${toneCls}`}>
      <div className="flex items-center gap-2">
        <button data-testid={`${testId}-yes`} disabled={busy || handled} onClick={confirm}
                className={`px-3 py-1 text-xs font-medium rounded text-white disabled:opacity-50 ${btnCls}`}>
          {busy ? busyLabel : confirmLabel}
        </button>
        <button data-testid={`${testId}-no`} disabled={busy || handled} onClick={dismiss}
                className="px-3 py-1 text-xs font-medium rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {cancelLabel}
        </button>
      </div>
    </div>
  );
}

// Compact two-input form shown under an assistant message when the backend
// detected a bimodal amount distribution. Lets the user name the below /
// above categories and fires them through the same natural-language flow.
function SplitHintForm({ hint, onApply, onDismiss }) {
  const [below, setBelow] = useState(hint.previous_below || "");
  const [above, setAbove] = useState(hint.previous_above || "");
  const [busy, setBusy] = useState(false);
  const hasHistory = !!(hint.previous_below || hint.previous_above);
  const apply = async () => {
    if (busy || !below.trim() || !above.trim()) return;
    setBusy(true);
    try { await onApply(below.trim(), above.trim()); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-[12px]"
         data-testid="split-hint">
      <div className="text-emerald-900 mb-1.5 font-medium flex items-center justify-between">
        <span>Split at ${hint.threshold.toLocaleString()}</span>
        {hasHistory && (
          <span data-testid="split-hint-recall" className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
            recalled from last time
          </span>
        )}
      </div>
      <div className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1 items-center">
        <span className="text-emerald-900/80 text-[11px]">
          {hint.below.count} rows (${hint.below.min}–${hint.below.max}) →
        </span>
        <input
          data-testid="split-hint-below"
          value={below}
          onChange={(e) => setBelow(e.target.value)}
          placeholder="e.g. Meals"
          className="w-full px-2 py-1 rounded border border-slate-300 bg-white text-[12px]"
          onKeyDown={(e) => { if (e.key === "Enter" && above.trim()) apply(); }}
        />
        <span className="text-emerald-900/80 text-[11px]">
          {hint.above.count} rows (${hint.above.min}–${hint.above.max}) →
        </span>
        <input
          data-testid="split-hint-above"
          value={above}
          onChange={(e) => setAbove(e.target.value)}
          placeholder="e.g. Office Supplies"
          className="w-full px-2 py-1 rounded border border-slate-300 bg-white text-[12px]"
          onKeyDown={(e) => { if (e.key === "Enter" && below.trim()) apply(); }}
        />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button
          data-testid="split-hint-apply"
          disabled={busy || !below.trim() || !above.trim()}
          onClick={apply}
          className="px-3 py-1 text-xs font-medium rounded text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy ? "Applying…" : (hasHistory ? "Yes, same again" : "Apply split")}
        </button>
        <button
          data-testid="split-hint-dismiss"
          disabled={busy}
          onClick={onDismiss}
          className="px-3 py-1 text-xs font-medium rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Ignore
        </button>
      </div>
    </div>
  );
}

// Interactive confirmation card shown in the chat stream after a user
// approves a transaction that has other unapproved siblings from the same
// contact. Yes → bulk-approve + rule; No → dismiss.
function BulkApproveCard({ similar, createRule, ruleExists, ticked, currentId, onDone, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [handled, setHandled] = useState(false);
  const catName = similar.category_account_name || similar.category_account_code || "the same category";
  const ids = ticked || null;
  const n = ids ? ids.length : similar.count;

  const applyBulk = async (withRule = createRule) => {
    if (busy || handled) return;
    setBusy(true);
    try {
      let txnIds = ids;
      if (!txnIds) {
        if (similar.contact_id) {
          const r = await api.get(`/companies/${currentId}/transactions?contact_id=${similar.contact_id}&limit=1000`);
          txnIds = (r.data.transactions || []).filter((t) => !t.human_reviewed).map((t) => t.id);
        } else {
          txnIds = (similar.items || []).map((x) => x.id);
        }
      }
      const res = await api.post(
        `/companies/${currentId}/transactions/apply-bulk-approve-rule`,
        {
          txn_ids: txnIds,
          category_account_id: similar.category_account_id,
          contact_id: similar.contact_id,
          contact_name: similar.contact_name,
          create_rule: !!withRule,
          match_text: similar.match_kind === "description" ? similar.match_value : null,
        }
      );
      const updated = res.data?.updated || 0;
      const ruleId = res.data?.rule_id;
      const msg = ruleId
        ? `Approved ${updated} transaction${updated === 1 ? "" : "s"} and created a rule for ${similar.contact_name}.`
        : `Approved ${updated} transaction${updated === 1 ? "" : "s"}.`;
      setHandled(true);
      onDone(msg);
    } catch (e) {
      onDone("Sorry — bulk approval failed.");
      setHandled(true);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    if (handled) return;
    setHandled(true);
    onDismiss();
  };

  return (
    <div
      data-testid="bulk-approve-card"
      className="mt-2 rounded-md border border-fuchsia-200 bg-fuchsia-50/60 px-3 py-2 text-[13px]"
    >
      <div className="text-fuchsia-900 mb-2">
        <span className="font-semibold">{n}</span>{ids && ids.length !== similar.count ? ` of ${similar.count}` : " other"}{" "}
        <span className="font-semibold">{similar.contact_name}</span>{" "}
        transaction{n === 1 ? "" : "s"} → categorize as{" "}
        <span className="font-semibold">{catName}</span> and approve?
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="bulk-approve-yes"
          disabled={busy || handled || n === 0}
          onClick={() => applyBulk(false)}
          className="px-3 py-1 text-xs font-medium rounded bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50"
        >
          {busy ? "Applying…" : `Yes, approve ${n}`}
        </button>
        {!ruleExists && (
          <button
            data-testid="bulk-approve-yes-rule"
            disabled={busy || handled || n === 0}
            onClick={() => applyBulk(true)}
            className="px-3 py-1 text-xs font-medium rounded border border-fuchsia-300 bg-white text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50"
          >
            Approve {n} + create rule
          </button>
        )}
        <button
          data-testid="bulk-approve-no"
          disabled={busy || handled}
          onClick={dismiss}
          className="px-3 py-1 text-xs font-medium rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          No, thanks
        </button>
      </div>
    </div>
  );
}

// --------------------- Report → one-sentence spoken summary ---------------------
// Runs entirely on the client from the report API's JSON response so we never
// spin up a chat LLM just to read numbers out loud.
const _fmt$ = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${v < 0 ? "-" : ""}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${v < 0 ? "-" : ""}$${(abs / 1_000).toFixed(1)}K`;
  return `${v < 0 ? "-" : ""}$${abs.toFixed(2)}`;
};
function summarizeReport(kind, name, data, filters = {}) {
  if (!data) return `No data for ${name}.`;
  const range = (filters.start && filters.end) ? ` from ${filters.start} to ${filters.end}` : "";
  const basis = filters.basis ? ` on ${filters.basis} basis` : "";
  const suffix = `${range}${basis}`;
  try {
    if (kind === "income-statement") {
      const rev = data.total_revenue || 0;
      const exp = data.total_expense || 0;
      const ni  = data.net_income ?? (rev - exp);
      const topExp = [...(data.expenses || [])]
        .sort((a, b) => (b.amount || 0) - (a.amount || 0)).slice(0, 3)
        .map(x => `${x.account_name || x.name} at ${_fmt$(x.amount)}`);
      const topLine = topExp.length ? ` Top expenses: ${topExp.join(", ")}.` : "";
      return `${name}${suffix}: revenue ${_fmt$(rev)}, expenses ${_fmt$(exp)}, net income ${_fmt$(ni)}.${topLine}`;
    }
    if (kind === "balance-sheet") {
      const a = data.total_assets || 0;
      const l = data.total_liabilities || 0;
      const e = data.total_equity || 0;
      return `${name}${suffix}: assets ${_fmt$(a)}, liabilities ${_fmt$(l)}, equity ${_fmt$(e)}.`;
    }
    if (kind === "cash-flow") {
      const net = data.net_change || data.total || 0;
      return `${name}${suffix}: net change in cash ${_fmt$(net)}.`;
    }
    if (kind === "trial-balance") {
      const d = data.total_debit ?? data.total_debits ?? 0;
      const c = data.total_credit ?? data.total_credits ?? 0;
      const bal = Math.abs(d - c) < 0.01 ? "and in balance" : "out of balance";
      return `${name}${suffix}: debits ${_fmt$(d)}, credits ${_fmt$(c)}, ${bal}.`;
    }
    if (kind === "general-ledger") {
      const n = (data.rows || data.entries || []).length;
      return `${name}${suffix}: ${n} ledger entries.`;
    }
    // Fallback: best-effort scalar hunt.
    const t = data.total || data.net_income || data.total_revenue || null;
    return t !== null ? `${name}${suffix}: ${_fmt$(t)}.` : `${name}${suffix}: report loaded.`;
  } catch {
    return `${name}${suffix}: report loaded.`;
  }
}

// Comparative narration. Given a report kind and TWO period responses (now
// and prior), pick the top 2 line-item movers (by |delta|) and speak them.
// Falls back to top-level totals for non-P&L reports.
function summarizeComparison(kind, nowData, priorData, priorLabel = "prior period") {
  if (!nowData || !priorData) return "";
  const pct = (a, b) => {
    if (!isFinite(a) || !isFinite(b) || Math.abs(b) < 0.01) return null;
    return Math.round(((a - b) / Math.abs(b)) * 100);
  };
  const dir = (a, b) => (a > b ? "up" : a < b ? "down" : "flat");

  try {
    if (kind === "income-statement") {
      // Top-line: net income delta.
      const nowNi   = nowData.net_income   ?? ((nowData.total_revenue   || 0) - (nowData.total_expense   || 0));
      const priorNi = priorData.net_income ?? ((priorData.total_revenue || 0) - (priorData.total_expense || 0));
      const niPct = pct(nowNi, priorNi);
      const headline = `Net income is ${dir(nowNi, priorNi)}${niPct != null ? ` ${Math.abs(niPct)}%` : ""} vs ${priorLabel}.`;

      // Combine revenue + expenses into one flat list keyed by account_name.
      const flat = (rpt) => {
        const out = {};
        for (const g of ["revenue", "expenses"]) {
          for (const row of (rpt[g] || [])) {
            const k = (row.account_name || row.name || "").toLowerCase();
            if (!k) continue;
            out[k] = { name: row.account_name || row.name, amount: row.amount || 0, section: g };
          }
        }
        return out;
      };
      const N = flat(nowData), P = flat(priorData);
      const keys = new Set([...Object.keys(N), ...Object.keys(P)]);
      const movers = [];
      for (const k of keys) {
        const n = N[k]?.amount || 0;
        const p = P[k]?.amount || 0;
        const delta = n - p;
        if (Math.abs(delta) < 1) continue;
        movers.push({ name: N[k]?.name || P[k]?.name || k, n, p, delta, pct: pct(n, p) });
      }
      movers.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
      const top = movers.slice(0, 2).map(m =>
        `${m.name} ${dir(m.n, m.p)} ${m.pct != null ? `${Math.abs(m.pct)}%` : _fmt$(Math.abs(m.delta))}`
      );
      const moversLine = top.length ? ` Top movers: ${top.join("; ")}.` : "";
      return `${headline}${moversLine}`;
    }
    if (kind === "balance-sheet") {
      const nA = nowData.total_assets || 0, pA = priorData.total_assets || 0;
      const nL = nowData.total_liabilities || 0, pL = priorData.total_liabilities || 0;
      const nE = nowData.total_equity || 0, pE = priorData.total_equity || 0;
      const bits = [
        `assets ${dir(nA, pA)} ${_fmt$(Math.abs(nA - pA))}`,
        `liabilities ${dir(nL, pL)} ${_fmt$(Math.abs(nL - pL))}`,
        `equity ${dir(nE, pE)} ${_fmt$(Math.abs(nE - pE))}`,
      ];
      return `Vs ${priorLabel}: ${bits.join(", ")}.`;
    }
    if (kind === "cash-flow") {
      const n = nowData.net_change || 0, p = priorData.net_change || 0;
      const cp = pct(n, p);
      return `Net change in cash ${dir(n, p)}${cp != null ? ` ${Math.abs(cp)}%` : ""} vs ${priorLabel}.`;
    }
    return "";
  } catch {
    return "";
  }
}



const getSR = () => window.SpeechRecognition || window.webkitSpeechRecognition;

export default function AiPanel({ collapsed, onToggle }) {
  const chatGate = useFeature("chat");
  if (!chatGate.allowed) return <LockedChatRail collapsed={collapsed} onToggle={onToggle} onUpgrade={() => chatGate.openUpgrade()} />;
  return <AiPanelInner collapsed={collapsed} onToggle={onToggle} />;
}

function LockedChatRail({ collapsed, onToggle, onUpgrade }) {
  return (
    <div className={`h-full flex flex-col items-center justify-center gap-3 p-4 text-center bg-white border-l ${collapsed ? "w-12" : ""}`} data-testid="ai-panel-locked">
      <span className="w-10 h-10 rounded-xl bg-slate-900 text-white grid place-items-center"><Lock size={16} /></span>
      {!collapsed && (<>
        <div className="text-sm font-semibold text-slate-900">AI Review Chat</div>
        <div className="text-xs text-slate-500">Ask questions, review your books and run commands — included in AI Assistant.</div>
        <button onClick={onUpgrade} className="h-9 px-4 rounded-xl bg-slate-900 text-white text-xs font-bold" data-testid="ai-panel-unlock">Unlock AI Assistant</button>
      </>)}
      {onToggle && <button onClick={onToggle} className="text-[11px] text-slate-400 underline" data-testid="ai-panel-locked-toggle">{collapsed ? "Expand" : "Collapse"}</button>}
    </div>
  );
}

function AiPanelInner({ collapsed, onToggle }) {
  const { currentId, current, companies, switchCompany } = useCompany();
  const { user } = useAuth();
  // Private-label the header (icon initial + firm name + "Assistant") so
  // enterprise-branded clients see e.g. "CypherPro Assistant" instead of
  // the generic "SmartBooks Assistant". Falls back to SmartBooks
  // whenever the branding hasn't loaded yet or the firm name is blank.
  const { branding } = useBranding();
  const firmName = (branding?.firm_name || "").trim() || "SmartBooks";
  const assistantTitle = `${firmName} Assistant`;
  const firmInitial = firmName.charAt(0).toUpperCase();
  const navigate = useNavigate();
  // User-adjustable panel width. Persisted in localStorage. Constrained to a
  // sensible range so it can't be dragged narrower than the header controls
  // or wider than half the viewport.
  const MIN_W = 260;
  const MAX_W = Math.min(900, typeof window !== "undefined" ? Math.floor(window.innerWidth * 0.6) : 900);
  const [panelWidth, setPanelWidth] = useState(() => {
    const stored = parseInt(localStorage.getItem("axiom_ai_panel_width") || "", 10);
    return Number.isFinite(stored) && stored >= MIN_W ? Math.min(MAX_W, stored) : 384;
  });
  useEffect(() => {
    localStorage.setItem("axiom_ai_panel_width", String(panelWidth));
    // Expose the current width as a CSS variable so global stylesheets
    // (e.g. the Plaid-Link overlay carve-out) can leave room for us.
    document.documentElement.style.setProperty("--ai-panel-width", `${panelWidth}px`);
  }, [panelWidth]);
  // Publish an explicit "expanded" flag on the body so peers (e.g.
  // InsightsChatWidget) can distinguish "panel exists but tucked away"
  // from "panel is actually eating pixels on the right edge".
  useEffect(() => {
    document.body.setAttribute("data-ai-panel-open", collapsed ? "0" : "1");
    return () => document.body.removeAttribute("data-ai-panel-open");
  }, [collapsed]);
  const isMobile = useIsMobile();
  const [typing, setTyping] = useState(false);
  // Mobile sheet snap points: peek (one line + input) · half · full.
  // Deliberate opens (Chat tab) → full; row sparkles → half; else peek.
  const [sheet, setSheet] = useState("peek");
  const sheetUp = () => setSheet((v) => (v === "peek" ? "half" : "full"));
  const sheetDown = () => { if (sheet === "peek") onToggle?.(); else setSheet((v) => (v === "full" ? "half" : "peek")); };
  useActionListener("ai-open", (p) => {
    if (!isMobile) return;
    setSheet(p?.source === "nav" ? "full" : p?.source === "row" || p?.txn ? "half" : "peek");
  });
  useActionListener("ai-tell-me-about", () => { if (isMobile) setSheet((v) => (v === "peek" ? "half" : v)); });
  // Any modal/dialog needs the screen — the sheet sits below them (z-45 vs z-50+)
  // and drops to peek so their buttons stay reachable.
  useActionListener("ai-bulk-approve-prompt", () => { if (isMobile) setSheet("peek"); });
  useEffect(() => {
    if (!isMobile || collapsed) return;
    const isModal = (el) => el?.nodeType === 1 && (el.matches?.('[role="dialog"], [data-testid$="-modal"], [data-testid="modal-panel"]') || el.querySelector?.('[role="dialog"], [data-testid$="-modal"], [data-testid="modal-panel"]'));
    const mo = new MutationObserver((muts) => {
      for (const m of muts) for (const n of m.addedNodes) if (isModal(n) && !n.closest?.('[data-testid="ai-panel"]')) { setSheet("peek"); return; }
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [isMobile, collapsed]);
  const dragY = useRef(null);
  const onHandleTouchStart = (e) => { dragY.current = e.touches[0].clientY; };
  const onHandleTouchEnd = (e) => {
    if (dragY.current == null) return;
    const dy = e.changedTouches[0].clientY - dragY.current;
    dragY.current = null;
    if (dy < -40) sheetUp(); else if (dy > 40) sheetDown();
  };
  // Keyboard inset — keep the input above the soft keyboard (visualViewport).
  const [kbInset, setKbInset] = useState(0);
  useEffect(() => {
    if (!isMobile || !window.visualViewport) return;
    const vv = window.visualViewport;
    const on = () => setKbInset(Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)));
    vv.addEventListener("resize", on); vv.addEventListener("scroll", on); on();
    return () => { vv.removeEventListener("resize", on); vv.removeEventListener("scroll", on); };
  }, [isMobile]);
  useEffect(() => { if (typing && isMobile && sheet === "peek") setSheet("half"); }, [typing, isMobile, sheet]);
  // Drag-to-resize. Listeners are attached synchronously inside
  // startResize so we never race with React's render cycle — previous
  // implementation gated attachment on a ref inside a useEffect,
  // which never re-ran (refs don't trigger renders) and left the
  // col-resize cursor stuck when the user released the mouse.
  const startResize = (e) => {
    e.preventDefault();
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      // Drag handle is on the LEFT edge — width grows as pointer moves LEFT.
      const w = window.innerWidth - ev.clientX;
      const clamped = Math.max(MIN_W, Math.min(MAX_W, w));
      setPanelWidth(clamped);
    };
    const onUp = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const [messages, setMessages] = useState([]);
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  // Route awareness — the "Review | Chat" toggle at the top of the
  // panel only appears when we're on the review-books chat page. On
  // every OTHER route the panel behaves exactly as before.
  const location = useLocation();
  const isReviewChatRoute = location.pathname === "/accounting/review-chat";
  // Panel-body mode when we ARE on the review-chat route. "review"
  // shows the 5 helper cards + a free-form prompt input; "chat" is
  // the standard AI chat surface. Default to "review" so a first-
  // time visitor sees the affordances first. Reset back to "review"
  // whenever the user re-enters the review-chat route.
  const [reviewMode, setReviewMode] = useState("review");
  useEffect(() => {
    if (isReviewChatRoute) setReviewMode("review");
  }, [isReviewChatRoute]);
  // Auto-flip Review → Chat whenever a new message lands (either the
  // user typed a scenario in the free-form input, or a card injected
  // a canned Q&A). Keeps the conversation front-and-center once
  // there's actually a conversation.
  const flipToChatOnActivity = () => {
    if (isReviewChatRoute && reviewMode === "review") setReviewMode("chat");
  };
  const [listening, setListening] = useState(false);
  // Mic mode: "off" | "ptt" (push-to-talk, hold to speak) | "open" (open-mic
  // with silence auto-submit + TTS echo protection). Persisted so a user's
  // preferred conversation mode survives reloads.
  // Legacy value from earlier PTT design: coerce 'ptt' → 'open' on load so
  // returning users don't get stuck in a mode the UI no longer supports.
  const [micMode, setMicMode] = useState(() => {
    const v = localStorage.getItem("axiom_mic_mode") || "off";
    return v === "ptt" ? "open" : v;
  });
  useEffect(() => { localStorage.setItem("axiom_mic_mode", micMode); }, [micMode]);
  const micModeRef = useRef(micMode);
  useEffect(() => { micModeRef.current = micMode; }, [micMode]);
  const [interim, setInterim] = useState("");
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("axiom_tts") === "1");
  // Listen for `axiom-tts-changed` broadcasts (fired from
  // WelcomeModal's speaker toggle + the "Onboard with sound / chat
  // only" CTAs). AiPanel reads `axiom_tts` at mount only, so without
  // this sync it would keep narrating even after a user asks for a
  // silent onboarding.
  useEffect(() => {
    const onChange = (e) => {
      const desired = !!(e?.detail?.on ?? (localStorage.getItem("axiom_tts") === "1"));
      setVoiceOn(desired);
      if (!desired && "speechSynthesis" in window) window.speechSynthesis.cancel();
    };
    window.addEventListener("axiom-tts-changed", onChange);
    return () => window.removeEventListener("axiom-tts-changed", onChange);
  }, []);
  const [voiceName, setVoiceName] = useState(() => localStorage.getItem("axiom_tts_voice") || "Google UK English Female");
  const [voices, setVoices] = useState([]);
  const [voiceMenuOpen, setVoiceMenuOpen] = useState(false);
  const [voiceRate, setVoiceRate] = useState(() => {
    const v = parseFloat(localStorage.getItem("axiom_tts_rate") || "1.05");
    return isFinite(v) ? v : 1.05;
  });
  const [terseness, setTerseness] = useState(() =>
    localStorage.getItem("axiom_terseness") || "balanced"
  );
  useEffect(() => { localStorage.setItem("axiom_terseness", terseness); }, [terseness]);
  // Pending create-intent from the backend parser. When populated, a
  // "confirm" utterance submits it via API; "cancel" clears it.
  const [pendingIntent, setPendingIntent] = useState(null);
  const pendingIntentRef = useRef(null);
  // Track DRAFT markers we've already dispatched in the current stream
  // so we don't refire them each time the same chunk is reparsed.
  const dispatchedDraftsRef = useRef(new Set());
  useEffect(() => { pendingIntentRef.current = pendingIntent; }, [pendingIntent]);

  // Feb 2026 — Belt-and-suspenders: clear any stale `cleanup-inquiry`
  // pending intent whenever the pinned focus switches to a different
  // contact. Covers all paths (Sparkle button, keyboard nav in the
  // stepper, direct programmatic setFocus), not just the sparkle
  // action listener. Prevents "Plan for Romeo Ugali · 30 rows" from
  // appearing after focus has moved to R.c. Willey · 3 rows.


  // Weekly-review mode: paced multi-step briefing. When active, the panel
  // shows a progress card and listens for "next / skip / back / exit" cues
  // between steps instead of routing utterances to the chat stream.
  const [review, setReview] = useState(null); // { steps: [...], idx: number }
  const reviewRef = useRef(null);
  useEffect(() => { reviewRef.current = review; }, [review]);

  // Batch resolve mode: paced sprint through flagged transactions. Each row
  // is announced with the AI's best-guess category; user says "yes" to accept,
  // "no it's X" to reclassify, or "skip" to move on. All accepts/rejects are
  // sent to the existing bulk-approve / bulk-reclassify endpoints.
  const [batch, setBatch] = useState(null); // { txns, idx, accounts, decisions }
  const batchRef = useRef(null);
  useEffect(() => { batchRef.current = batch; }, [batch]);

  // Voice-Action clarification hand-off: the VoiceActionReview popup can
  // ask "You said 'Larry OR John' — which one?" and route it here so the
  // user answers via chat (text or voice) and the popup re-plans instead
  // of forcing an inline edit. Same event bus that fires the popup.
  const [voiceClarify, setVoiceClarify] = useState(null); // { questions: string[], originalText: string }
  const voiceClarifyRef = useRef(null);
  useEffect(() => { voiceClarifyRef.current = voiceClarify; }, [voiceClarify]);
  // After the popup resolves with NO pending clarifications, we invite
  // the user to say "looks good" (commit) or describe a tweak (re-plan
  // via the clarification bus). Same interception pattern as clarify.
  const [voiceReview, setVoiceReview] = useState(null); // { originalText: string }
  const voiceReviewRef = useRef(null);
  useEffect(() => { voiceReviewRef.current = voiceReview; }, [voiceReview]);
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  // TTS pointers: how much of the current assistant reply we've already
  // queued to the browser's speechSynthesis. Kept in a ref so streaming
  // delta callbacks don't cause React re-renders on every chunk.
  const spokenIdxRef = useRef(0);
  const voiceOnRef = useRef(voiceOn);
  const voiceNameRef = useRef(voiceName);
  const voiceRateRef = useRef(voiceRate);
  useEffect(() => {
    voiceOnRef.current = voiceOn;
    localStorage.setItem("axiom_tts", voiceOn ? "1" : "0");
    if (!voiceOn && "speechSynthesis" in window) window.speechSynthesis.cancel();
  }, [voiceOn]);
  useEffect(() => {
    voiceNameRef.current = voiceName;
    localStorage.setItem("axiom_tts_voice", voiceName || "");
  }, [voiceName]);
  useEffect(() => {
    voiceRateRef.current = voiceRate;
    localStorage.setItem("axiom_tts_rate", String(voiceRate));
  }, [voiceRate]);
  // Populate voices — Chrome loads them asynchronously so we subscribe to
  // the `voiceschanged` event as well as reading once on mount.
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const load = () => {
      const v = window.speechSynthesis.getVoices() || [];
      setVoices(v);
      // Auto-select the best default if the current pick isn't available:
      // 1) Google UK English Female  2) any en-GB female  3) any en-* voice
      //    4) whatever the OS gives us first.
      if (v.length && !v.find(x => x.name === voiceNameRef.current)) {
        const pick =
          v.find(x => /google uk english female/i.test(x.name))
          || v.find(x => /en-gb/i.test(x.lang) && /female/i.test(x.name))
          || v.find(x => /^en(-|$)/i.test(x.lang))
          || v[0];
        if (pick) setVoiceName(pick.name);
      }
    };
    load();
    window.speechSynthesis.addEventListener("voiceschanged", load);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", load);
  }, []);
  const { focus, setFocus, pinned: focusPinned } = useAiFocus();
  useEffect(() => {
    // Belt-and-suspenders (Feb 2026): clear any stale cleanup-inquiry
    // whenever the focused contact changes. See comment above.
    if (pendingIntentRef.current?.kind === "cleanup-inquiry"
        && focus?.contact_name
        && pendingIntentRef.current?.action?.contact_name !== focus.contact_name) {
      pendingIntentRef.current = null;
      setPendingIntent(null);
    }
  }, [focus?.contact_name, focus?.key]);


  // Cleanup Copilot integration: when the user clicks a chip / "Fix now" on
  // the hero band, the Transactions page emits `cleanup-inquiry` with the
  // action payload. We seed a conversational message so the AI asks about
  // the contact, and stash the action on `pendingIntentRef` so the user's
  // next reply is interpreted as the category description.
  //
  // De-dupe guard — the LetsReview / NoContactReview redirects can fire
  // the same inquiry twice in quick succession when React re-runs the
  // redirect useEffect (e.g. after a stepper advance or a `groups`
  // reference change). Track the last (kind + entity_id) we surfaced and
  // ignore any exact repeat within 2s.
  const lastInquiryRef = useRef({ key: "", at: 0 });
  useActionListener("cleanup-inquiry", async (payload) => {
    const a = payload?.action;
    if (!a) return;
    const entityKey = a.contact_id || a.group_key || a.label || "";
    const dedupeKey = `${a.kind}::${entityKey}::${a.count || 0}`;
    const now = Date.now();
    if (
      lastInquiryRef.current.key === dedupeKey &&
      now - lastInquiryRef.current.at < 2000
    ) {
      // Silent drop — same inquiry fired within the 2s dedupe window.
      return;
    }
    lastInquiryRef.current = { key: dedupeKey, at: now };
    let msg = "";
    if (a.kind === "contact_in_uncat") {
      msg = `I see **${a.count} ${a.contact_name}** transactions sitting in Uncategorized. Tell me about them.`;
    } else if (a.kind === "contact_split") {
      msg = `**${a.contact_name}** is split across ${a.count} different accounts — that's usually a categorization drift. Tell me what these transactions actually are, and I'll consolidate them and create a rule.`;
    } else if (a.kind === "contact_ai_ready") {
      const acode = a.account?.code || "";
      const aname = a.account?.name || "";
      msg = `**${a.count} ${a.contact_name}** transactions were AI-categorized as **${acode} ${aname}** and are waiting for your sign-off. Say **"approve"** to bulk-approve them all, or tell me the correct category if the AI got it wrong.`;
    } else if (a.kind === "flagged_batch") {
      // Fetch the first flagged transaction so we can dive straight in
      // instead of asking "Ready?" — friction the user explicitly called out.
      let first = null;
      if (currentId) {
        try {
          const r = await api.get(
            `/companies/${currentId}/transactions?needs_review=true&limit=1&sort=date_desc`
          );
          const items = r.data?.transactions || r.data?.items || [];
          first = items[0] || null;
        } catch { /* non-fatal */ }
      }
      if (first) {
        const label = first.merchant || first.description || first.contact_name || "this transaction";
        const amt = (typeof first.amount === "number")
          ? ` (${first.amount.toLocaleString("en-US", { style: "currency", currency: "USD" })})`
          : "";
        msg = `Let's run through the ${a.count} flagged transactions together. I'll surface one at a time — tell me what each one is in your own words. The first one is **${label}**${amt}.`;
      } else {
        msg = `Let's run through the ${a.count} flagged transactions together. I'll surface one at a time — tell me what each one is in your own words.`;
      }
    }
    if (!msg) return;

    // For contact-in-uncat, probe the amount distribution — if it's bimodal,
    // surface the natural split as a quick-action chip so the user doesn't
    // have to eyeball the amounts themselves.
    let splitHint = null;
    if (a.kind === "contact_in_uncat" && a.contact_id && currentId) {
      try {
        const r = await api.get(
          `/companies/${currentId}/transactions/split-suggestion?contact_id=${a.contact_id}`
        );
        const s = r.data?.suggestion;
        if (s) {
          splitHint = {
            threshold: s.threshold,
            below: s.below,
            above: s.above,
            contactName: a.contact_name,
            previous_below: s.previous_below || null,
            previous_above: s.previous_above || null,
          };
          if (s.previous_below && s.previous_above) {
            msg += `\n\n💡 Last time you split ${a.contact_name} at **$${s.threshold.toLocaleString()}** → **${s.previous_below}** / **${s.previous_above}**. Same again? (${s.below.count} below · ${s.above.count} above)`;
          } else {
            msg += `\n\n💡 These amounts naturally split at **$${s.threshold.toLocaleString()}** — ${s.below.count} rows under, ${s.above.count} rows above. Want to bucket them separately?`;
          }
        }
      } catch { /* non-fatal */ }
    }

    // The reply always answers the prompt the user can SEE. Set the
    // pending intent at the same instant the bubble is appended (after
    // any awaits above) so an inquiry for another contact that fired
    // mid-await can't silently re-point the user's next answer.
    const inquiry = { kind: "cleanup-inquiry", action: a };
    pendingIntentRef.current = inquiry;
    // A stale "Focused transaction" pin from another contact only adds
    // noise next to a vendor prompt — drop it.
    if (focus && focus.contact_name && focus.contact_name !== a.contact_name) setFocus(null);
    setMessages(m => [...m, { role: "assistant", content: msg, splitHint, showSkip: true, inquiry,
                              skipContact: { id: a.contact_id, name: a.contact_name, kind: a.kind } }]);
    // Barge in on any still-playing previous vendor's message when the
    // user manually clicks Skip / Next on the AI Cleanup Copilot — same
    // "manual advance stops the AI mid-sentence" behavior as onboarding.
    stopTtsNow();
    if (voiceOnRef.current) speakOne(msg.replace(/\*\*/g, ""));
    emitAction("ai-open");
  });

  // Fired from the per-row Sparkles button on the Transactions table. The
  // AI panel is already open (the button emits `ai-open` right after), so
  // we drop an "OK, tell me about this transaction" assistant message and
  // pop open the mic so the user can dictate what it is without hunting
  // for the mic button.
  const inquiryTxnRef = useRef(null);  // txn.id currently under Sparkle inquiry, null otherwise
  const resolveInquiry = () => {
    // End the current single-txn inquiry: drop the pinned focus, close the
    // mic, and forget the txn id. Called after the AI successfully
    // categorizes / marks-as-transfer / creates-account for the focused
    // row — or when the user declines. Safe to call multiple times: only
    // does work when an inquiry is actually active.
    if (!inquiryTxnRef.current) return;
    inquiryTxnRef.current = null;
    setFocus(null, { force: true });
    setMicMode("off");
  };
  useActionListener("ai-tell-me-about", async (payload) => {
    const t = payload?.txn;
    if (!t) return;
    inquiryTxnRef.current = t.id;
    // Pin the focus so the "Cancel focus" pill shows above the input —
    // this is an explicit user gesture (they clicked the sparkle), which
    // is distinct from the ephemeral hover-focus that would only bleed a
    // "Focused transaction" hint without deserving a cancel affordance.
    setFocus(
      {
        id: t.id,
        merchant: t.merchant || t.description || t.contact_name || "Transaction",
        amount: typeof t.amount === "number" ? t.amount : 0,
        date: t.date || "",
      },
      { pin: true },
    );
    const label = t.merchant || t.description || t.contact_name || "this transaction";
    const amt = (typeof t.amount === "number")
      ? ` (${t.amount.toLocaleString("en-US", { style: "currency", currency: "USD" })})`
      : "";
    const msg = `Tell me about this transaction — **${label}**${amt}. What was it for? I'll categorize it (and any similar ones from the same vendor) and offer to create a rule so future imports land in the right place.`;
    setMessages(m => [...m, { role: "assistant", content: msg }]);
    if (voiceOnRef.current) speakOne(msg.replace(/\*\*/g, ""));
    // Give the panel a beat to open + render, then flip on the mic. Doing
    // it via `micMode` mirrors the click on the mic pill in the header
    // (same open-mic + auto-submit-on-silence behavior).
    setTimeout(() => setMicMode("open"), 250);
  });

  // Silent chat-bubble injector — the mega-approve modal's "How To" tour
  // fires this per script step. Posts an assistant bubble without TTS
  // (the modal orchestrator drives its own TTS so it can await utterance
  // ends between steps).
  useActionListener("ai-chat-say", (payload) => {
    const msg = (payload?.message || "").trim();
    if (msg) setMessages(m => [...m, { role: "assistant", content: msg }]);
  });

  // "Show N similar" lifecycle — Transactions page reports the ticked ids
  // while the similar view is active, and tells us when it's been cleared.
  useActionListener("similar-selection-changed", (payload) => {
    const ids = payload?.ids || [];
    lastSimilarSelectionRef.current = ids;
    setMessages(m => m.map(mm => mm.card?.similarMode?.active
      ? { ...mm, card: { ...mm.card, similarMode: { active: true, ticked: ids } } } : mm));
  });
  useActionListener("similar-view-cleared", () => {
    setMessages(m => m.map(mm => mm.card?.similarMode?.active
      ? { ...mm, card: { ...mm.card, similarMode: null } } : mm));
  });

  // Row approve → same-vendor bulk prompt. Mirrors the SimilarApproveModal
  // on the Transactions page: ticks sync both ways, either side applies.
  useActionListener("ai-bulk-approve-prompt", (payload) => {
    const { similar, rule_exists, ids } = payload || {};
    if (!similar?.count) return;
    const catName = similar.category_account_name || similar.category_account_code || "the same category";
    const who = similar.match_kind === "description" ? `with a description like **“${similar.contact_name}”**` : `from **${similar.contact_name}**`;
    const prompt = `Approved. There ${similar.count === 1 ? "is" : "are"} **${similar.count}** other unapproved transaction${similar.count === 1 ? "" : "s"} ${who}. Categorize the ticked ones as **${catName}** and approve them? Say **yes**${rule_exists ? "" : ", or **yes, and make a rule** so future imports land there automatically"}.`;
    pendingIntentRef.current = { kind: "bulk-approve-contact", similar, create_rule: false, rule_exists: !!rule_exists, ticked: ids || null };
    setMessages(m => [...m.map(mm => mm.card?.kind === "bulk-approve-confirm" ? { ...mm, card: null } : mm),
      { role: "assistant", content: prompt, card: { kind: "bulk-approve-confirm", similar, create_rule: false, rule_exists: !!rule_exists, ticked: ids || null } }]);
    stopTtsNow();
    if (voiceOnRef.current) speakOne(prompt.replace(/\*\*/g, ""));
  });
  useActionListener("bulk-approve-selection-changed", (p) => {
    if (p?.origin === "chat" || !Array.isArray(p?.ids)) return;
    if (pendingIntentRef.current?.kind === "bulk-approve-contact") pendingIntentRef.current.ticked = p.ids;
    setMessages(m => m.map(mm => mm.card?.kind === "bulk-approve-confirm" ? { ...mm, card: { ...mm.card, ticked: p.ids } } : mm));
  });
  useActionListener("bulk-approve-done", (p) => {
    if (p?.origin === "chat") return;
    if (pendingIntentRef.current?.kind === "bulk-approve-contact") pendingIntentRef.current = null;
    const msg = p?.declined ? "OK — just the one approved." : (p?.msg || "Done.");
    setMessages(m => m.map(mm => mm.card?.kind === "bulk-approve-confirm" ? { ...mm, card: null } : mm).concat([{ role: "assistant", content: msg }]));
    if (voiceOnRef.current) speakOne(msg);
  });

  // Same as `ai-chat-say` but attaches a clickable CTA to the bubble
  // (e.g. the "Re-play tour" button posted by CleanupCopilot when its
  // walkthrough finishes). Payload: { message, cta: { label, actionKey } }.
  // Clicking the button emits `chat-cta:<actionKey>` so the originating
  // component can react without AiPanel knowing about specific features.
  useActionListener("ai-chat-say-with-cta", (payload) => {
    const msg = (payload?.message || "").trim();
    const cta = payload?.cta;
    if (!msg || !cta?.label || !cta?.actionKey) return;
    setMessages(m => [...m, { role: "assistant", content: msg, cta }]);
  });

  // Sibling action fired by the mega-approve modal's per-vendor sparkle
  // button. Primes the chat with the ENTIRE vendor bucket's context (count,
  // dollar volume, current category, whether it's been overridden vs. the
  // AI's original) so the CPA can ask "why is Walmart going to Supplies?"
  // or "make this a rule" and the LLM has everything it needs.
  useActionListener("ai-tell-me-about-bucket", (payload) => {
    const b = payload?.bucket;
    if (!b) return;
    // Silent focus — no primed assistant bubble, no TTS. Just pin the
    // bucket so the "Focused bucket" card renders at the top of chat and
    // the source row on the mega-approve modal picks up the rainbow ring.
    // The LLM sees this focus in its system context on the next user
    // question, so "why is this categorized as supplies?" or "make it a
    // rule" work naturally without any prompting overhead.
    setFocus(
      {
        bucket: true,
        key: b.key,
        contact_name: b.contact_name,
        count: b.count,
        amount: b.amount,
        account_code: b.account_code,
        account_name: b.account_name,
      },
      { pin: true },
    );
    // Feb 2026 — Clear any stale `cleanup-inquiry` intent from a
    // PREVIOUS bucket so the next user message is processed against
    // THIS bucket, not the last one. Previously a user could focus
    // R.c. Willey after having said something on Romeo Ugali and the
    // AI's next plan would still say "Plan for Romeo Ugali · 30 rows"
    // because pendingIntentRef held the old inquiry.
    if (pendingIntentRef.current?.kind === "cleanup-inquiry"
        && pendingIntentRef.current?.action?.contact_name !== b.contact_name) {
      pendingIntentRef.current = null;
      setPendingIntent(null);
    }
    // Auto-open the mic so the CPA can just start talking — same UX as
    // the transaction-row sparkle click.
    setTimeout(() => setMicMode("open"), 250);
  });

  // Live-accountant onboarding coach — the Onboarding page fires this event
  // to inject scripted assistant messages that greet the user at each step
  // ("Great, tell me about your business…"). The rainbow-outline treatment
  // on the last unanswered assistant message is already handled by the
  // chat renderer, so these messages naturally wear the shimmer border.
  useActionListener("onboarding-coach-greet", (payload) => {
    const msg = (payload?.message || "").trim();
    if (!msg) return;
    stopTtsNow();
    setMessages(m => [...m, { role: "assistant", content: msg }]);
    if (voiceOnRef.current) speakOne(msg.replace(/\*\*/g, ""));
  });

  // Reusable kill-switch — cancels the browser speech queue AND clears
  // our internal TTS state flags so the next utterance can start
  // immediately without the barge-in / tail-grace suppression firing.
  // Used both externally (ai-stop-tts action) and internally right
  // before any coach-style bubble is spoken so we barge in on any
  // still-playing previous message from a step the user has moved past.
  const stopTtsNow = () => {
    try {
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } catch { /* noop */ }
    ttsSpeakingRef.current = false;
    setTtsActive(false);
    ttsTailUntilRef.current = 0;
    spokenIdxRef.current = 0;
  };

  // External kill-switch for TTS — used by the onboarding coach when the
  // user clicks Next/Back faster than the AI can speak. Cancels the
  // browser's speech queue AND clears our internal "TTS is speaking"
  // flags so the next step's greeting can start immediately without the
  // barge-in / tail-grace suppression firing.
  useActionListener("ai-stop-tts", () => {
    stopTtsNow();
  });

  // ------------------------- Open-mic + TTS-echo protection -------------------------
  // Rules (see architecture doc in PR):
  //   1. Recognizer is continuous + self-heals on `onend` while a "listening" flag holds.
  //   2. In "open" mode a silence timer auto-submits after SILENCE_MS of no speech
  //      events, but only when the AI isn't talking (ttsSpeaking gate).
  //   3. Transcripts arriving while TTS is speaking are dropped entirely.
  //   4. A short TAIL_MS grace after TTS ends continues to drop transcripts so
  //      hardware audio tail can't leak into the user's next turn.
  //   5. Barge-in: recognizer's own `onspeechstart` past the tail grace during
  //      TTS is treated as the user cutting in — cancel TTS, drop the flag,
  //      and let subsequent transcripts flow through normally.
  const SILENCE_MS = 1800;
  const TAIL_MS = 300;
  const ERROR_WINDOW_MS = 5000;
  const ERROR_MAX = 3;
  const ttsSpeakingRef = useRef(false);
  // Mirror TTS activity into state so the "Stop AI" pill can render
  // reactively. Ref alone doesn't trigger re-renders, so the old
  // "AI speaking — mic muted" indicator only appeared when some other
  // state change forced a paint.
  const [ttsActive, setTtsActive] = useState(false);
  const ttsTailUntilRef = useRef(0);
  const silenceTimerRef = useRef(null);
  const lastFinalRef = useRef({ text: "", at: 0 });
  const errorLogRef = useRef([]);      // timestamps of recent recognizer errors
  const inputRef = useRef("");
  const submitInFlightRef = useRef(false);

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const armSilenceTimer = () => {
    // In "open" mode only — refuses to arm while TTS is talking.
    if (micModeRef.current !== "open") return;
    if (ttsSpeakingRef.current) return;
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      // Re-check TTS state at fire-time — user may have started listening
      // while AI was still speaking.
      if (ttsSpeakingRef.current) return;
      if (Date.now() < ttsTailUntilRef.current) return;
      // Read input via ref (not via setInput callback) so React StrictMode's
      // double-invocation of setter callbacks can't fire send() twice.
      const current = (inputRef.current || "").trim();
      if (!current) return;
      // Idempotency guard: if a submit is already in flight, don't dispatch
      // another one from a re-arm race.
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;
      // Defer so React can commit any pending state before send() reads it.
      setTimeout(() => {
        try { sendRef.current && sendRef.current(); }
        finally { submitInFlightRef.current = false; }
      }, 0);
    }, SILENCE_MS);
  };

  // Kept as a ref so the timer callback can call the latest `send`.
  const sendRef = useRef(null);

  // Clear chat: wipes the on-screen conversation AND asks the backend to
  // drop the persisted transcript for this session so a page refresh
  // doesn't restore old messages.
  const clearChatMessages = async () => {
    setMessages([{
      role: "assistant",
      content: `Fresh session. Ask me anything about ${current?.name || "the books"}.`,
    }]);
    setPendingIntent(null);
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    if (currentId) {
      try { await api.delete(`/ai/chat/history?company_id=${currentId}`); } catch { /* non-fatal */ }
    }
  };

  // Dispatch a parsed create/open intent → open the right modal, show a
  // confirmation card in chat.
  const handleParsedIntent = (userMsg, parsed) => {
    const { intent, prefill = {}, say, confidence = 0 } = parsed || {};
    if (!intent || intent === "none" || confidence < 0.4) return false;

    // ---------------------------------------------------------------
    // STT collision: "credit invoice" ≈ "create an invoice". Show a
    // clarification card in the chat with two clickable buttons — one
    // routes to the standard create_invoice flow (probably what the
    // user actually said), the other explains credit memos aren't
    // supported yet (Option 1 — polite no-op).
    // ---------------------------------------------------------------
    if (intent === "disambiguate_credit_or_create") {
      const clarifyMsg = {
        role: "assistant",
        content: (
          `I heard "credit invoice" — did you mean:\n\n` +
          `• **Create an invoice** — I probably misheard "create"\n` +
          `• **Add a credit invoice** — a customer refund/credit memo`
        ),
        disambigCreditOrCreate: {
          originalUtterance: parsed.original_utterance || userMsg,
          prefill,
        },
      };
      setMessages(m => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && last.content === "Parsing…") {
          copy[copy.length - 1] = clarifyMsg;
        } else {
          copy.push(clarifyMsg);
        }
        return copy;
      });
      if (voiceOnRef.current) speakOne("Did you mean create an invoice, or add a credit invoice?");
      return true;
    }

    const routeFor = (i) => {
      if (i === "create_invoice" || i === "open_invoice") return "/invoices";
      if (i === "create_bill" || i === "open_bill") return "/bills";
      if (i === "create_contact" || i === "open_contact") return "/contacts";
      if (i === "create_account") return "/accounting/chart-of-accounts";
      if (i === "create_payment") return "/payments";
      if (i === "create_receipt") return "/receipts";
      return null;
    };
    const kindFor = (i) => {
      if (i.startsWith("create_")) return i.slice("create_".length);
      if (i.startsWith("open_"))   return `open-${i.slice("open_".length)}`;
      return null;
    };
    const url = routeFor(intent);
    const kind = kindFor(intent);
    if (!url || !kind) return false;

    // Navigate first so the page mounts and its listener is ready; the
    // createBus queue backstops any race where the event fires early.
    navigate(url);
    setTimeout(() => emitCreate(kind, prefill), 30);

    // Human-friendly card in chat.
    const readable = intent.startsWith("create_")
      ? `Draft ready — review the ${kind} modal, then say "confirm" to save or "cancel" to abort.`
      : say || "Opened.";

    setMessages(m => {
      const copy = [...m];
      // Replace the "Parsing…" placeholder if present, else append.
      const last = copy[copy.length - 1];
      const card = { role: "assistant", content: `${say || readable}\n\n${intent.startsWith("create_") ? readable : ""}`.trim() };
      if (last && last.role === "assistant" && last.content === "Parsing…") {
        copy[copy.length - 1] = card;
      } else {
        copy.push(card);
      }
      return copy;
    });

    // Only creates are pending — opens are already handled by the page nav.
    if (intent.startsWith("create_")) {
      setPendingIntent({ intent, prefill, url });
    } else {
      setPendingIntent(null);
    }
    if (voiceOnRef.current && say) speakOne(say);
    return true;
  };

  // Submit a pending create intent programmatically via API. Returns true
  // on success. On failure we leave the modal open for the user to fix.
  const submitPendingIntent = async (pending) => {
    if (!pending || !currentId) return false;
    const { intent, prefill } = pending;
    try {
      if (intent === "create_invoice") {
        const amt = Number(prefill.amount || 0);
        // Same due-date logic as InvoiceModal — `due_days:0` (spoken
        // "due today") must survive, and a past-due date backdates the
        // invoice date to match so the record isn't born overdue.
        const _today = new Date().toISOString().slice(0, 10);
        const _dueDays = prefill.due_days;
        const _off = (_dueDays === undefined || _dueDays === null || _dueDays === "")
          ? 30 : Number(_dueDays);
        const _due = prefill.due_date
          || new Date(Date.now() + _off * 86400000).toISOString().slice(0, 10);
        const _issue = prefill.issue_date || (_due < _today ? _due : _today);
        // Voice path: if the backend hydrated `prefill.lines[]` from the
        // item catalog, POST those directly. Fallback to the legacy
        // single-line-with-amount shape when the utterance was a lump
        // sum without item detail.
        const line_items = (Array.isArray(prefill.lines) && prefill.lines.length)
          ? prefill.lines.map(l => {
              const quantity = Number(l.quantity || 1) || 1;
              const rate = Number(l.rate || 0);
              return {
                item_id: l.item_id,
                item_name: l.item_name,
                description: l.description || l.item_name || "",
                quantity,
                rate,
                amount: quantity * rate,
                income_account_id: l.income_account_id,
                income_account_name: l.income_account_name,
              };
            })
          : [{ description: prefill.description || "Services", quantity: 1, rate: amt, amount: amt }];
        const body = {
          contact_id: prefill.contact_id || null,
          contact_name: prefill.contact_name || "",
          issue_date: _issue,
          due_date: _due,
          line_items,
          tax: Number(prefill.tax || 0),
          status: prefill.status || "sent",
        };
        await api.post(`/companies/${currentId}/invoices`, body);
      } else if (intent === "create_bill") {
        const amt = Number(prefill.amount || 0);
        const _today = new Date().toISOString().slice(0, 10);
        const _dueDays = prefill.due_days;
        const _off = (_dueDays === undefined || _dueDays === null || _dueDays === "")
          ? 30 : Number(_dueDays);
        const _due = prefill.due_date
          || new Date(Date.now() + _off * 86400000).toISOString().slice(0, 10);
        const _issue = prefill.issue_date || (_due < _today ? _due : _today);
        const line_items = (Array.isArray(prefill.lines) && prefill.lines.length)
          ? prefill.lines.map(l => {
              const quantity = Number(l.quantity || 1) || 1;
              const rate = Number(l.rate || 0);
              return {
                item_id: l.item_id,
                item_name: l.item_name,
                description: l.description || l.item_name || "",
                quantity,
                rate,
                amount: quantity * rate,
                income_account_id: l.income_account_id,
                income_account_name: l.income_account_name,
              };
            })
          : [{ description: prefill.description || "Services", quantity: 1, rate: amt, amount: amt }];
        const body = {
          contact_id: prefill.contact_id || null,
          contact_name: prefill.contact_name || "",
          issue_date: _issue,
          due_date: _due,
          line_items,
          status: prefill.status || "open",
        };
        await api.post(`/companies/${currentId}/bills`, body);
      } else if (intent === "create_contact") {
        await api.post(`/companies/${currentId}/contacts`, {
          name: prefill.name || "",
          type: prefill.type || "customer",
          email: prefill.email || "",
          phone: prefill.phone || "",
          address: prefill.address || "",
        });
      } else if (intent === "create_account") {
        await api.post(`/companies/${currentId}/accounts`, {
          code: prefill.code || "9990",
          name: prefill.name || "New Account",
          type: prefill.type || "expense",
          subtype: prefill.subtype || "operating_expense",
        });
      } else {
        return false;
      }
      // Ask the currently open modal to close after a successful save.
      emitAction("close-current-modal");
      toast.success("Created via voice");
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
      return false;
    }
  };

  // Start the paced weekly-review briefing. Fetches all 4 steps at once so
  // subsequent "next" utterances are instantaneous (no network hop).
  const startReview = async () => {
    if (!currentId) return;
    setMessages(m => [
      ...m,
      { role: "assistant", content: "One sec — pulling your briefing…" },
    ]);
    try {
      const r = await api.get(`/companies/${currentId}/ai/review`);
      const steps = r.data?.steps || [];
      if (!steps.length) throw new Error("empty");
      setReview({ steps, idx: 0 });
      // Speak step 1 immediately + drop a card into the chat.
      const first = steps[0];
      const intro = `Morning stand-up. ${first.spoken} Say "next" for step 2.`;
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: intro };
        return copy;
      });
      if (voiceOnRef.current) speakOne(intro);
    } catch {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Couldn't pull the briefing right now." };
        return copy;
      });
    }
  };

  // Advance / step-nav review mode.
  const advanceReview = (direction /* 'next' | 'skip' | 'back' */) => {
    const r = reviewRef.current;
    if (!r) return false;
    let idx = r.idx;
    if (direction === "back") idx = Math.max(0, idx - 1);
    else                       idx = idx + 1;

    if (idx >= r.steps.length) {
      const outro = "That's the briefing. Anything you'd like to dig into?";
      setReview(null);
      setMessages(m => [...m, { role: "assistant", content: outro }]);
      if (voiceOnRef.current) speakOne(outro);
      return true;
    }
    const step = r.steps[idx];
    setReview({ ...r, idx });
    const total = r.steps.length;
    const line = `Step ${idx + 1} of ${total} — ${step.title}. ${step.spoken}` +
      (idx + 1 < total ? ' Say "next" to continue.' : ' Last one — say "next" to finish.');
    setMessages(m => [...m, { role: "assistant", content: line }]);
    if (voiceOnRef.current) speakOne(line);
    return true;
  };

  const exitReview = () => {
    setReview(null);
    setMessages(m => [...m, { role: "assistant", content: "Ended the briefing." }]);
    if (voiceOnRef.current) speakOne("Ended.");
  };

  // ---------------------------- Batch resolve mode ----------------------------
  //
  // Voice-driven sprint through flagged transactions. Uses the existing
  // bulk-approve / bulk-reclassify endpoints so nothing new was needed on
  // the backend. The AI just paces + resolves one row at a time.

  const _fmtMoney = (n) => {
    const v = Number(n || 0);
    return `$${Math.abs(v).toFixed(2)}${v < 0 ? " out" : " in"}`;
  };
  const _announceBatchRow = (txn) => {
    if (!txn) return "";
    const merch = txn.merchant || txn.contact_name || "Unknown";
    const suggested = txn.category_account_name || "Uncategorized";
    return `${merch} for ${_fmtMoney(txn.amount)} — I'll book this to ${suggested}. Yes, skip, or tell me the right category.`;
  };

  const startBatch = async () => {
    if (!currentId) return;
    setMessages(m => [
      ...m,
      { role: "assistant", content: "Pulling flagged transactions…" },
    ]);
    try {
      const [txnsR, acctsR] = await Promise.all([
        api.get(`/companies/${currentId}/transactions?needs_review=true&limit=50`),
        api.get(`/companies/${currentId}/accounts`),
      ]);
      const txns = (txnsR.data.transactions || []).filter(t => t.needs_review);
      const accounts = acctsR.data.accounts || [];
      if (!txns.length) {
        const done = "No flagged transactions — you're clean.";
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: done };
          return copy;
        });
        if (voiceOnRef.current) speakOne(done);
        return;
      }
      setBatch({ txns, idx: 0, accounts, accepted: 0, reclassified: 0, skipped: 0 });
      const intro = `Batch resolve — ${txns.length} flagged. First up: ${_announceBatchRow(txns[0])}`;
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: intro };
        return copy;
      });
      if (voiceOnRef.current) speakOne(intro);
    } catch (e) {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Couldn't pull the flagged queue." };
        return copy;
      });
    }
  };

  // Fuzzy-match a spoken category ("meals", "software subscriptions") to a
  // Chart of Accounts row. Returns the account or null.
  const _matchAccount = (needle, accounts) => {
    if (!needle) return null;
    const q = needle.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    if (q.length < 2) return null;
    const words = q.split(" ").filter(w => w.length >= 2);
    let best = null, bestScore = 0;
    for (const a of accounts) {
      // Prefer expense/COGS/revenue in batch mode (we're categorizing txns).
      if (!["expense", "cogs", "revenue"].includes(a.type)) continue;
      const n = (a.name || "").toLowerCase();
      let score = 0;
      if (n === q) score = 1000;
      else if (n.includes(q) || q.includes(n)) score = 500 + Math.min(q.length, 20);
      else score = words.reduce((s, w) => s + (n.includes(w) ? 10 : 0), 0);
      if (score > bestScore) { bestScore = score; best = a; }
    }
    return bestScore >= 10 ? best : null;
  };

  const _advanceBatch = (b, updatedCounts) => {
    const nextIdx = b.idx + 1;
    const next = { ...b, ...updatedCounts, idx: nextIdx };
    if (nextIdx >= b.txns.length) {
      const summary = `Done. Accepted ${next.accepted}, reclassified ${next.reclassified}, skipped ${next.skipped}.`;
      setBatch(null);
      setMessages(m => [...m, { role: "assistant", content: summary }]);
      if (voiceOnRef.current) speakOne(summary);
      return;
    }
    setBatch(next);
    const line = `${nextIdx + 1} of ${b.txns.length}. ${_announceBatchRow(b.txns[nextIdx])}`;
    setMessages(m => [...m, { role: "assistant", content: line }]);
    if (voiceOnRef.current) speakOne(line);
  };

  const handleBatchAction = async (action) => {
    const b = batchRef.current;
    if (!b) return;
    const cur = b.txns[b.idx];
    if (!cur) return;

    if (action.action === "exit") {
      const summary = `Ended. Accepted ${b.accepted}, reclassified ${b.reclassified}, skipped ${b.skipped}.`;
      setBatch(null);
      setMessages(m => [...m, { role: "assistant", content: summary }]);
      if (voiceOnRef.current) speakOne(summary);
      return;
    }

    if (action.action === "accept") {
      try {
        // The list endpoint accepts a raw list body — signature: bulk_approve(cid, ids: List[str]).
        await api.post(`/companies/${currentId}/transactions/bulk-approve`, [cur.id]);
        _advanceBatch(b, { accepted: b.accepted + 1 });
      } catch (e) {
        toast.error("Approve failed"); _advanceBatch(b, { skipped: b.skipped + 1 });
      }
      return;
    }
    if (action.action === "skip") {
      _advanceBatch(b, { skipped: b.skipped + 1 });
      return;
    }
    if (action.action === "reclassify") {
      const acct = _matchAccount(action.target, b.accounts);
      if (!acct) {
        const line = `I couldn't find "${action.target}". Try again, say "skip", or "exit".`;
        setMessages(m => [...m, { role: "assistant", content: line }]);
        if (voiceOnRef.current) speakOne(line);
        return;
      }
      try {
        await api.post(`/companies/${currentId}/transactions/bulk-reclassify`, {
          transaction_ids: [cur.id],
          category_account_id: acct.id,
        });
        setMessages(m => [...m, { role: "assistant", content: `Reclassified to ${acct.name}.` }]);
        if (voiceOnRef.current) speakOne(`Reclassified to ${acct.name}.`);
        _advanceBatch(b, { reclassified: b.reclassified + 1 });
      } catch (e) {
        toast.error("Reclassify failed");
        _advanceBatch(b, { skipped: b.skipped + 1 });
      }
    }
  };

  // Manual buttons for batch mode (accessibility + click fallback).
  const batchAcceptBtn  = () => handleBatchAction({ action: "accept" });
  const batchSkipBtn    = () => handleBatchAction({ action: "skip" });
  const batchExitBtn    = () => handleBatchAction({ action: "exit" });




  const startRecognizer = () => {
    const SR = getSR();
    if (!SR) return null;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.onresult = (event) => {
      // LAYER 1: while TTS is playing, drop transcripts entirely.
      if (ttsSpeakingRef.current) return;
      // LAYER 2: within the TAIL_MS grace after TTS ends, still drop.
      if (Date.now() < ttsTailUntilRef.current) return;

      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += t;
        else interimText += t;
      }
      if (finalText) {
        // Chrome sometimes fires the same final result twice around onend
        // restarts — dedupe identical strings within 500ms.
        const cleaned = finalText.trim();
        const last = lastFinalRef.current;
        if (cleaned && !(cleaned === last.text && Date.now() - last.at < 500)) {
          // Broadcast every final transcript on the global speech bus.
          // The Voice-Action overlay listens for confirm/cancel words
          // and consumes them when it's open.
          try {
            window.dispatchEvent(new CustomEvent("axiom:speech", {
              detail: { text: cleaned },
            }));
          } catch { /* CustomEvent unsupported — ignore */ }

          // Round 7.5 (Feb 2026): the popup no longer consumes voice
          // commands directly. AiPanel.send() intercepts "looks good" /
          // "cancel" / refinements via the voiceReview + voiceClarify
          // state, so we always spill the transcript into the input and
          // let the silence timer auto-submit it.
          setInput(prev => (prev + " " + cleaned).replace(/\s+/g, " ").trim());
          lastFinalRef.current = { text: cleaned, at: Date.now() };
        }
      }
      setInterim(interimText);
      // Any speech event re-arms the silence timer.
      armSilenceTimer();
    };

    rec.onspeechstart = () => {
      // LAYER 3 (barge-in): if the user starts speaking while TTS is playing
      // (past the tail grace so we don't self-trigger from AI's own audio),
      // cancel TTS and open the gate so the incoming transcript is kept.
      if (ttsSpeakingRef.current && Date.now() >= ttsTailUntilRef.current) {
        if ("speechSynthesis" in window) window.speechSynthesis.cancel();
        ttsSpeakingRef.current = false;
      }
      armSilenceTimer();
    };

    rec.onerror = (e) => {
      const now = Date.now();
      errorLogRef.current = errorLogRef.current
        .filter(t => now - t < ERROR_WINDOW_MS)
        .concat(now);
      if (errorLogRef.current.length >= ERROR_MAX) {
        toast.error("Mic keeps failing — turning off. Click the mic again when ready.");
        setMicMode("off");
        setListening(false);
      }
    };

    rec.onend = () => {
      // Self-heal: if we're still supposed to be listening (open-mic or a
      // long ptt press), restart. Otherwise clear listening state.
      if (micModeRef.current === "open") {
        try { rec.start(); return; } catch { /* fall through */ }
      }
      setListening(false);
    };

    try {
      rec.start();
      return rec;
    } catch (e) {
      toast.error("Could not start microphone");
      return null;
    }
  };

  // Set up SpeechRecognition once — but the *instance* is (re)created inside
  // startRecognizer so we always get a fresh state machine.
  useEffect(() => {
    return () => {
      const rec = recognitionRef.current;
      if (rec) { try { rec.stop(); } catch {} }
      clearSilenceTimer();
    };
  }, []);

  const startListening = () => {
    if (!getSR()) {
      toast.error("Voice input isn't supported in this browser. Try Chrome/Edge.");
      return;
    }
    setInterim("");
    const rec = startRecognizer();
    if (rec) {
      recognitionRef.current = rec;
      setListening(true);
    }
  };
  const stopListening = () => {
    const rec = recognitionRef.current;
    if (rec) { try { rec.stop(); } catch {} }
    clearSilenceTimer();
    setListening(false);
    setInterim("");
  };

  // Whenever micMode flips, open the mic (open) or close it (off).
  useEffect(() => {
    if (micMode === "open" && !listening) startListening();
    if (micMode === "off"  &&  listening) stopListening();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micMode]);

  useEffect(() => {
    if (!currentId) return;
    let cancelled = false;   // a slower reply for the previous company must not overwrite this one
    api.get(`/ai/chat/history?company_id=${currentId}`).then(r => {
      if (cancelled) return;
      const msgs = r.data.messages || [];
      if (msgs.length === 0) {
        // Skip the generic "Hi — I'm watching X" welcome whenever a
        // step-specific coach is going to greet the user instead: the
        // /onboarding page itself, plus the "let's finish onboarding" nudge
        // that fires on /dashboard for a company that hasn't completed
        // onboarding. Both scenarios produce a warmer, contextual first
        // bubble that makes the generic welcome feel redundant.
        const path = typeof window !== "undefined" ? window.location.pathname : "";
        const onboardingContext =
          path.startsWith("/onboarding") ||
          (current && current.onboarding_complete === false);
        if (onboardingContext) return;
        const firstName = (user?.name || "").split(" ")[0];
        const greeting = firstName
          ? `Hi ${firstName} — I'm watching ${current?.name || "your books"}. I categorize transactions, post JEs, and answer any accounting question. Ask me anything.`
          : `Hi — I'm watching ${current?.name || "your books"}. I categorize transactions, post JEs, and answer any accounting question. Ask me anything.`;
        setMessages([{ role: "assistant", content: greeting }]);
      } else {
        setMessages(msgs.map(m => ({ role: m.role, content: m.content })));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [currentId, current?.name, current?.onboarding_complete, user?.name]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  // ── Voice-Action clarification bus ─────────────────────────────
  // Popup asks a question → push it as an assistant chat card and set
  // the pending-clarification state so the very next user input is
  // routed back into the popup (via VOICE_CLARIFY_ANSWER_EVENT).
  useEffect(() => {
    const onAsk = (e) => {
      const questions = (e?.detail?.questions || []).filter(Boolean);
      const originalText = e?.detail?.originalText || "";
      if (!questions.length) return;
      setVoiceClarify({ questions, originalText });
      const body = questions.length === 1
        ? questions[0]
        : questions.map((q, i) => `${i + 1}. ${q}`).join("\n");
      setMessages(m => [
        ...m,
        {
          role: "assistant",
          content: body,
          voiceClarify: true,
        },
      ]);
      if (voiceOnRef.current) {
        // Speak just the first question so open-mic users can answer immediately.
        try { speakOne(questions[0]); } catch { /* ignore */ }
      }
    };
    const onClear = () => { setVoiceClarify(null); setVoiceReview(null); };
    const onReviewReady = (e) => {
      const summary = e?.detail?.summary || {};
      const originalText = e?.detail?.originalText || "";
      const parts = [];
      if (summary.notes)  parts.push(`${summary.notes} note`);
      if (summary.appts)  parts.push(`${summary.appts} appointment${summary.appts !== 1 ? "s" : ""}`);
      if (summary.tasks)  parts.push(`${summary.tasks} task${summary.tasks !== 1 ? "s" : ""}`);
      if (summary.emails) parts.push(`${summary.emails} email${summary.emails !== 1 ? "s" : ""}`);
      const list = parts.length ? parts.join(", ") : "no actions";
      const prompt =
        `Got **${list}** ready in the popup. Say **"looks good"** to save everything, ` +
        `or tell me what to change (e.g. "drop the second email", "make the meeting at 3", "skip the task").`;
      setVoiceReview({ originalText });
      setMessages(m => [...m, { role: "assistant", content: prompt, voiceReview: true }]);
      if (voiceOnRef.current) {
        try { speakOne(`Got ${list}. Say looks good to save, or tell me what to change.`); }
        catch { /* ignore */ }
      }
    };
    window.addEventListener("axiom:voice-clarify-ask", onAsk);
    window.addEventListener("axiom:voice-clarify-clear", onClear);
    window.addEventListener("axiom:voice-review-ready", onReviewReady);
    return () => {
      window.removeEventListener("axiom:voice-clarify-ask", onAsk);
      window.removeEventListener("axiom:voice-clarify-clear", onClear);
      window.removeEventListener("axiom:voice-review-ready", onReviewReady);
    };
  }, []);

  // Expose the latest send() to the silence timer via ref.
  useEffect(() => { sendRef.current = send; });
  // Mirror `input` state into a ref so refs-only code paths (silence timer,
  // barge-in handler) can read it without React StrictMode double-invoke.
  useEffect(() => { inputRef.current = input; }, [input]);

  // ---- Category recommendation card: shared handlers (buttons + voice/text) ----
  const dismissCard = (i) => setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, card: null } : mm2));
  const showSimilarForCard = (i, card) => {
    const ids = [...new Set([card.txnId, ...(card.similarIds || [])].filter(Boolean))];
    const payload = { ids, label: card.similarLabel || "", count: card.similar || 0, returnTo: location.pathname };
    setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, card: { ...mm2.card, similarMode: { active: true, ticked: ids } } } : mm2));
    sessionStorage.setItem("axiom_similar_view", JSON.stringify(payload));
    if (location.pathname === "/accounting/transactions") emitAction("show-similar-txns", payload);
    else navigate("/accounting/transactions");
  };
  const applyCategoryCard = async (i, card, { withSimilar, accountId, newAccount, txnIds, setContactName }) => {
    try {
      let ids = txnIds || (card.txnId ? [card.txnId] : []);
      if (withSimilar && card.txn && !txnIds) {
        const q = card.txn.contact_id ? `contact_id=${card.txn.contact_id}` : `q=${encodeURIComponent(card.txn.merchant || "")}`;
        const r = await api.get(`/companies/${currentId}/transactions?${q}&limit=2000`);
        ids = [...new Set([...ids, ...(r.data.transactions || []).filter(t => !t.human_reviewed).map(t => t.id)])];
      }
      const res = await api.post(`/companies/${currentId}/ai/resolve-category/apply`, {
        txn_ids: ids, account_id: accountId || null, new_account: newAccount || null,
        create_rule: !!withSimilar && !!card.txn?.contact_id, contact_id: card.txn?.contact_id || null,
        set_contact_name: setContactName || null,
      });
      const acct = res.data.account;
      const say = `${res.data.created_account ? "Created" : "Used"} **${acct.code} ${acct.name}** and categorized **${res.data.updated}** transaction${res.data.updated === 1 ? "" : "s"}${res.data.contact ? ` · contact set to **${res.data.contact.name}**` : ""}${res.data.rule_id ? " · rule saved for future imports" : ""}.`;
      setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, card: null } : mm2).concat([{ role: "assistant", content: say }]));
      if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
      if (card.similarMode?.active) emitAction("similar-clear");
      emitAction("txns:changed");
      if (card.txnId && inquiryTxnRef.current === card.txnId) resolveInquiry();
      if (sweepRef.current) sweepAdvance("done", res.data.updated || 0);
    } catch {
      setMessages(mm => [...mm, { role: "assistant", content: "Sorry — I couldn't apply that category." }]);
    }
  };
  const runBulkApprove = async (i, card, withRule) => {
    const sim = card.similar;
    try {
      let ids = card.ticked;
      if (!ids) {
        if (sim.contact_id) {
          const r = await api.get(`/companies/${currentId}/transactions?contact_id=${sim.contact_id}&limit=1000`);
          ids = (r.data.transactions || []).filter(t => !t.human_reviewed).map(t => t.id);
        } else {
          ids = (sim.items || []).map(x => x.id);
        }
      }
      const res = await api.post(`/companies/${currentId}/transactions/apply-bulk-approve-rule`, {
        txn_ids: ids, category_account_id: sim.category_account_id, contact_id: sim.contact_id,
        contact_name: sim.contact_name, create_rule: !!withRule,
        match_text: sim.match_kind === "description" ? sim.match_value : null,
      });
      const n = res.data?.updated || 0;
      const msg = res.data?.rule_id ? `Approved ${n} transaction${n === 1 ? "" : "s"} and created a rule for ${sim.contact_name}.` : `Approved ${n} transaction${n === 1 ? "" : "s"}.`;
      pendingIntentRef.current = null;
      setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, card: null } : mm2).concat([{ role: "assistant", content: msg }]));
      if (voiceOnRef.current) speakOne(msg);
      emitAction("bulk-approve-done", { msg, origin: "chat" });
      emitAction("txns:changed");
    } catch {
      setMessages(mm => [...mm, { role: "assistant", content: "Sorry — bulk approval failed." }]);
    }
  };
  const declineBulkApprove = (i) => {
    pendingIntentRef.current = null;
    setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, card: null } : mm2).concat([{ role: "assistant", content: "OK — just the one approved." }]));
    if (voiceOnRef.current) speakOne("OK, just the one approved.");
    emitAction("bulk-approve-done", { origin: "chat", declined: true });
  };
  // Voice/text → card button. The LLM picks which visible button the
  // utterance means (or none → normal chat flow). Returns true if handled.
  const SWEEP_ACTIONS = [
    { id: "sweep_skip", label: "Skip", hint: "skip this vendor for now and move to the next uncategorized one" },
    { id: "sweep_stop", label: "Stop", hint: "stop the uncategorized sweep entirely" },
  ];
  const runSweepAction = (id, userMsg) => {
    setMessages(m => [...m, { role: "user", content: userMsg }]);
    if (id === "sweep_skip") sweepAdvance("skip");
    else endSweep("stopped");
  };
  const routeToCategoryCard = async (userMsg) => {
    const msgs = messagesRef.current;
    const i = msgs.map(mm => ["category-recommend", "bulk-approve-confirm"].includes(mm.card?.kind)).lastIndexOf(true);
    if (i < 0) {
      if (!sweepRef.current) return false;
      try {
        const { data } = await api.post(`/companies/${currentId}/ai/card-intent`, { message: userMsg, actions: SWEEP_ACTIONS });
        if (data?.action_id && (data.confidence ?? 0) >= 0.6) { runSweepAction(data.action_id, userMsg); return true; }
      } catch { /* fall through */ }
      return false;
    }
    const card = msgs[i].card;
    const sweepExtra = sweepRef.current ? SWEEP_ACTIONS : [];
    if (card.kind === "bulk-approve-confirm") {
      const n = card.ticked ? card.ticked.length : card.similar.count;
      const actions = [
        { id: "approve_ticked", label: `Yes, approve ${n}`, hint: `categorize the ${n} ticked ${card.similar.contact_name} rows the same way and approve them, no rule` },
        ...(card.rule_exists ? [] : [{ id: "approve_ticked_rule", label: `Approve ${n} + create rule`, hint: "same, AND create a rule so future imports from this vendor land there automatically" }]),
        { id: "decline", label: "No, thanks", hint: "leave the others alone, only the one already approved" },
        ...sweepExtra,
      ];
      let pick = null;
      try {
        const { data } = await api.post(`/companies/${currentId}/ai/card-intent`, { message: userMsg, actions });
        if (data?.action_id && (data.confidence ?? 0) >= 0.6) pick = actions.find(x => x.id === data.action_id) || null;
      } catch { return false; }
      if (!pick) return false;
      if (pick.id.startsWith("sweep_")) { runSweepAction(pick.id, userMsg); return true; }
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      if (pick.id === "decline") declineBulkApprove(i);
      else await runBulkApprove(i, card, pick.id === "approve_ticked_rule");
      return true;
    }
    const a = card.rec.account;
    const isNew = card.rec.kind === "new";
    const ticked = card.similarMode?.active ? (card.similarMode.ticked || []) : null;
    const actions = [
      { id: "use_this", label: isNew ? "Create account & categorize" : "Use this", hint: `categorize the focused transaction as ${a.code || ""} ${a.name}` },
      ...(card.similar > 0 && !ticked ? [{ id: "show_similar", label: `Show ${card.similar} similar`, hint: "filter the transactions table to the similar rows, apply nothing" }] : []),
      ...(card.similar > 0 ? [{ id: "apply_similar_rule", label: `Also ${card.similar} similar + save rule`, hint: "categorize focused + all similar rows and create a rule for future imports" }] : []),
      ...(ticked ? [{ id: "apply_ticked", label: `Apply to ${ticked.length}`, hint: "categorize only the ticked rows in the table, no rule" },
                    { id: "clear_similar", label: "Clear", hint: "close the similar view and go back to the previous list" }] : []),
      { id: "not_this", label: "Not this", hint: "dismiss / reject the recommendation" },
      ...(card.alternatives || []).map(alt => ({ id: `alt:${alt.id}`, label: `${alt.code} ${alt.name}`, hint: `use alternative account ${alt.name} instead` })),
      ...(card.direction === "money_out" && !/owner/i.test(a.name) ? [{ id: "owner_draw", label: "Not a business expense (Owner's Draw)", hint: "personal spending, book to Owner's Draw" }] : []),
      ...sweepExtra,
    ];
    let pick = null;
    try {
      const { data } = await api.post(`/companies/${currentId}/ai/card-intent`, { message: userMsg, actions });
      if (data?.action_id && (data.confidence ?? 0) >= 0.6) pick = actions.find(x => x.id === data.action_id) || null;
    } catch { return false; }
    if (!pick) return false;
    if (pick.id.startsWith("sweep_")) { dismissCard(i); runSweepAction(pick.id, userMsg); return true; }
    setMessages(m => [...m, { role: "user", content: userMsg }, { role: "assistant", content: `On it — **${pick.label}**.` }]);
    const primary = { ...(isNew ? { newAccount: a } : { accountId: a.id }), setContactName: card.setContact || null };
    if (pick.id === "use_this") await applyCategoryCard(i, card, primary);
    else if (pick.id === "show_similar") showSimilarForCard(i, card);
    else if (pick.id === "apply_similar_rule") await applyCategoryCard(i, card, { ...primary, withSimilar: true });
    else if (pick.id === "apply_ticked") await applyCategoryCard(i, card, { ...primary, txnIds: ticked });
    else if (pick.id === "clear_similar") emitAction("similar-clear");
    else if (pick.id === "not_this") dismissCard(i);
    else if (pick.id === "owner_draw") await applyCategoryCard(i, card, { newAccount: { semantic: "owner_draw", name: "Owner's Draw", type: "equity" } });
    else if (pick.id.startsWith("alt:")) await applyCategoryCard(i, card, { accountId: pick.id.slice(4) });
    return true;
  };

  // ---- Uncategorized Sweep: one group (vendor) at a time ----
  // Reuses the pinned-txn → recommendCategory → applyCategoryCard flow; the
  // Transactions page shows the group's rows pre-ticked via show-similar-txns.
  const sweepRef = useRef(null); // { groups, idx, done, doneTxns, skipped }
  const lastSimilarSelectionRef = useRef(null);
  const fmtMoney = (n) => Math.abs(n).toLocaleString("en-US", { style: "currency", currency: "USD" });
  const endSweep = (reason) => {
    const s = sweepRef.current;
    sweepRef.current = null;
    emitAction("similar-clear");
    resolveInquiry();
    if (!s) return;
    const say = reason === "empty"
      ? "Nothing left in Uncategorized — you're all caught up."
      : `Sweep ${reason === "stopped" ? "stopped" : "complete"} — categorized **${s.done}** group${s.done === 1 ? "" : "s"} (${s.doneTxns} transaction${s.doneTxns === 1 ? "" : "s"})${s.skipped ? `, skipped **${s.skipped}**` : ""}${reason === "stopped" && s.idx < s.groups.length ? `, ${s.groups.length - s.idx} left for later` : ""}.`;
    setMessages(m => [...m, { role: "assistant", content: say }]);
    if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
  };
  const sweepStep = () => {
    const s = sweepRef.current;
    if (!s) return;
    if (s.idx >= s.groups.length) return endSweep("done");
    const g = s.groups[s.idx];
    const dir = g.money_in && g.money_out ? "mixed in/out" : g.money_in ? "in" : "out";
    const when = g.first_date === g.last_date ? g.last_date : `${g.first_date} → ${g.last_date}`;
    const say = `**${s.idx + 1} of ${s.groups.length}** · **${g.label}** — ${g.count} uncategorized, ${fmtMoney(g.total)} ${dir} (${when}). What ${g.count === 1 ? "was this" : "were these"} for? Describe it, or say **skip** / **stop**.`;
    inquiryTxnRef.current = g.sample.id;
    setFocus({ id: g.sample.id, merchant: g.sample.merchant, amount: g.sample.amount, date: g.sample.date }, { pin: true });
    const payload = { ids: g.ids, label: g.label, count: Math.max(0, g.count - 1), returnTo: "/accounting/transactions" };
    lastSimilarSelectionRef.current = g.ids;
    sessionStorage.setItem("axiom_similar_view", JSON.stringify(payload));
    if (location.pathname === "/accounting/transactions") emitAction("show-similar-txns", payload);
    else navigate("/accounting/transactions");
    setMessages(m => [...m, { role: "assistant", content: say }]);
    if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
    setTimeout(() => setMicMode("open"), 250);
  };
  const sweepAdvance = (how, txns = 0) => {
    const s = sweepRef.current;
    if (!s) return;
    if (how === "done") { s.done += 1; s.doneTxns += txns; } else if (how === "skip") { s.skipped += 1; }
    s.idx += 1;
    emitAction("similar-clear");
    setTimeout(sweepStep, 450);
  };
  const startSweep = async () => {
    try {
      const { data } = await api.get(`/companies/${currentId}/ai/uncategorized-sweep`);
      const groups = data.groups || [];
      if (!groups.length) { sweepRef.current = { groups: [], idx: 0, done: 0, doneTxns: 0, skipped: 0 }; return endSweep("empty"); }
      sweepRef.current = { groups, idx: 0, done: 0, doneTxns: 0, skipped: 0 };
      const intro = `Let's clear Uncategorized — **${data.total_transactions}** transaction${data.total_transactions === 1 ? "" : "s"} across **${groups.length}** vendor${groups.length === 1 ? "" : "s"}. I'll take them one vendor at a time; answer in your own words and I'll categorize the ticked rows.`;
      setMessages(m => [...m, { role: "assistant", content: intro }]);
      if (voiceOnRef.current) speakOne(intro.replace(/\*\*/g, ""));
      setTimeout(sweepStep, 300);
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't load the uncategorized list." }]);
    }
  };
  const startSweepRef = useRef(startSweep);
  startSweepRef.current = startSweep;
  useActionListener("uncat-sweep-start", () => { if (!sweepRef.current) startSweepRef.current(); });

  const send = async () => {
    if (!input.trim() || streaming || !currentId) return;
    // In open-mic mode we deliberately keep the recognizer alive across
    // turns so the user never re-taps. In ptt/off modes, stop.
    if (listening && micModeRef.current !== "open") {
      stopListening();
    }
    clearSilenceTimer();
    const userMsg = input.trim();
    setInput("");

    // ── Voice-Action clarification hand-off ────────────────────
    // If the Voice popup asked us a question, the very next user turn
    // is the ANSWER — ship it back to the popup and don't LLM it.
    if (voiceClarifyRef.current) {
      const q = voiceClarifyRef.current.questions?.[0] || "";
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: "Updating the popup — one sec." },
      ]);
      try {
        window.dispatchEvent(new CustomEvent("axiom:voice-clarify-answer", {
          detail: { answer: userMsg, question: q },
        }));
      } catch { /* CustomEvent unsupported */ }
      setVoiceClarify(null);
      return;
    }

    // ── Voice-Action review hand-off ────────────────────────────
    // Popup is ready + no questions pending. Interpret user's next turn:
    //   • "looks good" / "confirm" / "send it" → commit the popup.
    //   • "cancel" / "scratch that"          → close the popup.
    //   • anything else                      → re-plan as a refinement.
    if (voiceReviewRef.current) {
      const confirmRe = /^(?:looks?\s+good|looks\s+good\s+to\s+me|lgtm|yep|yeah|yes|confirm(?:\s+all)?|save(?:\s+it)?|send(?:\s+it)?|go\s+ahead|do\s+it|ship\s+it|perfect|all\s+good)[\s.!]*$/i;
      const cancelRe  = /^(?:cancel|scratch\s+that|nevermind|never\s+mind|abort|stop|no\s+thanks?|drop\s+it|discard(?:\s+all)?)[\s.!]*$/i;
      if (confirmRe.test(userMsg)) {
        setMessages(m => [
          ...m,
          { role: "user", content: userMsg },
          { role: "assistant", content: "Saving now." },
        ]);
        try { window.dispatchEvent(new CustomEvent("axiom:voice-review-confirm")); }
        catch { /* ignore */ }
        setVoiceReview(null);
        return;
      }
      if (cancelRe.test(userMsg)) {
        setMessages(m => [
          ...m,
          { role: "user", content: userMsg },
          { role: "assistant", content: "Cancelled — nothing was saved." },
        ]);
        try { window.dispatchEvent(new CustomEvent("axiom:voice-review-cancel")); }
        catch { /* ignore */ }
        setVoiceReview(null);
        return;
      }
      // Refinement — treat like a clarification: re-plan with this turn
      // appended to the utterance. The popup will regenerate itself.
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: "Updating the popup — one sec." },
      ]);
      try {
        window.dispatchEvent(new CustomEvent("axiom:voice-clarify-answer", {
          detail: { answer: userMsg, question: "" },
        }));
      } catch { /* ignore */ }
      setVoiceReview(null);
      return;
    }

    // ── Category card: voice/text → button ─────────────────────
    // "use it", "show the three similar ones", "apply to those", "not that
    // one — owner's draw"… an LLM maps the utterance to a visible button.
    if (await routeToCategoryCard(userMsg)) return;

    // "Stop" / "quiet" / "shut up" / "be quiet" / "silence" / "hush" / "cancel
    // speech" — kills any current TTS utterance immediately. Doesn't send to
    // the LLM, doesn't post a chat bubble (that would just add MORE for TTS
    // to potentially say next).
    if (/^(?:stop(?: talking| speaking)?|quiet|shush|hush|silence|shut up|be quiet|(?:cancel|stop)\s+speech|(?:cancel|stop)\s+voice)[\s.!]*$/i.test(userMsg)) {
      try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
      ttsSpeakingRef.current = false;
      setTtsActive(false);
      return;
    }

    // "Cancel focus" / "clear focus" / "unfocus" / "stop focus" — voice-
    // and text-driven way to drop the pinned bucket/transaction focus
    // without having to reach for the mouse.
    if (/^(?:cancel|clear|drop|stop|end|remove|unpin|un)[\s-]?focus\b|^unfocus\b/i.test(userMsg)) {
      setFocus(null, { force: true });
      const ack = "Focus cleared. What next?";
      setMessages(m => [...m, { role: "user", content: userMsg }, { role: "assistant", content: ack }]);
      if (voiceOnRef.current) speakOne(ack);
      return;
    }

    // ------ HELP-ROUTER interceptor -----------------------------------
    // Route "how do I / where do I / can I / what is / show me" questions
    // to the 34-task help catalog. Only fires on those verb prefixes; any
    // other input (imperatives, cleanup answers, business-profile chat)
    // falls through untouched. Renders navigation + "do it for me" pills
    // via the existing `panel-action` event bus so the AiPanel doesn't
    // need custom rendering code. Feb 28 2026.
    if (/^(?:how\s+(?:do|to|can)|where\s+(?:do|is|can|to)|can\s+(?:i|you|we)|is\s+it\s+possible|do\s+you\s+support|what\s+(?:is|does|are)|what'?s)\b/i.test(userMsg)) {
      try {
        const r = await api.post(`/help/ask`, { query: userMsg });
        const help = r.data;
        if (help?.matched) {
          // Build a markdown-ish assistant bubble. Actions expressed
          // via the existing quick-actions/action buttons that AiPanel
          // already renders — reuse the `.actions` field.
          const actions = [];
          if (help.take_me_there) {
            actions.push({ label: "Take me there →", to: help.take_me_there });
          }
          if (help.do_it_for_me) {
            actions.push({
              label: help.do_it_for_me.label,
              to: help.do_it_for_me.url,
              variant: help.do_it_for_me.tier === "green" ? "primary" : "secondary",
            });
          }
          const heading = help.heading ? `**${help.heading}**\n\n` : "";
          setMessages(m => [
            ...m,
            { role: "user", content: userMsg },
            { role: "assistant", content: heading + (help.body || ""), actions },
          ]);
          if (voiceOnRef.current && help.body) {
            speakOne(help.body.replace(/\*\*/g, "").split("\n")[0]);
          }
          return;
        }
      } catch { /* fall through to LLM */ }
    }

    // coach listener on either the onboarding page (Business Profile
    // extractor) OR the dashboard's onboarding-not-complete nudge. In BOTH
    // contexts we suppress the normal LLM chat so the coach's clean
    // confirmation isn't tailed by a yappy generic follow-up (avoids two
    // overlapping TTS voices playing at once).
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      const onOnboarding = path.startsWith("/onboarding");
      const onDashboardWithNudge =
        path.startsWith("/dashboard") && current && current.onboarding_complete === false;
      if (onOnboarding || onDashboardWithNudge) {
        emitAction("onboarding-user-message", { text: userMsg });
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        return;
      }
    }
    // ------ Meaning-first category recommendation --------------------
    // Shared by the focused-transaction path and the free-text fallback.
    // Returns true when a recommendation card was shown.
    async function recommendCategory(text, txnId) {
      try {
        const { data } = await api.post(`/companies/${currentId}/ai/resolve-category`, { message: text, txn_id: txnId || null });
        const rec = data?.recommendation;
        if (!rec?.account) {
          const say = data?.ask ? data.ask : data?.direction === "money_out"
            ? "This is money going OUT, so it can't be a sale or customer payment. Was it something you bought (what?), a refund you gave a customer, a transfer to another account, or money you took for yourself?"
            : data?.direction === "money_in"
              ? "This is money coming IN, so it can't be an expense. Was it a customer paying you (for what?), a refund from a vendor, a transfer from another account, or money you put in yourself?"
              : "I couldn't work out where that belongs yet — tell me what the money was for in a few words (e.g. “office supplies”, “a customer paying an invoice”, “paid myself”).";
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          return true;
        }
        const a = rec.account;
        const label = `${a.code || "new"} ${a.name}`;
        const who = data.txn?.merchant ? `**${data.txn.merchant}**${typeof data.txn.amount === "number" ? ` (${data.txn.amount.toLocaleString("en-US", { style: "currency", currency: "USD" })})` : ""} → ` : "";
        const typeLine = [...new Set([a.type, a.subtype, a.detail_type].filter(Boolean).map(s => String(s).replace(/_/g, " ")))].join(" › ");
        const say = rec.kind === "existing"
          ? `${who}**${label}** (already on your chart). ${rec.why}`
          : rec.subaccount
            ? `${who}I'd add a sub-account **${a.name}** under **${a.parent_name}**${a.code ? ` (${a.code})` : ""}. ${rec.why}`
            : `${who}nothing on your chart fits, so I'd add **${label}** — ${typeLine}${a.parent_name ? `, under ${a.parent_name}` : ""}. ${rec.why}`;
        setMessages(m => [...m, {
          role: "assistant", content: say,
          card: { kind: "category-recommend", rec, txnId: txnId || data.txn?.id || null, txn: data.txn,
                  similar: data.similar_count || 0, similarIds: data.similar_ids || [], similarLabel: data.similar_label || data.txn?.merchant || "",
                  alternatives: data.alternatives || [], direction: data.direction, setContact: data.set_contact || null, partyName: data.party_name || null,
                  sweep: !!sweepRef.current, similarMode: sweepRef.current ? { active: true, ticked: lastSimilarSelectionRef.current || [] } : null },
        }]);
        if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
        return true;
      } catch {
        return false;
      }
    }

    // ------ Cleanup-Copilot inquiry interceptor -----------------------
    // The Transactions page emits `cleanup-inquiry` when the user clicks
    // "Fix now" on the hero band. The AI asked "what are these X <contact>
    // transactions?" — the next user message is the answer. Interpret it
    // as a category description, resolve/create the account, and show a
    // bulk-approve confirmation card.
    if (pendingIntentRef.current?.kind === "cleanup-inquiry") {
      // Source of truth = the newest vendor prompt in the transcript. If
      // the ref drifted (prompt for contact B fired after A's bubble was
      // already the latest), trust the bubble the user is replying to.
      const lastPrompt = [...messagesRef.current].reverse().find(mm => mm.role === "assistant" && mm.inquiry);
      const inq = (lastPrompt?.inquiry?.action?.contact_id
                   && lastPrompt.inquiry.action.contact_id !== pendingIntentRef.current?.action?.contact_id)
        ? lastPrompt.inquiry
        : pendingIntentRef.current;
      pendingIntentRef.current = null;
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      const rawText = userMsg;

      // Skip / defer intent — the user wants to leave this contact alone
      // and jump to the next one in the queue. Recognise natural phrasing
      // ("skip", "skip amazon", "let's skip this", "move on", "next",
      // "pass", "come back later"). We deliberately anchor these so a
      // category name that merely CONTAINS "skip" (unlikely, but e.g.
      // "Skiptown Brewery") isn't caught.
      const skipRe = /^(?:let'?s?\s+|please\s+|just\s+|can\s+we\s+|can\s+you\s+)?(?:skip|move\s+on|move\s+past|pass|pass\s+on|next(?:\s+one)?|not\s+(?:now|yet)|come\s+back\s+(?:to\s+(?:this|it|them|these))?\s*later|hold\s+off|ignore|forget\s+(?:it|this|these|them)|leave\s+(?:it|this|these|them))\b(?!\w)/i;
      if (skipRe.test(rawText)) {
        const contactId = inq?.action?.contact_id;
        const contactName = inq?.action?.contact_name || "this contact";
        const say = `Skipped **${contactName}** — moving on.`;
        setMessages(mm => [...mm, { role: "assistant", content: say }]);
        if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
        // Auto-advance: same event the confirm cards emit, so the
        // CleanupCopilot dismisses this contact + queues up the next.
        emitAction("cleanup-completed", {
          contact_id: contactId,
          kind: inq?.action?.kind || "contact_in_uncat",
          count: 0,
          skipped: true,
        });
        return;
      }

      // Everything past this point goes to the LLM-backed CPA reviewer.
      // The reviewer classifies intent (categorize / approve_existing /
      // redirect / skip / question / unclear) AND, for categorize, resolves
      // the user's words to real Chart-of-Accounts rows — so we never again
      // create an account literally named "they look good the way they are".
      let review;
      try {
        review = await api.post(`/companies/${currentId}/ai/cpa-review`, {
          message: rawText,
          contact_name: inq?.action?.contact_name || "this contact",
          contact_id: inq?.action?.contact_id || null,
        });
      } catch (e) {
        pendingIntentRef.current = inq;
        const say = "I couldn't reach the AI reviewer just now — mind rephrasing?";
        setMessages(m => [...m, { role: "assistant", content: say }]);
        if (voiceOnRef.current) speakOne(say);
        return;
      }
      const rev = review?.data || {};

      // Skip via LLM (belt & suspenders — the regex above already catches most).
      if (rev.intent === "skip") {
        const say = rev.say || `Skipped **${inq?.action?.contact_name || "this contact"}** — moving on.`;
        setMessages(mm => [...mm, { role: "assistant", content: say }]);
        if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
        emitAction("cleanup-completed", {
          contact_id: inq?.action?.contact_id,
          kind: inq?.action?.kind || "contact_in_uncat",
          count: 0,
          skipped: true,
        });
        return;
      }

      // Approve-existing: user wants to leave rows with their current
      // categories, just mark them human-reviewed.
      if (rev.intent === "approve_existing") {
        try {
          const list = await api.get(
            `/companies/${currentId}/transactions?contact_id=${inq.action.contact_id}&limit=2000`
          );
          const rows = (list.data.transactions || []).filter(t => !t.human_reviewed);
          const catRows = rows.filter(t => !!t.category_account_id);
          const uncatCount = rows.length - catRows.length;
          // Idempotent path: if nothing is left unreviewed for this contact
          // (already approved in an earlier turn, or all rows are truly
          // uncategorized), still emit cleanup-completed so the copilot
          // dismisses this contact and moves on. This is the primary fix for
          // the "same contact keeps coming back" bug the user reported.
          if (rows.length === 0) {
            const name = inq.action.contact_name || "these transactions";
            const say = `Already approved — moving on from **${name}**.`;
            setMessages(mm => [...mm, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
            emitAction("cleanup-completed", {
              contact_id: inq.action.contact_id,
              kind: inq.action.kind || "contact_split",
              count: 0,
            });
            return;
          }
          if (catRows.length === 0) {
            const say = rev.say || "None of these rows have a category yet — I need one before I can approve them. What account should they post to?";
            pendingIntentRef.current = inq;
            setMessages(mm => [...mm, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say);
            return;
          }
          // Bucket by existing category so we can send one apply-bulk-approve
          // per unique account.
          const byAcct = new Map();
          for (const t of catRows) {
            const k = t.category_account_id;
            if (!byAcct.has(k)) byAcct.set(k, []);
            byAcct.get(k).push(t.id);
          }
          const groups = [];
          for (const [acctId, ids] of byAcct.entries()) {
            groups.push({ txn_ids: ids, category_account_id: acctId });
          }
          const res = await api.post(
            `/companies/${currentId}/transactions/apply-multi-bulk-approve-rule`,
            { contact_id: inq.action.contact_id, contact_name: inq.action.contact_name,
              groups, create_rules: false }
          );
          const say = `Approved **${res.data?.updated || 0}** ${inq.action.contact_name} rows with their current categorization.${uncatCount ? ` (${uncatCount} without a category were left alone.)` : ""}`;
          setMessages(mm => [...mm, { role: "assistant", content: say,
            undo: { txnIds: catRows.map(t => t.id), contactName: inq.action.contact_name } }]);
          if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
          emitAction("txns:changed");
          emitAction("cleanup-completed", {
            contact_id: inq.action.contact_id,
            kind: inq.action.kind || "contact_in_uncat",
            count: res.data?.updated || 0,
          });
        } catch (e) {
          setMessages(mm => [...mm, { role: "assistant", content: "Sorry — I couldn't approve those rows." }]);
        }
        return;
      }

      // Redirect: user wants to jump to a different contact.
      if (rev.intent === "redirect") {
        const target = rev.resolution?.target_contact_name;
        if (!target) {
          const say = rev.say || "Which contact would you like to look at?";
          pendingIntentRef.current = inq;
          setMessages(mm => [...mm, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          return;
        }
        // Dismiss the current contact, then look up the target contact by name.
        emitAction("cleanup-completed", {
          contact_id: inq.action.contact_id,
          kind: inq.action.kind || "contact_in_uncat",
          count: 0,
          skipped: true,
        });
        try {
          const cr = await api.get(`/companies/${currentId}/contacts?q=${encodeURIComponent(target)}&limit=5`);
          const list = cr.data.contacts || [];
          const hit = list.find(c => (c.name || "").toLowerCase() === target.toLowerCase()) || list[0];
          if (hit) {
            // Count unreviewed txns for the target.
            const tr = await api.get(`/companies/${currentId}/transactions?contact_id=${hit.id}&status=unreviewed&limit=1`);
            const total = tr.data.total || tr.data.transactions?.length || 0;
            const say = rev.say || `Jumping to **${hit.name}**.`;
            setMessages(mm => [...mm, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
            emitAction("cleanup-inquiry", {
              action: {
                kind: "contact_in_uncat",
                contact_id: hit.id,
                contact_name: hit.name,
                count: total,
                total_amount: 0,
              },
            });
          } else {
            const say = `I couldn't find a contact called "${target}" — try again with the exact name.`;
            setMessages(mm => [...mm, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say);
          }
        } catch (e) {
          setMessages(mm => [...mm, { role: "assistant", content: "Sorry — I couldn't redirect." }]);
        }
        return;
      }

      // Question: user is asking about the txns, not categorizing them.
      // Hand off to the normal chat stream so it can answer conversationally.
      if (rev.intent === "question") {
        pendingIntentRef.current = inq; // leave the queue paused for the follow-up
        // Fall through — the code below will hit the streaming assistant path.
      } else if (rev.intent === "unclear") {
        pendingIntentRef.current = inq;
        const say = rev.say || rev.resolution?.clarifying_question ||
          "I need one or two more words — what category should these fall under?";
        setMessages(mm => [...mm, { role: "assistant", content: say }]);
        if (voiceOnRef.current) speakOne(say);
        return;
      } else if (rev.intent === "categorize") {
        // Build the cleanup-multi-confirm card from the LLM's resolved buckets.
        try {
          const list = await api.get(
            `/companies/${currentId}/transactions?contact_id=${inq.action.contact_id}&limit=2000`
          );
          const rows = (list.data.transactions || []).filter(t => !t.human_reviewed);
          const buckets = rev.resolution?.buckets || [];
          if (buckets.length === 0) {
            pendingIntentRef.current = inq;
            const say = "I understood you wanted to categorize, but couldn't pin down which account. Can you try again?";
            setMessages(mm => [...mm, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say);
            return;
          }
          // Assign each row to the first bucket whose predicate matches; rows
          // that match nothing go to the last (base) bucket.
          const bucketState = buckets.map(b => ({
            label: b.label || b.account?.name || "everything else",
            account: b.account || {},
            predicate: b.predicate || null,
            txns: [],
          }));
          const baseBucket = bucketState.find(b => !b.predicate) || bucketState[bucketState.length - 1];
          for (const t of rows) {
            const amt = Math.abs(parseFloat(t.amount) || 0);
            let placed = false;
            for (const b of bucketState) {
              const p = b.predicate;
              if (!p) continue;
              const hit = (p.exactAmount != null && Math.abs(amt - p.exactAmount) < 0.5)
                       || (p.max != null && amt <= p.max && (p.min == null || amt >= p.min))
                       || (p.min != null && amt >= p.min && (p.max == null || amt <= p.max));
              if (hit) { b.txns.push(t); placed = true; break; }
            }
            if (!placed && baseBucket) baseBucket.txns.push(t);
          }
          const preview = bucketState.filter(b => b.txns.length > 0);
          if (preview.length === 0) {
            setMessages(mm => [...mm, { role: "assistant", content: "None of the rows matched the buckets — nothing to do." }]);
            return;
          }
          const totalCount = preview.reduce((s, b) => s + b.txns.length, 0);
          const previewLines = preview.map(b => {
            const a = b.account;
            const label = a.existing_account_id
              ? `${a.code || "?"} ${a.name || "?"}`
              : `${a.code || "new"} ${a.name || "?"} (new)`;
            return `• **${b.txns.length}** ${b.txns.length === 1 ? "row" : "rows"} (${b.label}) → **${label}**`;
          }).join("\n");
          const say = `Here's the plan for **${inq.action.contact_name}**:\n${previewLines}\nApply all ${totalCount}, mark them reviewed, and create rules so future imports do the same?`;
          setMessages(m => [...m, {
            role: "assistant", content: say,
            card: {
              kind: "cleanup-multi-confirm",
              // Adapt the LLM-resolved buckets into the shape the existing
              // InlineConfirmCard handler expects.
              preview: preview.map(b => ({
                label: b.label,
                categoryName: b.account?.name || b.label,
                txns: b.txns,
                amount_min: b.predicate?.min ?? null,
                amount_max: b.predicate?.max ?? null,
                existingAccount: b.account?.existing_account_id ? {
                  id: b.account.existing_account_id,
                  code: b.account.code,
                  name: b.account.name,
                } : null,
                guessType: b.account?.type || "expense",
              })),
              contactId: inq.action.contact_id,
              contactName: inq.action.contact_name,
              count: totalCount,
            },
          }]);
          if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, "").replace(/\n/g, ". "));
          // Keep the inquiry PENDING so the user can refine by typing
          // ("no, only the uncategorized ones as Consulting" / "actually
          // make the big one a Loan"). When they click Yes/No on the card
          // OR skip, the handlers clear pendingIntentRef.
          pendingIntentRef.current = inq;
        } catch (e) {
          setMessages(mm => [...mm, { role: "assistant", content: "Sorry — I couldn't build the plan." }]);
        }
        return;
      }
      // Note: if intent === "question", we intentionally fall through to the
      // regular chat stream below.
    }

    // ---- Pinned-transaction free text → category recommendation ----
    // The user answered "what was this for?" in their own words and no
    // explicit command matched. Short, non-question text about a pinned
    // transaction is a description — resolve its meaning.
    {
      const pinnedTxnId = inquiryTxnRef.current || (focusPinned && focus?.id) || null;
      const words = userMsg.trim().split(/\s+/).length;
      if (pinnedTxnId && words <= 16 && !/\?\s*$/.test(userMsg)
          && !/^(?:what|why|how|when|where|who|which|show|open|go to|create an? invoice|read)\b/i.test(userMsg.trim())) {
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        if (await recommendCategory(userMsg, pinnedTxnId)) return;
        setMessages(m => m.slice(0, -1));
      }
    }

    // ---- Ambiguous account-type resolver ----
    // If we asked the user which side to book an ambiguous name on (e.g.
    // "customer refund" → revenue contra vs expense), catch their answer
    // here BEFORE the generic voice-command dispatch. Recognizes phrases
    // like "as revenue" / "as expense" / "revenue" / "expense" / "it's an
    // expense" / etc. If the utterance doesn't cleanly resolve, fall
    // through so the user can rephrase or ask a follow-up.
    if (pendingIntentRef.current?.kind === "resolve-ambiguous-type") {
      const p = pendingIntentRef.current;
      const t = userMsg.toLowerCase();
      let chosen = null;
      if (/\b(revenue|income|sales|contra[\s-]*sales?|contra[\s-]*revenue)\b/.test(t)) {
        chosen = "revenue";
      } else if (/\b(expense|expend|cost|operating)\b/.test(t)) {
        chosen = "expense";
      }
      if (chosen) {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
            name: p.accountName, type: chosen,
          });
          await api.patch(`/companies/${currentId}/transactions/${p.txnId}`, {
            category_account_id: r.data.id,
          });
          const msg = `${r.data.created ? "Created" : "Reusing"} ${r.data.code} ${r.data.name} (${chosen}) and recategorized this transaction.`;
          setMessages(m => [...m, { role: "assistant", content: msg }]);
          if (voiceOnRef.current) speakOne(msg);
          emitAction("txns:changed");
          if (inquiryTxnRef.current === p.txnId) resolveInquiry();
        } catch (e) {
          setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't create the account or recategorize." }]);
        }
        return;
      }
      // Not a clean resolve — fall through so the user can rephrase.
    }

    // ------ Voice command dispatch (client-side, zero cost) ------
    // If the user's utterance matches a local intent (route/company switch/
    // meta), execute it immediately and skip the LLM round-trip.
    const cmd = resolveVoiceCommand(userMsg, {
      companies,
      navigate,
      switchCompany,
      clearChat: clearChatMessages,
      focus,
      batchActive: !!batchRef.current,
      hasPendingIntent: !!pendingIntentRef.current,
    });

    // --- Batch resolve mode: entry ---
    if (cmd.handled && cmd.remote === "batch-start") {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      await startBatch();
      return;
    }
    // --- Batch resolve mode: in-flight ---
    if (cmd.handled && cmd.batch && batchRef.current) {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      await handleBatchAction(cmd.batch);
      return;
    }

    // --- Weekly review mode: entry ---
    if (cmd.handled && cmd.remote === "review-start") {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      await startReview();
      return;
    }
    // --- Weekly review mode: in-flight navigation ---
    if (cmd.handled && cmd.review && reviewRef.current) {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      if (cmd.review === "exit") exitReview();
      else advanceReview(cmd.review); // next / skip / back
      return;
    }
    // If a review-command word (e.g. "next") arrived when NO review is
    // active, fall through to LLM chat so it's not silently swallowed.

    // --- Pending intent follow-ups (confirm / cancel) ---
    if (cmd.handled && cmd.pending === "confirm" && pendingIntentRef.current) {
      const p = pendingIntentRef.current;
      // Special-case our bulk-approve-contact intent: run the dedicated flow.
      if (p.kind === "bulk-approve-contact") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const sim = p.similar;
          // Fetch every unapproved txn for this contact — sample caps at 5.
          let ids = p.ticked || null;
          if (!ids) {
            if (sim.contact_id) {
              const full = await api.get(
                `/companies/${currentId}/transactions?contact_id=${sim.contact_id}&limit=1000`
              );
              ids = (full.data.transactions || []).filter((t) => !t.human_reviewed).map((t) => t.id);
            } else {
              ids = (sim.items || []).map((x) => x.id);
            }
          }
          const res = await api.post(
            `/companies/${currentId}/transactions/apply-bulk-approve-rule`,
            {
              txn_ids: ids,
              category_account_id: sim.category_account_id,
              contact_id: sim.contact_id,
              contact_name: sim.contact_name,
              create_rule: !!p.create_rule,
              match_text: sim.match_kind === "description" ? sim.match_value : null,
            }
          );
          const updated = res.data?.updated || 0;
          const ruleId = res.data?.rule_id;
          const reply = ruleId
            ? `Approved ${updated} transaction${updated === 1 ? "" : "s"} and created a rule for ${sim.contact_name}.`
            : `Approved ${updated} transaction${updated === 1 ? "" : "s"}.`;
          setMessages(m => m.map(mm => mm.card?.kind === "bulk-approve-confirm" ? { ...mm, card: null } : mm).concat([{ role: "assistant", content: reply }]));
          if (voiceOnRef.current) speakOne(reply);
          emitAction("bulk-approve-done", { msg: reply, origin: "chat" });
          emitAction("txns:changed");
        } catch (e) {
          setMessages(m => [...m, { role: "assistant", content: "Sorry — bulk approval failed." }]);
        }
        return;
      }

      // Transfer-match card: user said "yes" to mark the matching leg too.
      if (p.kind === "transfer-match") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const legId = p.candidates?.[0]?.id;
          if (!legId) throw new Error("no matching leg");
          await api.post(`/companies/${currentId}/transactions/${p.txnId}/mark-as-transfer`, {
            matching_leg_id: legId,
          });
          const say = "Done — both legs of the transfer are categorized correctly and won't hit the P&L.";
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          emitAction("txns:changed");
        } catch (e) {
          setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't link the matching leg." }]);
        }
        return;
      }

      // Create-fixed-asset card: user said "yes" — AI drafts the full
      // payload, we POST it, then broadcast a `fixed-assets:created` event
      // so the FixedAssetsPage refreshes and closes any open modal.
      if (p.kind === "create-fixed-asset") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const r = await api.post(`/companies/${currentId}/assets`, p.payload);
          const monthly = r.data?.monthly_depreciation;
          const posted = r.data?.depreciation_jes_posted;
          const pending = !!r.data?.pending_funding;
          const cost = Number(p.payload?.cost || 0);
          const baseMsg = posted
            ? `Fixed asset created — on the Fixed Assets page + Chart of Accounts, ${posted} depreciation entries scheduled at $${Number(monthly).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}/month.`
            : `Fixed asset created — on the Fixed Assets page + Chart of Accounts (non-depreciable, no schedule).`;
          const fundingMsg = pending
            ? ` The acquisition is booked against Fixed Asset Suspense for $${cost.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}. Next: how was it funded? (cash / loan / owner — you can mix multiple)`
            : "";
          const msg = baseMsg + fundingMsg;
          setMessages(m => [...m, { role: "assistant", content: msg }]);
          if (voiceOnRef.current) speakOne(msg);
          window.dispatchEvent(new CustomEvent("fixed-assets:created", { detail: r.data }));
          if (!pending) setFocus(null);
        } catch (e) {
          const err = e.response?.data?.detail || "Sorry — I couldn't create that asset.";
          setMessages(m => [...m, { role: "assistant", content: err }]);
        }
        return;
      }

      // Fund-fixed-asset card: Phase 2 of the two-phase asset flow.
      // Sweeps balance out of Fixed Asset Suspense into real funding
      // sources (cash, mortgage, owner). Can fire multiple times if
      // funding trickles in from different sources across turns.
      if (p.kind === "fund-fixed-asset") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const assetId = p.payload?.asset_id;
          const sources = p.payload?.sources || [];
          const r = await api.post(
            `/companies/${currentId}/assets/${assetId}/fund`,
            { sources },
          );
          const funded = Number(r.data?.funded_amount || 0);
          const remaining = Number(r.data?.remaining || 0);
          const stillPending = !!r.data?.pending_funding;
          const msg = stillPending
            ? `Funded $${funded.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} — $${remaining.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} still to go. How's the rest covered?`
            : `Fully funded — $${funded.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} swept out of Suspense. Balance Sheet and Loans page are now in sync.`;
          setMessages(m => [...m, { role: "assistant", content: msg }]);
          if (voiceOnRef.current) speakOne(msg);
          window.dispatchEvent(new CustomEvent("fixed-assets:created", { detail: r.data }));
          if (!stillPending) setFocus(null);
        } catch (e) {
          const err = e.response?.data?.detail || "Sorry — I couldn't apply that funding.";
          setMessages(m => [...m, { role: "assistant", content: err }]);
        }
        return;
      }


      // Create-liability-account card: sets up a mortgage / loan account
      // so the AI can then use it as a Loan funding source on the next turn.
      if (p.kind === "create-liability-account") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
            name: p.name, type: "liability", subtype: p.subtype || "long_term_debt",
            code: p.code,
            parent_account_id: p.parent_account_id || null,
            lender: p.lender || null,
            principal: p.principal != null ? Number(p.principal) : null,
            rate: p.rate != null ? Number(p.rate) : null,
            term_months: p.term_months != null ? Number(p.term_months) : null,
          });
          const created = r.data?.created;
          const parentSuffix = p.parent_account_id && created ? " as a sub-account" : "";
          const loanSuffix = created && (p.lender || p.principal != null) ? " Linked Loan record also created." : "";
          const msg = created
            ? `Created ${r.data.code} · ${r.data.name} (liability)${parentSuffix}.${loanSuffix} You can now use it as a Loan funding source.`
            : `${r.data.code} · ${r.data.name} already exists — I'll use it.`;
          setMessages(m => [...m, { role: "assistant", content: msg }]);
          if (voiceOnRef.current) speakOne(msg);
        } catch (e) {
          setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't create the loan account." }]);
        }
        return;
      }


      // Create-account (standalone) card: user said "yes" to create the account.
      if (p.kind === "create-account") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
            name: p.accountName, type: p.accountType,
          });
          const msg = r.data?.created
            ? `Created ${r.data.code} ${r.data.name} (${r.data.type}).`
            : `${r.data.code} ${r.data.name} already exists — using it.`;
          setMessages(m => [...m, { role: "assistant", content: msg }]);
          if (voiceOnRef.current) speakOne(msg);
          emitAction("txns:changed");
        } catch (e) {
          setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't create that account." }]);
        }
        return;
      }

      // Create-then-recat card: user said "yes" to create the account AND
      // reclassify the focused transaction to it in a single step.
      if (p.kind === "create-then-recat") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
            name: p.accountName, type: p.accountType,
          });
          await api.patch(`/companies/${currentId}/transactions/${p.txnId}`, {
            category_account_id: r.data.id,
          });
          const msg = `${r.data.created ? "Created" : "Reusing"} ${r.data.code} ${r.data.name} and recategorized this transaction.`;
          setMessages(m => [...m, { role: "assistant", content: msg }]);
          if (voiceOnRef.current) speakOne(msg);
          emitAction("txns:changed");
          if (inquiryTxnRef.current === p.txnId) resolveInquiry();
        } catch (e) {
          setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't create the account or recategorize." }]);
        }
        return;
      }

      // AI PROPOSAL follow-through: the AI's previous turn ended with
      // [[PROPOSAL:action=categorize|category=<name>|scope=<focused|selected|contact:...>]].
      // The user just said "yes / do it / categorize it / go ahead". Emit
      // an action the Transactions page listens on; it will fuzzy-match the
      // category against the CoA and apply to the selection (or focused row).
      if (p.kind === "categorize-proposal") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        emitAction("apply-categorize-proposal", {
          category: p.category,
          scope: p.scope,
          focusedTxnId: focus?.id || null,
        });
        const ack = `On it — categorizing to ${p.category}.`;
        setMessages(m => [...m, { role: "assistant", content: ack }]);
        if (voiceOnRef.current) speakOne(ack);
        return;
      }
      // Feb 2026 — user said yes to a proposal that both CREATES a new
      // CoA account AND categorizes the focused transaction/bucket to
      // it. Now supports BOTH single-txn focus and bucket focus (stepper
      // view). For bucket focus, delegates to CleanupCopilot's own
      // apply-categorize-proposal listener which correctly resolves
      // bucket rows via bulk-approve-ai-ready. After both succeed on a
      // single-txn focus, we look up other UN-REVIEWED transactions
      // from the same contact and offer to apply the new account to
      // them too.
      if (p.kind === "create-then-categorize-focused") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          // 1) Create (or reuse) the CoA account (idempotent).
          const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
            name: p.accountName, type: p.accountType,
          });
          const acct = r.data;
          const created = acct.created ? "Created" : "Reusing";
          // 2a) BUCKET focus path — delegate to CleanupCopilot's
          // apply-categorize-proposal listener. It handles bucket +
          // checked-bucket + single-txn cases via bulk-approve-ai-ready.
          if (focus?.bucket && focus?.key) {
            emitAction("apply-categorize-proposal", {
              category: acct.name,
              accountId: acct.id,
              scope: "focused",
              focusedTxnId: null,
            });
            const say = `${created} ${acct.code} ${acct.name} — applying to ${focus.count || "the"} row${focus.count === 1 ? "" : "s"} in the ${focus.contact_name || "focused"} bucket.`;
            setMessages(m => [...m, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say);
            return;
          }
          // 2b) Single-txn focus path — direct PATCH + sibling suggestions.
          const txnId = focus?.id;
          if (!txnId) {
            const say = `${created} ${acct.code} ${acct.name}, but I couldn't tell which transaction to book it to — pin a transaction or bucket first.`;
            setMessages(m => [...m, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say);
            return;
          }
          await api.patch(`/companies/${currentId}/transactions/${txnId}`, {
            category_account_id: acct.id,
          });
          emitAction("txns:changed");
          // 3) Find OTHER un-reviewed txns from the same contact.
          const focusedTxn = await api.get(`/companies/${currentId}/transactions/${txnId}`).catch(() => null);
          const contactId = focusedTxn?.data?.contact_id || focus?.contact_id;
          let siblings = [];
          if (contactId) {
            const sib = await api.get(
              `/companies/${currentId}/transactions`,
              { params: {
                contact_id: contactId,
                limit: 100,
                status: "unapproved",
              }},
            ).catch(() => ({ data: { transactions: [] } }));
            // Sibling safety filter — see comment in the sibling code
            // path above. Same amount-sign + all contact-name tokens
            // must appear in merchant/description.
            const focusedAmt = focusedTxn?.data?.amount || 0;
            const focusedSign = focusedAmt >= 0 ? 1 : -1;
            const contactName = String(focusedTxn?.data?.contact_name || "").toLowerCase();
            const tokens = (contactName.match(/[a-z]{2,}/g) || [])
              .filter(t => !["the","and","for","inc","llc","ltd","corp","co"].includes(t));
            siblings = (sib.data?.transactions || []).filter(t => {
              if (t.id === txnId) return false;
              if (t.human_reviewed) return false;
              if (t.category_account_id === acct.id) return false;
              const sign = (t.amount || 0) >= 0 ? 1 : -1;
              if (sign !== focusedSign) return false;
              const hay = ((t.merchant || "") + " " + (t.description || "")).toLowerCase();
              if (tokens.length && !tokens.every(tok => hay.includes(tok))) return false;
              return true;
            });
          }
          if (siblings.length > 0) {
            const ask = `${created} ${acct.code} ${acct.name} and booked this transaction to it. I found ${siblings.length} other un-reviewed ${siblings.length === 1 ? "transaction" : "transactions"} from ${focusedTxn?.data?.contact_name || "this contact"} — want me to apply ${acct.name} to ${siblings.length === 1 ? "it" : "them"} too?`;
            setMessages(m => [...m, { role: "assistant", content: ask }]);
            if (voiceOnRef.current) speakOne(ask);
            pendingIntentRef.current = {
              kind: "apply-to-siblings",
              accountId: acct.id,
              accountName: acct.name,
              accountCode: acct.code,
              siblingIds: siblings.map(t => t.id),
              contactName: focusedTxn?.data?.contact_name || "this contact",
            };
          } else {
            const say = `${created} ${acct.code} ${acct.name} and booked this transaction to it.`;
            setMessages(m => [...m, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say);
          }
          if (inquiryTxnRef.current === txnId) resolveInquiry();
        } catch (e) {
          setMessages(m => [...m, {
            role: "assistant",
            content: `Sorry — I couldn't create the account or categorize: ${e?.response?.data?.detail || e.message}`,
          }]);
        }
        return;
      }
      // Follow-through from create-then-categorize: user said yes to
      // applying the newly-created account to the other same-contact txns.
      if (p.kind === "apply-to-siblings") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const r = await api.post(
            `/companies/${currentId}/transactions/bulk-reclassify`,
            {
              transaction_ids: p.siblingIds,
              category_account_id: p.accountId,
            },
          );
          const say = `Done — reclassified ${r.data?.updated || p.siblingIds.length} more ${p.siblingIds.length === 1 ? "transaction" : "transactions"} to ${p.accountName}.`;
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          emitAction("txns:changed");
        } catch (e) {
          setMessages(m => [...m, {
            role: "assistant",
            content: `Sorry — bulk apply failed: ${e?.response?.data?.detail || e.message}`,
          }]);
        }
        return;
      }
      if (p.kind === "transfer-proposal") {
        pendingIntentRef.current = null;
        setPendingIntent(null);
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        if (!focus?.id) {
          const say = "Hover a transaction first so I know which one is the transfer.";
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          return;
        }
        try {
          await api.post(`/companies/${currentId}/transactions/${focus.id}/mark-as-transfer`, {});
          const say = "Marked as an internal transfer.";
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          emitAction("txns:changed");
        } catch (e) {
          setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't mark that as a transfer." }]);
        }
        return;
      }
      setPendingIntent(null);
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      const ok = await submitPendingIntent(p);
      const reply = ok ? "Created." : "I couldn't create that — check the modal.";
      setMessages(m => [...m, { role: "assistant", content: reply }]);
      if (voiceOnRef.current) speakOne(reply);
      return;
    }
    if (cmd.handled && cmd.pending === "cancel") {
      // Special-case: cancelling our bulk-approve card doesn't touch a modal.
      if (pendingIntentRef.current?.kind === "bulk-approve-contact") {
        pendingIntentRef.current = null;
        setMessages(m => [
          ...m,
          { role: "user", content: userMsg },
          { role: "assistant", content: "OK — just the one approved." },
        ]);
        if (voiceOnRef.current) speakOne("OK, just the one approved.");
        return;
      }
      setPendingIntent(null);
      emitAction("close-current-modal");
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: "Cancelled." },
      ]);
      if (voiceOnRef.current) speakOne("Cancelled");
      // User declined to categorize — end the single-txn inquiry.
      resolveInquiry();
      return;
    }
    if (cmd.handled && cmd.pending) {
      // Confirm/cancel with nothing pending in a modal — but if there's a
      // pinned focused transaction and the utterance is affirmative
      // ("yes", "looks good"), treat it as an approve intent on that row.
      if (cmd.pending === "confirm" && focus?.id) {
        setMessages(m => [...m, { role: "user", content: userMsg }]);
        try {
          const r = await api.post(
            `/companies/${currentId}/transactions/${focus.id}/approve-with-suggestion`
          );
          const { similar, rule_exists } = r.data || {};
          if (!similar || !similar.count) {
            const say = "Approved.";
            setMessages(m => [...m, { role: "assistant", content: say }]);
            if (voiceOnRef.current) speakOne(say);
            emitAction("txns:changed");
            if (inquiryTxnRef.current === focus?.id) resolveInquiry();
            return;
          }
          const catName = similar.category_account_name || similar.category_account_code || "the same category";
          const prompt = `Approved. There ${similar.count === 1 ? "is" : "are"} ${similar.count} other unapproved transaction${similar.count === 1 ? "" : "s"} from **${similar.contact_name}**. Would you like me to categorize them all as **${catName}** and approve them${rule_exists ? "" : ", and create a rule for this contact"}?`;
          const tickedIds = (similar.items || []).map(x => x.id);
          pendingIntentRef.current = {
            kind: "bulk-approve-contact",
            similar,
            create_rule: false,
            rule_exists: !!rule_exists,
            ticked: tickedIds,
          };
          setMessages(m => [...m, {
            role: "assistant",
            content: prompt,
            card: { kind: "bulk-approve-confirm", similar, create_rule: false, rule_exists: !!rule_exists, ticked: tickedIds },
          }]);
          emitAction("open-similar-approve-modal", { similar, rule_exists: !!rule_exists });
          if (voiceOnRef.current) speakOne(prompt.replace(/\*\*/g, ""));
          emitAction("txns:changed");
        } catch (e) {
          const detail = e?.response?.data?.detail;
          const say = detail ? `${detail} Tell me what it was and I'll categorize it.` : "Sorry — I couldn't approve that transaction.";
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
        }
        return;
      }
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: cmd.pending === "confirm" ? "Nothing pending to confirm." : "Nothing to cancel." },
      ]);
      return;
    }

    // --- Remote intent: report narration ---
    if (cmd.handled && cmd.remote === "read-report") {
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: "Fetching numbers…" },
      ]);
      try {
        const params = new URLSearchParams(cmd.filters || {});
        const r = await api.get(`/companies/${currentId}/reports/${cmd.reportKind}?${params.toString()}`);
        const summary = summarizeReport(cmd.reportKind, cmd.reportName, r.data, cmd.filters);
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: summary };
          return copy;
        });
        if (voiceOnRef.current) speakOne(summary);
      } catch (e) {
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: "Couldn't pull that report." };
          return copy;
        });
      }
      return;
    }

    // --- Remote intent: comparative report narration ---
    //   "read my P&L vs last quarter" → current summary + top 2 movers.
    if (cmd.handled && cmd.remote === "read-report-compare") {
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: "Comparing periods…" },
      ]);
      try {
        const nowParams   = new URLSearchParams(cmd.filters || {});
        const priorParams = new URLSearchParams(cmd.priorFilters || {});
        const [now, prior] = await Promise.all([
          api.get(`/companies/${currentId}/reports/${cmd.reportKind}?${nowParams.toString()}`),
          api.get(`/companies/${currentId}/reports/${cmd.reportKind}?${priorParams.toString()}`),
        ]);
        const summary = summarizeReport(cmd.reportKind, cmd.reportName, now.data, cmd.filters);
        const delta   = summarizeComparison(cmd.reportKind, now.data, prior.data, cmd.priorLabel);
        const full    = `${summary} ${delta}`.trim();
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: full };
          return copy;
        });
        if (voiceOnRef.current) speakOne(full);
      } catch (e) {
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: "Couldn't pull that comparison." };
          return copy;
        });
      }
      return;
    }

    // --- Remote intent (backend parser for creates) ---
    // --- Remote intent (backend parser for creates) ---
    if (cmd.handled && cmd.remote === "voice-recap") {
      // Multi-section meeting recap: overlays on current page.
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: "Got it — extracting your recap." },
      ]);
      import("@/components/VoiceRecapReview").then(mod => {
        mod.emitVoiceRecap({ text: userMsg });
      });
      return;
    }

    if (cmd.handled && cmd.remote === "voice-action") {
      // Global CRM voice actions: overlays on current page, no navigation.
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: "On it — pop-up incoming." },
      ]);
      import("@/components/VoiceActionReview").then(mod => {
        mod.emitVoiceAction({ text: userMsg });
      });
      return;
    }

    if (cmd.handled && cmd.remote === "intent") {
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: "Parsing…" },
      ]);
      try {
        const r = await api.post(`/companies/${currentId}/ai/parse-intent`, { text: userMsg });
        const parsed = r.data || {};
        const handled = handleParsedIntent(userMsg, parsed);
        if (!handled) {
          setMessages(m => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: "I couldn't parse that as a create action. Try 'create an invoice for John Doe for 500 dollars'." };
            return copy;
          });
        }
      } catch (e) {
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: "Sorry — parsing failed." };
          return copy;
        });
      }
      return;
    }

    // --- Remote intent: open account detail (Balance Sheet drilldown by voice) ---
    if (cmd.handled && cmd.remote === "open-account") {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      try {
        const r = await api.get(`/companies/${currentId}/accounts`);
        const accts = (r.data.accounts || []);
        const needle = (cmd.target || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "").trim();
        // Try (1) exact code match, (2) code prefix, (3) name/fuzzy match.
        let hit = accts.find(a => (a.code || "").toLowerCase() === needle);
        if (!hit) hit = accts.find(a => (a.code || "").toLowerCase().startsWith(needle));
        if (!hit) {
          const words = needle.split(/\s+/).filter(Boolean);
          let best = null, bestScore = 0;
          for (const a of accts) {
            const n = (a.name || "").toLowerCase();
            if (!n) continue;
            let score = 0;
            if (n === needle) score = 1000;
            else if (n.includes(needle) || needle.includes(n)) score = 500;
            else score = words.reduce((s, w) => s + (n.includes(w) ? 1 : 0), 0);
            if (score > bestScore) { bestScore = score; best = a; }
          }
          if (best && bestScore >= (words.length || 1)) hit = best;
        }
        if (hit) {
          const say = `Opening ${hit.code} ${hit.name}`;
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          const params = [`account=${hit.id}`];
          if (cmd.start) params.push(`start=${encodeURIComponent(cmd.start)}`);
          if (cmd.end)   params.push(`end=${encodeURIComponent(cmd.end)}`);
          navigate(`/reports/account-detail?${params.join("&")}`);
        } else {
          const say = `I couldn't find an account matching "${cmd.target}".`;
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
        }
      } catch (e) {
        setMessages(m => [...m, { role: "assistant", content: "Couldn't look up that account." }]);
      }
      return;
    }

    // --- Remote intent: approve the focused transaction, then offer bulk-approve
    //     for every OTHER unapproved txn with the same contact + rule creation. ---
    // --- Remote intent: approve the focused transaction, then offer bulk-approve
    //     for every OTHER unapproved txn with the same contact + rule creation. ---
    if (cmd.handled && cmd.remote === "approve-bucket") {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      // Hand off to the mega-approve modal which owns the bucket-level
      // approve function (including the "auto-create rule" toggle state).
      emitAction("mega-approve-bucket", { key: cmd.bucket.key });
      const say = `Approving ${cmd.bucket.count} ${cmd.bucket.contact_name} row${cmd.bucket.count === 1 ? "" : "s"}.`;
      setMessages(m => [...m, { role: "assistant", content: say }]);
      if (voiceOnRef.current) speakOne(say);
      // Clear focus once approved — the row is about to disappear.
      setFocus(null, { force: true });
      return;
    }

    if (cmd.handled && cmd.remote === "approve-focused") {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      try {
        const r = await api.post(
          `/companies/${currentId}/transactions/${cmd.txnId}/approve-with-suggestion`
        );
        const { approved, similar, rule_exists } = r.data || {};
        // Case 1: no meaningful bulk candidates — just confirm the single approval.
        if (!similar || !similar.count) {
          const say = "Approved.";
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          emitAction("txns:changed");
          return;
        }
        // Case 2: offer the follow-up. Rendered as an interactive card in the
        // assistant message stream — the user answers yes/no via buttons OR
        // by saying "yes" / "no" (see the `pending: bulk-approve-contact` branch).
        const catName = similar.category_account_name || similar.category_account_code || "the same category";
        const prompt = `Approved. There ${similar.count === 1 ? "is" : "are"} ${similar.count} other unapproved transaction${similar.count === 1 ? "" : "s"} from **${similar.contact_name}**. Would you like me to categorize them all as **${catName}** and approve them${rule_exists ? "" : ", and create a rule for this contact"}?`;
        pendingIntentRef.current = {
          kind: "bulk-approve-contact",
          similar,
          create_rule: !rule_exists,
        };
        setMessages(m => [...m, {
          role: "assistant",
          content: prompt,
          card: {
            kind: "bulk-approve-confirm",
            similar,
            create_rule: !rule_exists,
          },
        }]);
        if (voiceOnRef.current) speakOne(prompt.replace(/\*\*/g, ""));
        emitAction("txns:changed");
      } catch (e) {
        setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't approve that transaction." }]);
      }
      return;
    }

    if (cmd.handled && cmd.remote === "unapprove-focused") {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      try {
        await api.post(`/companies/${currentId}/transactions/${cmd.txnId}/unapprove`);
        const say = "Unapproved.";
        setMessages(m => [...m, { role: "assistant", content: say }]);
        if (voiceOnRef.current) speakOne(say);
        emitAction("txns:changed");
      } catch (e) {
        setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't unapprove that." }]);
      }
      return;
    }

    // --- Remote intent: recategorize the focused transaction to a named account. ---
    if (cmd.handled && cmd.remote === "recategorize-focused") {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      // Meaning-first: resolve the user's words (not a literal account
      // name) against the chart, else propose a complete GAAP account.
      if (await recommendCategory(userMsg, cmd.txnId)) return;
      try {
        // Fallback (resolver unavailable): literal name/code match.
        const r = await api.get(`/companies/${currentId}/accounts`);
        const accts = r.data.accounts || [];
        const needle = cmd.targetName.toLowerCase();
        // Match priority: exact name → exact code → name-starts-with → contains.
        let hit = accts.find(a => (a.name || "").toLowerCase() === needle);
        if (!hit) hit = accts.find(a => (a.code || "") === cmd.targetName);
        if (!hit) hit = accts.find(a => (a.name || "").toLowerCase().startsWith(needle));
        if (!hit) hit = accts.find(a => (a.name || "").toLowerCase().includes(needle));
        if (!hit) {
          // Feb 2026 — infer the GAAP type from the account name instead
          // of the old hardcoded "expense". Rental Income → revenue,
          // Office Supplies → expense, etc. If genuinely ambiguous
          // (customer refund, reimbursement), ask the user instead of
          // guessing.
          const inferred = inferAccountTypeFromName(cmd.targetName);
          if (inferred.ambiguous) {
            const ask = `"${cmd.targetName}" could go a few ways — should I create it as ${inferred.options.join(" or ")}?`;
            setMessages(m => [...m, {
              role: "assistant",
              content: ask,
            }]);
            pendingIntentRef.current = {
              kind: "resolve-ambiguous-type",
              accountName: cmd.targetName,
              options: inferred.options,
              txnId: cmd.txnId,
            };
            if (voiceOnRef.current) speakOne(ask);
            return;
          }
          const typeLabel = ({
            revenue: "an income (revenue)",
            expense: "an expense",
            asset: "an asset",
            liability: "a liability",
            equity: "an equity",
            cogs: "a cost of goods sold",
          })[inferred.type] || "an expense";
          setMessages(m => [...m, {
            role: "assistant",
            content: `I couldn't find an account called "${cmd.targetName}". Want me to create it as ${typeLabel} account and use it here?`,
            card: {
              kind: "create-account-then-recategorize",
              accountName: cmd.targetName,
              accountType: inferred.type,
              txnId: cmd.txnId,
            },
          }]);
          pendingIntentRef.current = { kind: "create-then-recat", accountName: cmd.targetName, accountType: inferred.type, txnId: cmd.txnId };
          return;
        }
        // PATCH the transaction.
        await api.patch(`/companies/${currentId}/transactions/${cmd.txnId}`, {
          category_account_id: hit.id,
        });
        const say = `Recategorized to ${hit.code} ${hit.name}.`;
        setMessages(m => [...m, { role: "assistant", content: say }]);
        if (voiceOnRef.current) speakOne(say);
        emitAction("txns:changed");
        if (inquiryTxnRef.current === cmd.txnId) resolveInquiry();
      } catch (e) {
        setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't recategorize that." }]);
      }
      return;
    }

    // --- Remote intent: create a new Chart-of-Accounts entry. ---
    if (cmd.handled && cmd.remote === "create-account") {
      setMessages(m => [...m, {
        role: "user", content: userMsg,
      }, {
        role: "assistant",
        content: `Create a new **${cmd.accountType}** account named **${cmd.accountName}**?`,
        card: {
          kind: "create-account-confirm",
          accountName: cmd.accountName,
          accountType: cmd.accountType,
        },
      }]);
      pendingIntentRef.current = { kind: "create-account", accountName: cmd.accountName, accountType: cmd.accountType };
      return;
    }

    // --- Remote intent: mark focused txn as an internal transfer. ---
    if (cmd.handled && cmd.remote === "mark-transfer") {
      setMessages(m => [...m, { role: "user", content: userMsg }]);
      try {
        const r = await api.post(`/companies/${currentId}/transactions/${cmd.txnId}/mark-as-transfer`, {});
        const { transfer_account, candidates } = r.data || {};
        const acctStr = transfer_account ? `${transfer_account.code} ${transfer_account.name}` : "Transfer";
        if (!candidates || candidates.length === 0) {
          const say = `Done — recategorized to ${acctStr}. No matching leg found on another bank account within ±3 days.`;
          setMessages(m => [...m, { role: "assistant", content: say }]);
          if (voiceOnRef.current) speakOne(say);
          emitAction("txns:changed");
          if (inquiryTxnRef.current === cmd.txnId) resolveInquiry();
          return;
        }
        const first = candidates[0];
        const bank = first.bank_account_name || "another account";
        const prompt = `Recategorized to **${acctStr}**. I also see a matching **${bank}** entry on **${first.date}** for the opposite amount — want me to mark that as the other leg of the transfer too?`;
        setMessages(m => [...m, {
          role: "assistant",
          content: prompt,
          card: {
            kind: "transfer-match-confirm",
            txnId: cmd.txnId,
            candidates,
          },
        }]);
        pendingIntentRef.current = { kind: "transfer-match", txnId: cmd.txnId, candidates };
        if (voiceOnRef.current) speakOne(prompt.replace(/\*\*/g, ""));
        emitAction("txns:changed");
      } catch (e) {
        setMessages(m => [...m, { role: "assistant", content: "Sorry — I couldn't mark that as a transfer." }]);
      }
      return;
    }

    if (cmd.handled) {
      setMessages(m => [
        ...m,
        { role: "user", content: userMsg },
        { role: "assistant", content: cmd.say || "Done." },
      ]);
      if (voiceOnRef.current && cmd.say) speakOne(cmd.say);
      return;
    }

    setMessages(m => [...m, { role: "user", content: userMsg }, { role: "assistant", content: "" }]);
    // Fresh reply → reset TTS pointer and stop any prior speech so we don't
    // read overlapping messages.
    spokenIdxRef.current = 0;
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    ttsSpeakingRef.current = false;
    ttsTailUntilRef.current = 0;
    // Fresh stream → clear DRAFT dedup so new markers can fire.
    dispatchedDraftsRef.current = new Set();

    // Review Chat Co-Pilot intercept — when the CPA is on the Review
    // Chat page and hits Send, first classify the intent. If the LLM
    // matches an authored playbook, hand off to the tour engine (via
    // a window event) and skip the streaming general-chat call.
    const onReviewChat = typeof window !== "undefined" &&
      window.location.pathname.startsWith("/accounting/review-chat");
    if (onReviewChat) {
      try {
        const promptEl = document.querySelector('[data-testid="chat-review-prompt"]');
        const cardSnap = {
          prompt: promptEl?.innerText || null,
          contact_name: document.querySelector('[data-testid="chat-review-card-contact"]')?.innerText || null,
          sample_count: document.querySelectorAll('[data-testid^="chat-review-sample-"]').length || null,
        };
        const token = localStorage.getItem("axiom_token");
        const cResp = await fetch(`${BACKEND_URL}/api/companies/${currentId}/review-copilot/classify`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ message: userMsg, card: cardSnap }),
        });
        if (cResp.ok) {
          const parsed = await cResp.json();
          const reply = parsed.reply || "";
          setMessages(m => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: reply };
            return copy;
          });
          if (voiceOnRef.current && reply) speakOne(reply);
          if (parsed.intent && parsed.intent !== "explain-why") {
            // Fire a playbook — ChatReview.jsx picks this up and mounts
            // ChatReviewTour with the playbook's beats.
            window.dispatchEvent(new CustomEvent("chat-review-run-playbook", {
              detail: { playbook_key: parsed.intent, slots: parsed.slots || {} },
            }));
          }
          return; // skip the streaming general-chat call
        }
      } catch (_) {
        // Fall through to the generic streamed chat on any co-pilot error.
      }
    }

    setStreaming(true);
    try {
      const token = localStorage.getItem("axiom_token");
      const resp = await fetch(`${BACKEND_URL}/api/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          company_id: currentId,
          message: userMsg,
          focused_transaction_id: focus?.id || null,
          focused_bucket: focus?.bucket ? {
            contact_name: focus.contact_name,
            count: focus.count,
            amount: focus.amount,
            account_code: focus.account_code,
            account_name: focus.account_name,
          } : null,
          focused_new_asset: focus?.kind === "new-fixed-asset" ? {
            editing: focus.editing,
            asset_id: focus.asset_id,
            draft: focus.draft,
          } : null,
          terseness,
        }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          try {
            const j = JSON.parse(p.slice(6));
            if (j.delta) {
              setMessages(m => {
                const copy = [...m];
                const prev = copy[copy.length - 1].content;
                const nextRaw = prev + j.delta;
                // Strip the hidden [[PROPOSAL:...]] marker from the visible
                // text and, if a fully-formed marker just landed, stash it
                // as a pendingIntent so a follow-up "yes / do it" executes.
                const propRe = /\[\[PROPOSAL:([^\]]+)\]\]/;
                // [[DRAFT:{...}]] markers — the AI emits these as it learns
                // fields for a new fixed asset. Dispatched to the open
                // "New Fixed Asset" modal so the form fills in real time
                // as the user talks. Multiple DRAFT markers can appear
                // in a single message — we handle each and remove them
                // from the visible text.
                const draftRe = /\[\[DRAFT:(\{[^\]]+\})\]\]/g;
                let draftMatch;
                while ((draftMatch = draftRe.exec(nextRaw)) !== null) {
                  const key = draftMatch[0];
                  if (dispatchedDraftsRef.current.has(key)) continue;
                  dispatchedDraftsRef.current.add(key);
                  try {
                    const partial = JSON.parse(draftMatch[1]);
                    window.dispatchEvent(new CustomEvent(
                      "ai:fixed-asset-draft", { detail: partial },
                    ));
                  } catch { /* incomplete JSON while streaming — retry on next chunk */ }
                }
                const pm = nextRaw.match(propRe);
                if (pm) {
                  // Feb 2026 — always let the LATEST proposal win. Previously
                  // this was guarded by `!pendingIntentRef.current` which
                  // caused a stale intent from an earlier turn to be
                  // applied when the user course-corrected. e.g. AI: "book
                  // to Owner's Draw?" → user: "no, it's actually a tax
                  // payment" → AI: "book to Income Tax Expense?" → user:
                  // "yes" — pre-fix would still apply Owner's Draw.
                  const raw = pm[1].trim();
                  // Two supported formats:
                  //   (legacy)  action=categorize|category=X|scope=Y
                  //   (JSON)    {"kind":"create-fixed-asset","payload":{...}}
                  // Detect JSON by the leading `{` and route accordingly so
                  // structured proposals (fixed-asset creation, mortgage
                  // account setup) can carry nested objects like the
                  // `offsets` list without pipe-escaping.
                  if (raw.startsWith("{")) {
                    try {
                      const j = JSON.parse(raw);
                      if (j.kind === "create-fixed-asset" && j.payload) {
                        pendingIntentRef.current = {
                          kind: "create-fixed-asset",
                          payload: j.payload,
                        };
                      } else if (j.kind === "fund-fixed-asset" && j.payload) {
                        pendingIntentRef.current = {
                          kind: "fund-fixed-asset",
                          payload: j.payload,
                        };
                      } else if (j.kind === "create-liability-account") {
                        pendingIntentRef.current = {
                          kind: "create-liability-account",
                          name: j.name, code: j.code,
                          subtype: j.subtype || "long_term_debt",
                          parent_account_id: j.parent_account_id || null,
                          lender: j.lender || null,
                          principal: j.principal != null ? Number(j.principal) : null,
                          rate: j.rate != null ? Number(j.rate) : null,
                          term_months: j.term_months != null ? Number(j.term_months) : null,
                        };
                      }
                    } catch {}
                  } else {
                    const parts = raw.split("|");
                    const kv = {};
                    for (const seg of parts) {
                      const idx = seg.indexOf("=");
                      if (idx > 0) kv[seg.slice(0, idx).trim()] = seg.slice(idx + 1).trim();
                    }
                    if (kv.action === "categorize" && kv.category) {
                      pendingIntentRef.current = {
                        kind: "categorize-proposal",
                        category: kv.category,
                        scope: kv.scope || "focused",
                      };
                    } else if (kv.action === "transfer") {
                      pendingIntentRef.current = {
                        kind: "transfer-proposal",
                        scope: kv.scope || "focused",
                      };
                    } else if (kv.action === "create-then-categorize" && kv.name && kv.type) {
                      // Feb 2026 — user named a category that doesn't
                      // exist in the CoA. AI proposed creating it AND
                      // categorizing the focused txn in one step.
                      pendingIntentRef.current = {
                        kind: "create-then-categorize-focused",
                        accountName: kv.name,
                        accountType: kv.type,
                        scope: kv.scope || "focused",
                      };
                    }
                  }
                }
                const next = nextRaw
                  .replace(propRe, "")
                  .replace(/\[\[DRAFT:\{[^\]]+\}\]\]/g, "")
                  // Defensively convert LITERAL two-char "\n" (backslash + n)
                  // to a real newline. Feb 2026 — earlier system prompt
                  // examples were double-escaped so the LLM learned to
                  // emit `\n` as a literal string. Prompt is fixed but
                  // in-flight sessions may still emit the old form.
                  .replace(/\\n/g, "\n")
                  .replace(/\n\s*\n\s*$/, "")
                  .trimEnd();
                copy[copy.length - 1] = { role: "assistant", content: next };
                // Feed newly-completed sentences to speechSynthesis
                // immediately — this is what makes the voice "real-time":
                // as soon as Claude finishes a sentence, we speak it while
                // the next sentence is still being generated.
                if (voiceOnRef.current) speakNewSentences(next);
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (e) {
      setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "[Error contacting AI]" };
        return copy;
      });
    } finally {
      // Flush any trailing text that didn't end in a sentence terminator.
      if (voiceOnRef.current) {
        setMessages(m => {
          const last = m[m.length - 1];
          if (last && last.role === "assistant") speakRemainder(last.content);
          return m;
        });
      }
      setStreaming(false);
    }
  };

  // Split on sentence terminators (., !, ?, newline, colon) — speak completed
  // sentences and keep the trailing partial buffered until it terminates. This
  // yields the shortest possible time-to-first-word.
  const SENTENCE_END = /([.!?\n:])\s+/;
  const speakOne = (text) => {
    const clean = stripMarkdownForSpeech(text);
    if (!clean) return;
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(clean);
    const wanted = voiceNameRef.current;
    if (wanted) {
      const v = (window.speechSynthesis.getVoices() || []).find(x => x.name === wanted);
      if (v) { u.voice = v; u.lang = v.lang; }
    }
    u.rate = Math.max(0.5, Math.min(2.0, voiceRateRef.current || 1.05));
    u.pitch = 1.0;
    u.volume = 1.0;
    u.onstart = () => { ttsSpeakingRef.current = true; setTtsActive(true); };
    const finish = () => {
      // Only clear the flag when the browser queue is genuinely empty —
      // otherwise the NEXT chunked utterance (which fires start slightly
      // after this one's end) would race with a stale-false flag.
      const ss = window.speechSynthesis;
      const idle = !ss.speaking && !ss.pending;
      if (idle) {
        ttsSpeakingRef.current = false;
        setTtsActive(false);
        ttsTailUntilRef.current = Date.now() + TAIL_MS;
      }
    };
    u.onend = finish;
    u.onerror = finish;
    window.speechSynthesis.speak(u);
  };
  const speakNewSentences = (full) => {
    let start = spokenIdxRef.current;
    if (start >= full.length) return;
    const pending = full.slice(start);
    // Find the last sentence terminator in the pending text — everything up
    // to (and including) that terminator is safe to speak. The remainder is
    // held over for the next delta.
    let match;
    let lastEnd = -1;
    const re = new RegExp(SENTENCE_END.source, "g");
    while ((match = re.exec(pending)) !== null) lastEnd = match.index + match[0].length;
    if (lastEnd <= 0) return;
    const chunk = pending.slice(0, lastEnd);
    speakOne(chunk);
    spokenIdxRef.current = start + lastEnd;
  };
  const speakRemainder = (full) => {
    const rest = (full || "").slice(spokenIdxRef.current);
    if (rest.trim()) {
      speakOne(rest);
      spokenIdxRef.current = full.length;
    }
  };

  if (collapsed) {
    if (isMobile) return null;   // the bottom-nav "Chat" tab re-opens it
    return (
      <div className="w-12 shrink-0 border-l bg-white flex flex-col items-center py-4">
        <button
          data-testid={TID.aiPanelToggle}
          onClick={onToggle}
          className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
          title="Open Assistant"
        >
          <MessageSquare size={18} />
        </button>
      </div>
    );
  }

  // Mobile: a fixed bottom sheet above the bottom nav, full width, so
  // the page keeps scrolling behind it. Grows when the user is typing
  // so the keyboard doesn't swallow the conversation.
  const peek = isMobile && sheet === "peek";
  const navH = 0;   // the sheet replaces the bottom nav while open
  const mobileSheetHeight = sheet === "full"
    ? `calc(100dvh - ${navH}px - ${kbInset}px - env(safe-area-inset-bottom))`
    : sheet === "half" ? (kbInset > 0 ? `calc(100dvh - ${kbInset}px - 120px)` : "48vh") : "auto";
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
  return (
    <aside
      className={isMobile
        ? "fixed left-0 right-0 z-[45] bg-white flex flex-col rounded-t-2xl shadow-[0_-8px_30px_rgba(15,23,42,0.18)] border-t border-slate-200"
        : "shrink-0 border-l bg-white flex flex-col relative z-[60]"}
      style={isMobile
        ? { bottom: `calc(${navH}px + ${kbInset}px + env(safe-area-inset-bottom))`, height: mobileSheetHeight, maxHeight: "100dvh", transition: "height 180ms ease, bottom 120ms ease" }
        : { width: `${panelWidth}px` }}
      data-testid="ai-panel"
      data-mobile-sheet={isMobile ? sheet : "0"}
    >
      {peek && (
        <div
          className="shrink-0 flex items-center gap-2 px-3 pt-1.5 pb-1 select-none"
          onTouchStart={onHandleTouchStart}
          onTouchEnd={onHandleTouchEnd}
          onClick={() => setSheet("half")}
          data-testid="ai-sheet-handle"
        >
          <button type="button" onClick={(e) => { e.stopPropagation(); sheetDown(); }} className="p-1 text-slate-400" aria-label="Close assistant" data-testid="ai-sheet-down">
            <X size={15} />
          </button>
          <div className="flex-1 flex justify-center"><span className="block h-1 w-10 rounded-full bg-slate-300" /></div>
          <button type="button" onClick={(e) => { e.stopPropagation(); sheetUp(); }} className="p-1 text-slate-400" aria-label="Expand assistant" data-testid="ai-sheet-up">
            <ChevronDown size={16} className="rotate-180" />
          </button>
        </div>
      )}
      {peek && (
        <button type="button" onClick={() => setSheet("half")} className="px-4 pb-1 text-left text-xs text-slate-600 truncate" data-testid="ai-sheet-peek-line">
          {streaming ? "Thinking…" : lastAssistant ? stripMarkdownForSpeech(lastAssistant.content || "").slice(0, 140) : "Ask me anything about your books…"}
        </button>
      )}
      {/* Drag handle — 6px wide invisible strip along the left edge. */}
      {!isMobile && <div
        onMouseDown={startResize}
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize"
        data-testid="ai-panel-resize"
        className="absolute left-0 top-0 h-full w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-indigo-300/40 z-[65]"
      />}
      <div
        className={`shrink-0 border-b flex items-center gap-2 ${peek ? "hidden" : isMobile ? "h-11 px-2 select-none" : "h-16 px-4"}`}
        onTouchStart={isMobile ? onHandleTouchStart : undefined}
        onTouchEnd={isMobile ? onHandleTouchEnd : undefined}
        data-testid={isMobile ? "ai-sheet-handle" : undefined}
      >
        {isMobile && (
          <>
            <button type="button" onClick={sheetDown} className="p-1 text-slate-400" aria-label="Shrink assistant" data-testid="ai-sheet-down"><ChevronDown size={16} /></button>
            <div className="flex-1 flex justify-center"><span className="block h-1 w-10 rounded-full bg-slate-300" /></div>
            <button type="button" onClick={sheetUp} disabled={sheet === "full"} className="p-1 text-slate-400 disabled:opacity-30" aria-label="Expand assistant" data-testid="ai-sheet-up"><ChevronDown size={16} className="rotate-180" /></button>
          </>
        )}
        {/* Review | Chat toggle — only visible on the review-chat
            route. Left-aligned so it doesn't fight with the mute /
            clear / collapse cluster on the right. Segmented control
            styling matches other in-app toggles (e.g. Monthly/Annual
            on pricing). */}
        {isReviewChatRoute && (
          <div
            role="tablist"
            aria-label="Review or Chat"
            className="flex items-center rounded-full bg-slate-100 p-0.5 text-xs font-semibold"
            data-testid="ai-panel-mode-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={reviewMode === "review"}
              onClick={() => setReviewMode("review")}
              className={`px-3 py-1 rounded-full transition-colors ${
                reviewMode === "review"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="ai-panel-mode-review"
            >
              Review
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={reviewMode === "chat"}
              onClick={() => setReviewMode("chat")}
              className={`px-3 py-1 rounded-full transition-colors ${
                reviewMode === "chat"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="ai-panel-mode-chat"
            >
              Chat
            </button>
          </div>
        )}
        <button
          onClick={() => setVoiceOn(v => !v)}
          data-testid="ai-tts-toggle"
          className={`${isMobile ? "" : "ml-auto"} p-1.5 rounded hover:bg-slate-100 ${
            voiceOn ? "text-emerald-600" : "text-slate-400"
          }`}
          title={voiceOn ? "Voice on — click to mute" : "Voice off — click to enable"}
        >
          {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </button>
        <button
          onClick={clearChatMessages}
          data-testid="ai-chat-clear"
          className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
          title="Clear conversation"
        >
          <Trash2 size={15} />
        </button>
        <div className="relative">
          <button
            onClick={() => setVoiceMenuOpen(v => !v)}
            data-testid="ai-tts-voice-menu"
            className="p-1 rounded hover:bg-slate-100 text-slate-500"
            title="Voice settings"
          >
            <ChevronDown size={14} />
          </button>
          {voiceMenuOpen && (
            <VoicePicker
              voices={voices}
              voiceOn={voiceOn}
              setVoiceOn={setVoiceOn}
              voiceName={voiceName}
              setVoiceName={setVoiceName}
              voiceRate={voiceRate}
              setVoiceRate={setVoiceRate}
              terseness={terseness}
              setTerseness={setTerseness}
              speakOne={speakOne}
              onClose={() => setVoiceMenuOpen(false)}
            />
          )}
        </div>
        <button
          data-testid={TID.aiPanelToggle}
          onClick={onToggle}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
          title="Collapse"
        >
          <X size={16} />
        </button>
      </div>

      {focus && !peek && (
        <div className={`border rounded-md bg-indigo-50/50 border-indigo-200 text-xs ${isMobile ? "mx-2 mt-2 p-3 border-l-4 border-l-indigo-500 shadow-sm" : "mx-3 mt-3 p-2.5"}`} data-testid="ai-focus-card">
          {focus.kind === "new-fixed-asset" ? (
            <>
              <div className="font-medium text-slate-700 mb-0.5 flex items-center gap-1">
                <Sparkles size={11} className="text-indigo-600" />
                {focus.editing ? "Editing fixed asset" : "New fixed asset"}
              </div>
              <div className="text-slate-600">
                Describe what you bought and how it was paid. I can suggest the
                right IRS class, set up a mortgage/loan account if needed, and
                split the funding sources for you.
              </div>
              {focus.draft?.name && (
                <div className="mt-1 text-[11px] text-slate-500 truncate">
                  Draft: <b>{focus.draft.name}</b>
                  {focus.draft.cost && <> · ${Number(focus.draft.cost).toLocaleString()}</>}
                  {focus.draft.asset_type && <> · {focus.draft.asset_type}</>}
                </div>
              )}
            </>
          ) : focus.bucket ? (
            <>
              <div className="font-medium text-slate-700 mb-0.5">Focused bucket</div>
              <div className="text-slate-600 truncate">
                <b>{focus.contact_name}</b> · {focus.count} row{focus.count === 1 ? "" : "s"} · <span className="font-mono-num">${Math.abs(focus.amount).toLocaleString("en-US", {maximumFractionDigits: 0})}</span>
                {focus.account_code && (
                  <> · <span className="text-slate-500">{focus.account_code} · {focus.account_name}</span></>
                )}
              </div>
            </>
          ) : isMobile ? (
            <div className="flex items-center gap-3" data-testid="ai-focus-txn-card">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold">Focused</div>
                <div className="text-sm font-semibold text-slate-900 truncate">{focus.merchant || "Transaction"}</div>
                <div className="text-[11px] font-mono text-slate-500">{focus.date}</div>
              </div>
              <div className={`text-base font-semibold font-mono-num ${focus.amount > 0 ? "text-emerald-700" : "text-slate-900"}`}>
                {focus.amount > 0 ? "" : "-"}${Math.abs(focus.amount).toFixed(2)}
              </div>
              {focusPinned && (
                <button type="button" onClick={() => setFocus(null, { force: true })} className="p-1.5 rounded-md text-slate-400 hover:bg-indigo-100 hover:text-slate-700" aria-label="Cancel focus" data-testid="ai-cancel-focus">
                  <X size={14} />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="font-medium text-slate-700 mb-0.5">Focused transaction</div>
              <div className="text-slate-600 truncate">
                {focus.merchant} · <span className="font-mono-num">${Math.abs(focus.amount).toFixed(2)}</span> · {focus.date}
              </div>
            </>
          )}
        </div>
      )}

      {isReviewChatRoute && reviewMode === "review" && (
        <ReviewStartersPane />
      )}
      <div
        ref={scrollRef}
        className={`flex-1 overflow-y-auto p-3 space-y-3 ${
          (isReviewChatRoute && reviewMode === "review") || peek ? "hidden" : ""
        }`}
      >
        {(() => {
          // Which message should wear the rainbow outline? The most-recent
          // assistant message, IF nothing from the user has come in since it.
          // As soon as the user sends anything, the outline vanishes until
          // the AI speaks again.
          let latestUnansweredAssistant = -1;
          for (let j = messages.length - 1; j >= 0; j--) {
            if (messages[j].role === "user") break;
            if (messages[j].role === "assistant") { latestUnansweredAssistant = j; break; }
          }
          return messages.map((m, i) => (
          <div key={i} data-testid={TID.aiChatMessage}
               className={`max-w-[92%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                 m.role === "user"
                   ? "chat-bubble-user ml-auto whitespace-pre-wrap"
                   : "chat-bubble-ai chat-md"
               } ${i === latestUnansweredAssistant ? "ai-shimmer-bubble" : ""}`}>
            {m.role === "assistant" && m.content ? (
              // Render assistant messages as markdown so `**bold**`,
              // `_italic_`, lists, tables, and inline code all format
              // correctly. remark-gfm adds tables + strikethrough +
              // task lists. User messages stay plain-text (preserves
              // whatever the user typed, incl. accidental asterisks).
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
            ) : (
              m.content || (streaming && i === messages.length - 1 ? "…" : "")
            )}
            {m.splitHint && (
              <SplitHintForm
                hint={m.splitHint}
                onApply={(belowCat, aboveCat) => {
                  const t = m.splitHint.threshold;
                  // NOTE: template uses "above is X" (no second threshold)
                  // so the range regex's optional "is/=" doesn't get fooled
                  // by "$100 is" appearing twice in the input.
                  const templated = `under $${t} is ${belowCat}, above is ${aboveCat}`;
                  setInput(templated);
                  setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, splitHint: null } : mm2));
                  // Use sendRef so we invoke the LATEST send() closure —
                  // it will read the input state committed by setInput above.
                  setTimeout(() => sendRef.current?.(), 30);
                }}
                onDismiss={() => {
                  setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, splitHint: null } : mm2));
                }}
              />
            )}
            {m.voiceClarify && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1"
                   data-testid="chat-voice-clarify-hint">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse"/>
                Answer here (type or say it) — the popup will update.
              </div>
            )}
            {m.voiceReview && (
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
                   data-testid="chat-voice-review-hint">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
                Say <b>"looks good"</b> to save, <b>"cancel"</b> to drop, or describe a tweak.
              </div>
            )}
            {m.disambigCreditOrCreate && (
              <div className="mt-2 flex flex-col sm:flex-row gap-2" data-testid="chat-disambig-credit-or-create">
                <button
                  data-testid="disambig-create-invoice"
                  onClick={() => {
                    // Strip the disambig payload off the bubble first
                    // so it can't be double-clicked.
                    setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, disambigCreditOrCreate: null } : mm2));
                    // Route directly through handleParsedIntent using
                    // the prefill the server already parsed on the
                    // original utterance — no re-parse needed. This
                    // preserves customer_name / amount / due_days that
                    // would otherwise get lost if we replayed the
                    // rewritten text through the parser again.
                    const prefill = m.disambigCreditOrCreate.prefill || {};
                    handleParsedIntent(
                      m.disambigCreditOrCreate.originalUtterance || "create an invoice",
                      { intent: "create_invoice", confidence: 0.99, prefill },
                    );
                  }}
                  className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-slate-800"
                >
                  Create an invoice
                </button>
                <button
                  data-testid="disambig-credit-memo"
                  onClick={() => {
                    // Option 1 — polite no-op. Explain credit memos
                    // aren't supported yet and offer the manual
                    // workflow / regular-invoice fallback.
                    setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, disambigCreditOrCreate: null } : mm2)
                      .concat([{
                        role: "assistant",
                        content:
                          "Credit memos aren't supported yet — I can only create regular invoices right now. " +
                          "Want me to create a regular invoice instead? Or you can add a customer credit manually from " +
                          "**Sales & Payments → Payments**.",
                      }]));
                    if (voiceOnRef.current) speakOne("Credit memos aren't supported yet.");
                  }}
                  className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50"
                >
                  Add a credit invoice
                </button>
              </div>
            )}
            {m.showSkip && (
              <div className="mt-1.5" data-testid="cleanup-skip-hint">
                <button
                  data-testid="cleanup-skip-btn"
                  onClick={() => {
                    // Same flow as typing "skip <contact>" — clears the
                    // pending inquiry, notifies the copilot, auto-advances.
                    pendingIntentRef.current = null;
                    const name = m.skipContact?.name || "this contact";
                    const say = `Skipped **${name}** — moving on.`;
                    setMessages(mm => {
                      const cleared = mm.map((mm2, j) => j === i ? { ...mm2, showSkip: false, splitHint: null } : mm2);
                      return [...cleared, { role: "assistant", content: say }];
                    });
                    if (voiceOnRef.current) speakOne(say.replace(/\*\*/g, ""));
                    emitAction("cleanup-completed", {
                      contact_id: m.skipContact?.id,
                      kind: m.skipContact?.kind || "contact_in_uncat",
                      count: 0,
                      skipped: true,
                    });
                  }}
                  className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                >
                  or skip {m.skipContact?.name || "this contact"} →
                </button>
              </div>
            )}
            {m.cta && (
              <div className="mt-2">
                <button
                  data-testid={`chat-cta-${m.cta.actionKey}`}
                  onClick={() => {
                    // Fire a namespaced action so the originating
                    // component (CleanupCopilot for `restart-tour`, etc.)
                    // can react. Also strip the CTA after click so it
                    // can't be double-invoked from the same bubble.
                    emitAction(`chat-cta:${m.cta.actionKey}`, m.cta.data || {});
                    setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, cta: null } : mm2));
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-3 py-1.5 transition-colors"
                >
                  {m.cta.label}
                </button>
              </div>
            )}
            {/* Help-router action pills. Each button navigates via the
                normal React Router `navigate()` so the destination page
                mounts correctly (not just a shallow URL change).
                Feb 28 2026. */}
            {m.actions?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="chat-help-actions">
                {m.actions.map((a, ai) => (
                  <button
                    key={ai}
                    data-testid={`chat-help-action-${ai}`}
                    onClick={() => {
                      if (a.to) navigate(a.to);
                      setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, actions: null } : mm2));
                    }}
                    className={`inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border ${
                      a.variant === "primary"
                        ? "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
                        : a.variant === "secondary"
                        ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
                        : "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
            {m.undo && (
              <div className="mt-2">
                <button
                  type="button"
                  data-testid="chat-undo-approve"
                  onClick={async () => {
                    try {
                      const r = await api.post(`/companies/${currentId}/transactions/bulk-unapprove`, m.undo.txnIds);
                      const n = r.data?.count || 0;
                      setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, undo: null } : mm2)
                        .concat([{ role: "assistant", content: `Undone — **${n}** ${m.undo.contactName} rows are back in review with their categories unchanged.` }]));
                      emitAction("txns:changed");
                    } catch {
                      setMessages(mm => [...mm, { role: "assistant", content: "Sorry — I couldn't undo that approval." }]);
                    }
                  }}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                >
                  Undo approval
                </button>
              </div>
            )}
            {m.card?.kind === "bulk-approve-confirm" && (
              <BulkApproveCard
                similar={m.card.similar}
                createRule={m.card.create_rule}
                ruleExists={m.card.rule_exists}
                ticked={m.card.ticked || null}
                currentId={currentId}
                onDone={(msg) => {
                  pendingIntentRef.current = null;
                  setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, card: null } : mm2).concat([{ role: "assistant", content: msg }]));
                  if (voiceOnRef.current) speakOne(msg);
                  emitAction("bulk-approve-done", { msg, origin: "chat" });
                  emitAction("txns:changed");
                }}
                onDismiss={() => {
                  pendingIntentRef.current = null;
                  setMessages(mm => mm.map((mm2, j) => j === i ? { ...mm2, card: null } : mm2).concat([{ role: "assistant", content: "OK — just the one approved." }]));
                  if (voiceOnRef.current) speakOne("OK, just the one approved.");
                  emitAction("bulk-approve-done", { origin: "chat", declined: true });
                }}
              />
            )}
            {m.card?.kind === "create-account-confirm" && (
              <InlineConfirmCard
                testId="create-account-card"
                tone="indigo"
                onConfirm={async () => {
                  const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
                    name: m.card.accountName, type: m.card.accountType,
                  });
                  pendingIntentRef.current = null;
                  const msg = r.data?.created
                    ? `Created ${r.data.code} ${r.data.name} (${r.data.type}).`
                    : `${r.data.code} ${r.data.name} already exists — using it.`;
                  setMessages(mm => [...mm, { role: "assistant", content: msg }]);
                  if (voiceOnRef.current) speakOne(msg);
                  emitAction("txns:changed");
                }}
                onDismiss={() => {
                  pendingIntentRef.current = null;
                  setMessages(mm => [...mm, { role: "assistant", content: "OK — no new account created." }]);
                }}
              />
            )}
            {m.card?.kind === "category-recommend" && (
              <CategoryRecommendCard
                card={m.card}
                onShowSimilar={(card) => showSimilarForCard(i, card)}
                onClearSimilar={() => emitAction("similar-clear")}
                onSweepSkip={() => { dismissCard(i); sweepAdvance("skip"); }}
                onApply={(args) => applyCategoryCard(i, m.card, args)}
                onDismiss={() => dismissCard(i)}
              />
            )}
            {m.card?.kind === "create-account-then-recategorize" && (
              <InlineConfirmCard
                testId="create-then-recat-card"
                tone="indigo"
                confirmLabel="Yes, create + recategorize"
                onConfirm={async () => {
                  try {
                    const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
                      name: m.card.accountName, type: m.card.accountType,
                    });
                    const acct = r.data;
                    const created = acct.created ? "Created" : "Reusing";
                    // Feb 2026 — support bucket focus. When the focused
                    // entity is a bucket (stepper view) not a single
                    // txn, delegate to CleanupCopilot's apply-categorize
                    // -proposal listener which resolves bucket rows via
                    // bulk-approve-ai-ready. Previously this PATCHed
                    // /transactions/undefined and silently failed.
                    if (focus?.bucket && focus?.key && !m.card.txnId) {
                      emitAction("apply-categorize-proposal", {
                        category: acct.name,
                        accountId: acct.id,
                        scope: "focused",
                        focusedTxnId: null,
                      });
                      const say = `${created} ${acct.code} ${acct.name} — applying to ${focus.count || "the"} row${focus.count === 1 ? "" : "s"} in the ${focus.contact_name || "focused"} bucket.`;
                      pendingIntentRef.current = null;
                      setMessages(mm => [...mm, { role: "assistant", content: say }]);
                      if (voiceOnRef.current) speakOne(say);
                      return;
                    }
                    if (!m.card.txnId) {
                      const say = `${created} ${acct.code} ${acct.name}, but I couldn't tell which transaction to book it to — pin a transaction or bucket first.`;
                      pendingIntentRef.current = null;
                      setMessages(mm => [...mm, { role: "assistant", content: say }]);
                      if (voiceOnRef.current) speakOne(say);
                      return;
                    }
                    await api.patch(`/companies/${currentId}/transactions/${m.card.txnId}`, {
                      category_account_id: acct.id,
                    });
                    pendingIntentRef.current = null;
                    const msg = `${created} ${acct.code} ${acct.name} and recategorized this transaction.`;
                    setMessages(mm => [...mm, { role: "assistant", content: msg }]);
                    if (voiceOnRef.current) speakOne(msg);
                    emitAction("txns:changed");
                  } catch (e) {
                    pendingIntentRef.current = null;
                    setMessages(mm => [...mm, { role: "assistant",
                      content: `Sorry — couldn't create or recategorize: ${e?.response?.data?.detail || e.message}` }]);
                  }
                }}
                onDismiss={() => {
                  pendingIntentRef.current = null;
                  setMessages(mm => [...mm, { role: "assistant", content: "OK — no changes made." }]);
                }}
              />
            )}
            {m.card?.kind === "transfer-match-confirm" && (
              <InlineConfirmCard
                testId="transfer-match-card"
                tone="fuchsia"
                confirmLabel="Yes, mark that leg too"
                onConfirm={async () => {
                  const legId = m.card.candidates[0]?.id;
                  if (!legId) return;
                  await api.post(`/companies/${currentId}/transactions/${m.card.txnId}/mark-as-transfer`, {
                    matching_leg_id: legId,
                  });
                  pendingIntentRef.current = null;
                  const msg = "Done — both legs of the transfer are now categorized correctly and won't hit the P&L.";
                  setMessages(mm => [...mm, { role: "assistant", content: msg }]);
                  if (voiceOnRef.current) speakOne(msg);
                  emitAction("txns:changed");
                }}
                onDismiss={() => {
                  pendingIntentRef.current = null;
                  setMessages(mm => [...mm, { role: "assistant", content: "OK — only this side is marked as a transfer." }]);
                }}
              />
            )}
            {m.card?.kind === "cleanup-multi-confirm" && (
              <InlineConfirmCard
                testId="cleanup-multi-card"
                tone="emerald"
                confirmLabel={`Yes, categorize all ${m.card.count} + create rules`}
                onConfirm={async () => {
                  // 1. Ensure every bucket's category account exists.
                  const groups = [];
                  for (const b of m.card.preview) {
                    let acct = b.existingAccount;
                    if (!acct) {
                      const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
                        name: (b.categoryName || "").replace(/\b(\w)/g, (c) => c.toUpperCase()),
                        type: b.guessType,
                      });
                      acct = r.data;
                    }
                    groups.push({
                      txn_ids: b.txns.map((t) => t.id),
                      category_account_id: acct.id,
                      amount_min: b.amount_min ?? null,
                      amount_max: b.amount_max ?? null,
                      rule_label: b.label,
                    });
                  }
                  // 2. Apply all buckets in one call.
                  const res = await api.post(
                    `/companies/${currentId}/transactions/apply-multi-bulk-approve-rule`,
                    {
                      contact_id: m.card.contactId,
                      contact_name: m.card.contactName,
                      groups,
                      create_rules: true,
                    }
                  );
                  const updated = res.data?.updated || 0;
                  const rules = (res.data?.rule_ids || []).length;
                  const say = `Done — recategorized ${updated} ${m.card.contactName} transactions across ${groups.length} categor${groups.length === 1 ? "y" : "ies"}${rules ? ` and created ${rules} rule${rules === 1 ? "" : "s"}` : ""}.`;
                  setMessages(mm => [...mm, { role: "assistant", content: say }]);
                  if (voiceOnRef.current) speakOne(say);
                  emitAction("txns:changed");
                  // Auto-advance: nudge the Cleanup Copilot to serve up the
                  // next contact from the top-actions queue.
                  emitAction("cleanup-completed", {
                    contact_id: m.card.contactId,
                    kind: "contact_in_uncat",
                    count: updated,
                  });
                }}
                onDismiss={() => {
                  pendingIntentRef.current = {
                    kind: "cleanup-inquiry",
                    action: {
                      kind: "contact_in_uncat",
                      contact_id: m.card.contactId,
                      contact_name: m.card.contactName,
                      count: m.card.count,
                    },
                  };
                  const say = `OK — how would you like to categorize the ${m.card.count} ${m.card.contactName} transactions instead?`;
                  setMessages(mm => [...mm, { role: "assistant", content: say }]);
                  if (voiceOnRef.current) speakOne(say);
                }}
              />
            )}
            {m.card?.kind === "cleanup-bulk-confirm" && (
              <InlineConfirmCard
                testId="cleanup-bulk-card"
                tone="emerald"
                confirmLabel={m.card.existingAccount ? `Yes, categorize all ${m.card.count} + create rule` : `Yes, create + categorize all + rule`}
                onConfirm={async () => {
                  // 1. Ensure the account exists (create if we didn't find a match).
                  let acct = m.card.existingAccount;
                  if (!acct) {
                    const r = await api.post(`/companies/${currentId}/accounts/ensure`, {
                      name: m.card.targetName.replace(/\b(\w)/g, (c) => c.toUpperCase()),
                      type: m.card.guessType,
                    });
                    acct = r.data;
                  }
                  // 2. Grab every unapproved txn for this contact.
                  const list = await api.get(
                    `/companies/${currentId}/transactions?contact_id=${m.card.contactId}&limit=2000`
                  );
                  const ids = (list.data.transactions || [])
                    .filter(t => !t.human_reviewed)
                    .map(t => t.id);
                  // 3. Bulk-approve with rule creation.
                  const res = await api.post(
                    `/companies/${currentId}/transactions/apply-bulk-approve-rule`,
                    {
                      txn_ids: ids,
                      category_account_id: acct.id,
                      contact_id: m.card.contactId,
                      contact_name: m.card.contactName,
                      create_rule: true,
                    }
                  );
                  const updated = res.data?.updated || 0;
                  const say = `Done — recategorized and approved ${updated} ${m.card.contactName} transactions as ${acct.code} ${acct.name}${res.data?.rule_id ? " and created a rule so future imports auto-post here" : ""}.`;
                  setMessages(mm => [...mm, { role: "assistant", content: say }]);
                  if (voiceOnRef.current) speakOne(say);
                  emitAction("txns:changed");
                  emitAction("cleanup-completed", {
                    contact_id: m.card.contactId,
                    kind: "contact_in_uncat",
                    count: updated,
                  });
                }}
                onDismiss={() => {
                  // Re-open the inquiry so the user can give a different category
                  // or add more detail about the transactions.
                  pendingIntentRef.current = {
                    kind: "cleanup-inquiry",
                    action: {
                      kind: "contact_in_uncat",
                      contact_id: m.card.contactId,
                      contact_name: m.card.contactName,
                      count: m.card.count,
                    },
                  };
                  const say = `OK — what should I categorize the ${m.card.count} ${m.card.contactName} transactions as instead? Or tell me more about what they are.`;
                  setMessages(mm => [...mm, { role: "assistant", content: say }]);
                  if (voiceOnRef.current) speakOne(say);
                }}
              />
            )}
          </div>
        ));
        })()}
      </div>

      <div className="border-t p-3">
        {batch && (() => {
          const cur = batch.txns[batch.idx] || {};
          return (
            <div
              className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2"
              data-testid="ai-batch-card"
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-[11px] uppercase tracking-wide font-semibold text-amber-900">
                  Batch Resolve · {batch.idx + 1} of {batch.txns.length}
                </span>
                <button
                  data-testid="ai-batch-exit"
                  onClick={batchExitBtn}
                  className="ml-auto text-[11px] text-amber-900/70 hover:text-amber-950"
                >
                  Exit
                </button>
              </div>
              <div className="text-[13px] font-medium text-amber-950 leading-tight">
                {(cur.merchant || cur.contact_name || "Unknown")}
                <span className="ml-1 text-amber-800/80">
                  {typeof cur.amount === "number"
                    ? ` · $${Math.abs(cur.amount).toFixed(2)}${cur.amount < 0 ? " out" : " in"}`
                    : ""}
                </span>
              </div>
              <div className="text-[11px] text-amber-800 mt-0.5">
                Suggested: <b>{cur.category_account_name || "Uncategorized"}</b>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <button
                  data-testid="ai-batch-accept"
                  onClick={batchAcceptBtn}
                  className="text-[11px] px-2 py-0.5 rounded bg-amber-600 text-white hover:bg-amber-700"
                >
                  ✓ Accept
                </button>
                <button
                  data-testid="ai-batch-skip"
                  onClick={batchSkipBtn}
                  className="text-[11px] px-2 py-0.5 rounded text-amber-900 hover:bg-amber-100"
                >
                  Skip
                </button>
                <span className="text-[10px] text-amber-800/70 ml-1">
                  say <b>"yes"</b> · <b>"skip"</b> · <b>"no, it's meals"</b>
                </span>
              </div>
            </div>
          );
        })()}
        {review && (
          <div
            className="mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2"
            data-testid="ai-review-card"
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[11px] uppercase tracking-wide font-semibold text-emerald-800">
                Review Mode · Step {review.idx + 1} of {review.steps.length}
              </span>
              <button
                data-testid="ai-review-exit"
                onClick={exitReview}
                className="ml-auto text-[11px] text-emerald-800/70 hover:text-emerald-900"
              >
                Exit
              </button>
            </div>
            <div className="text-[13px] font-medium text-emerald-950 leading-tight">
              {review.steps[review.idx]?.title}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <button
                data-testid="ai-review-back"
                onClick={() => advanceReview("back")}
                disabled={review.idx === 0}
                className="text-[11px] px-2 py-0.5 rounded text-emerald-900 hover:bg-emerald-100 disabled:opacity-40"
              >
                ← Back
              </button>
              <button
                data-testid="ai-review-next"
                onClick={() => advanceReview("next")}
                className="text-[11px] px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Next →
              </button>
              <span className="text-[11px] text-emerald-800/70 ml-1">
                or say <b>"next"</b> · <b>"back"</b> · <b>"exit"</b>
              </span>
            </div>
          </div>
        )}
        {pendingIntent && (
          <div
            className="mb-2 flex items-center gap-2 rounded-md bg-indigo-50 border border-indigo-200 px-2.5 py-2"
            data-testid="ai-pending-intent"
          >
            <Sparkles size={13} className="text-indigo-600 flex-shrink-0" />
            <span className="text-xs text-indigo-900 flex-1 leading-tight">
              Pending: <b>{pendingIntent.intent.replace(/_/g, " ")}</b>
              {pendingIntent.prefill?.contact_name ? ` · ${pendingIntent.prefill.contact_name}` : ""}
              {(() => {
                // Prefer computed line-total when the backend hydrated `lines[]`
                // from the item catalog; fall back to the parsed lump-sum amount.
                const lines = pendingIntent.prefill?.lines;
                if (Array.isArray(lines) && lines.length) {
                  const total = lines.reduce(
                    (s, l) => s + (Number(l.quantity || 1) * Number(l.rate || 0)),
                    0,
                  );
                  if (total > 0) return ` · $${total.toFixed(2)}`;
                }
                return pendingIntent.prefill?.amount ? ` · $${pendingIntent.prefill.amount}` : "";
              })()}
            </span>
            <button
              data-testid="ai-pending-confirm"
              onClick={async () => {
                const p = pendingIntent;
                setPendingIntent(null);
                const ok = await submitPendingIntent(p);
                const reply = ok ? "Created." : "Couldn't create — check the modal.";
                setMessages(m => [...m, { role: "assistant", content: reply }]);
                if (voiceOnRef.current) speakOne(reply);
              }}
              className="text-[11px] px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
            >
              Confirm
            </button>
            <button
              data-testid="ai-pending-cancel"
              onClick={() => {
                setPendingIntent(null);
                emitAction("close-current-modal");
              }}
              className="text-[11px] px-2 py-0.5 rounded text-indigo-800 hover:bg-indigo-100"
            >
              Cancel
            </button>
          </div>
        )}
        {listening && !peek && (
          <div className="mb-2 flex items-center gap-2 rounded-md bg-red-50 border border-red-200 px-2.5 py-1.5">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
            <span className="text-xs text-red-700 flex-1">
              {ttsSpeakingRef.current
                ? <>AI speaking — mic muted <span className="opacity-60">(barge in to interrupt)</span></>
                : <>Listening {micMode === "open" ? "· open-mic" : ""} {interim && <span className="italic">"{interim}"</span>}</>
              }
            </span>
          </div>
        )}
        {ttsActive && (
          <button
            data-testid="ai-stop-speaking"
            onClick={() => {
              // Kill any queued + currently-speaking utterances immediately.
              try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
              ttsSpeakingRef.current = false;
              setTtsActive(false);
            }}
            className="mb-2 w-full flex items-center justify-center gap-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2.5 py-2 shadow-sm transition"
            title='Stop the AI from talking (or say "stop" / "quiet")'
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-white/60 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            Stop AI speaking
            <span className="text-[10px] font-normal opacity-80">(or say "stop")</span>
          </button>
        )}
        {focus && focusPinned && !peek && !isMobile && (
          <button
            data-testid="ai-cancel-focus"
            onClick={() => setFocus(null, { force: true })}
            className="mb-2 w-full flex items-center justify-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium px-2.5 py-1.5 transition"
            title="Stop focusing on this transaction / bucket"
          >
            <X size={12} /> Cancel focus
            <span className="text-[10px] text-indigo-500 font-normal">
              (or say "cancel focus")
            </span>
          </button>
        )}
        <div className="flex gap-2">
          <MicButton
            mode={micMode}
            listening={listening}
            streaming={streaming}
            ttsSpeaking={ttsSpeakingRef.current}
            onCycle={() => {
              // Simple binary toggle: off ↔ open (live mic).
              setMicMode(prev => (prev === "off" ? "open" : "off"));
            }}
          />
          <input
            data-testid={TID.aiChatInput}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && input.trim()) {
                flipToChatOnActivity();
                send();
              }
            }}
            placeholder={
              isReviewChatRoute && reviewMode === "review"
                ? "Or describe your scenario — I'll walk you through it…"
                : listening ? "Speak now, or type…" : "Ask about a transaction, report, or anything..."
            }
            className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
            disabled={streaming}
            onFocus={() => setTyping(true)}
            onBlur={() => setTyping(false)}
          />
          <button
            data-testid={TID.aiChatSend}
            onClick={() => { flipToChatOnActivity(); send(); }}
            disabled={streaming || !input.trim()}
            className="w-9 h-9 flex items-center justify-center rounded-md bg-slate-900 text-white disabled:opacity-50"
          >
            <Send size={15} />
          </button>
        </div>
        {!peek && <VoiceHintTape micMode={micMode} />}
      </div>
    </aside>
  );
}

function VoiceHintTape({ micMode }) {
  // Rotating "Try saying..." examples to teach voice commands without a
  // wall-of-text tutorial. Only shows when the mic is engaged so it's
  // discoverable at the right moment.
  const HINTS = [
    'Try: "walk me through the books"  (paced review)',
    'Try: "let\'s clear the flagged transactions"',
    'Try: "read my P&L vs last quarter"',
    'Try: "why are my liabilities negative?"',
    'Try: "transactions for Walmart"',
    'Try: "open the July 15th McDonald\'s transaction"',
    'Try: "create an invoice for John Doe for 500 dollars"',
    'Try: "overdue invoices"',
    'Say "looks good" to save a draft · "stop" cancels speech',
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % HINTS.length), 4500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const active = micMode !== "off";
  return (
    <div
      key={idx}
      data-testid="ai-voice-hint"
      className={`mt-2 text-[10px] transition-opacity duration-500 ${
        active ? "text-indigo-600" : "text-slate-500"
      }`}
      style={{ animation: "fadeInUp 0.5s" }}
    >
      {HINTS[idx]}
    </div>
  );
}

function VoicePicker({ voices, voiceOn, setVoiceOn, voiceName, setVoiceName, voiceRate, setVoiceRate, terseness, setTerseness, speakOne, onClose }) {
  // Prefer English voices at the top of the list — everything else follows,
  // grouped by language. Keeps "Google UK English Female" easy to find on a
  // machine with 60+ voices installed.
  const sorted = [...voices].sort((a, b) => {
    const aEn = /^en/i.test(a.lang) ? 0 : 1;
    const bEn = /^en/i.test(b.lang) ? 0 : 1;
    if (aEn !== bEn) return aEn - bEn;
    return (a.lang + a.name).localeCompare(b.lang + b.name);
  });
  const preview = () => {
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    speakOne("Hi — this is your Axiom assistant. I'll read replies aloud in this voice.");
  };
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="absolute right-0 top-full mt-1 z-50 w-72 rounded-md border bg-white shadow-lg p-3 space-y-2"
        data-testid="ai-tts-voice-panel"
      >
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={voiceOn}
            onChange={(e) => setVoiceOn(e.target.checked)}
            data-testid="ai-tts-auto-checkbox"
          />
          Read responses aloud automatically
        </label>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">
            Voice
          </label>
          <select
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            data-testid="ai-tts-voice-select"
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            {sorted.length === 0 && <option value="">System default</option>}
            {sorted.map(v => (
              <option key={`${v.name}-${v.lang}`} value={v.name}>
                {v.name} ({v.lang})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">
            Reply length
          </label>
          <div
            className="inline-flex rounded-md border border-slate-300 overflow-hidden text-xs w-full"
            data-testid="ai-terseness"
          >
            {[
              { k: "concise",  l: "Concise",  hint: "1 sentence, ≤25 words" },
              { k: "balanced", l: "Balanced", hint: "1-3 sentences (default)" },
              { k: "detailed", l: "Detailed", hint: "Deep analysis, multi-paragraph" },
            ].map((o, i) => (
              <button
                key={o.k}
                onClick={() => setTerseness(o.k)}
                data-testid={`ai-terseness-${o.k}`}
                title={o.hint}
                className={`flex-1 px-2 py-1 ${i > 0 ? "border-l border-slate-300" : ""} ${
                  terseness === o.k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Rate</label>
            <span className="text-[11px] tabular-nums text-slate-600">{voiceRate.toFixed(2)}×</span>
          </div>
          <input
            type="range"
            min="0.8"
            max="1.4"
            step="0.05"
            value={voiceRate}
            onChange={(e) => setVoiceRate(parseFloat(e.target.value))}
            data-testid="ai-tts-rate-slider"
            className="w-full accent-slate-900 cursor-pointer"
          />
        </div>
        <button
          onClick={preview}
          data-testid="ai-tts-preview"
          className="w-full text-xs px-2.5 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 inline-flex items-center justify-center gap-1"
        >
          <Volume2 size={12} /> Preview
        </button>
        {sorted.length === 0 && (
          <p className="text-[11px] text-slate-500">
            No voices detected yet — Chrome loads them asynchronously. Refresh the page or
            check your OS voice settings.
          </p>
        )}
      </div>
    </>
  );
}


function MicButton({ mode, listening, streaming, ttsSpeaking, onCycle }) {
  // Simplified click-to-toggle: mic is either OFF or LIVE (open-mic).
  // No push-to-talk. One click flips the state.
  const isLive = mode === "open" || mode === "ptt"; // treat legacy 'ptt' as live
  const armed = isLive && listening;
  const cls = ttsSpeaking
    ? "border-slate-200 bg-slate-100 text-slate-400"
    : armed
      ? "bg-red-500 border-red-500 text-white"
      : isLive
        ? "border-red-300 text-red-600 hover:bg-red-50"
        : "border-slate-200 text-slate-600 hover:bg-slate-50";
  const title = isLive
    ? "Voice on — click to mute"
    : "Voice off — click to go live";
  return (
    <div className="relative">
      <button
        data-testid="ai-chat-mic"
        onClick={(e) => { e.preventDefault(); onCycle(); }}
        disabled={streaming}
        className={`relative w-9 h-9 flex items-center justify-center rounded-md border transition select-none ${cls}`}
        title={title}
        aria-pressed={isLive}
      >
        {armed ? <MicOff size={15} /> : <Mic size={15} />}
      </button>
      {isLive && !ttsSpeaking && (
        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
      )}
    </div>
  );
}




// ReviewStartersPane — Review-Chat page's dedicated starter view.
// Renders 5 clickable helper cards using an ACCORDION pattern: click
// a card and its canned answer expands right underneath the same
// card. Click it again to collapse, or click a different card and
// the current one rolls up while the new one opens. This keeps
// context in place (no flip-to-chat), lets the user browse multiple
// topics without stream clutter, and stays fully local (no LLM call).
// Free-form scenarios still route through the parent's bottom input
// and hit the LLM.
function ReviewStartersPane() {
  const [openKey, setOpenKey] = useState(null);
  // Fires either the Tour or a specific Copilot playbook. ChatReview.jsx
  // listens for these events and mounts the ChatReviewTour engine.
  const runAction = (action) => {
    if (!action) return;
    if (action.type === "tour") {
      window.dispatchEvent(new CustomEvent("chat-review-start-tour"));
    } else if (action.type === "playbook") {
      window.dispatchEvent(new CustomEvent("chat-review-run-playbook", {
        detail: { playbook_key: action.key, slots: {}, force: true },
      }));
    }
  };
  const CARDS = [
    {
      key: "how-it-works",
      icon: HelpCircle,
      tone: "text-indigo-700 bg-indigo-50",
      title: "How does this work?",
      preview: "A 30-second tour of the Review Books chat.",
      action: { type: "tour", label: "Start the tour" },
      a:
`**Review Books chat, in 30 seconds:**

1. I group transactions I'm not 100% sure about into **questions**. Each question is one card in the middle of the page.
2. For each card, I show you the merchant, dollar amount, and my best guess. You just confirm — or correct me — in your own words at the bottom.
3. When you confirm, I book the whole group in one keystroke.
4. Not sure or don't have time? Hit **Skip for now** — I'll come back to it. Nothing posts until you confirm.

The green bar at the top shows your books are **N% confirmed by dollar value** — so the biggest, riskiest questions come first. Answer 5-10 of these and your books usually flip from ~65% → 95%.`,
    },
    {
      key: "view-all",
      icon: Eye,
      tone: "text-sky-700 bg-sky-50",
      title: "View all transactions",
      preview: "Open the full list of transactions on a card.",
      action: { type: "playbook", key: "view-all-transactions", label: "Show me how" },
      a:
`If a card mentions "**Scroll to see all 29**", the inline list is clipped to 5 rows. Two ways to see everything:

- **Scroll** — the little list is scrollable, use your trackpad.
- **Show all** (top-right of the transaction list) — opens a full-page popup with every row, an internal scroll, and the same per-row **⋯** menu.

In the Show-all view you can also **filter** by date, amount, or description, and use the **⋯** menu on any row to edit, recategorize, split, link to a bill/invoice, ask the client, or delete just that one.`,
    },
    {
      key: "multiple-contacts",
      icon: Users,
      tone: "text-amber-700 bg-amber-50",
      title: "Multiple contacts",
      preview: "The question mixes two or more vendors/customers.",
      action: { type: "playbook", key: "wrong-mixed-contacts", label: "Walk me through it" },
      a:
`Sometimes I bundle rows that share a merchant string but really belong to different people (e.g. everything labeled "WELLS FARGO" that's actually from 3 different clients).

You've got two options:

1. **Update contact** (link right under "Tell us in your own words") — rename or reassign the contact for the whole card. Best when it's *all* one wrong contact.
2. **Split into subgroups** — enter selection mode, check the rows that belong to Contact A, hit **Ask separately** so they peel off into their own card. Repeat for Contact B, C, ... Then answer each smaller card individually.

Either way, I remember the fix so the AI stops mis-labelling next time.`,
    },
    {
      key: "multiple-categories",
      icon: Layers,
      tone: "text-rose-700 bg-rose-50",
      title: "Multiple categories",
      preview: "The rows belong in different accounts.",
      action: { type: "playbook", key: "wrong-mixed-categories", label: "Walk me through it" },
      a:
`Two different flavors here — and they're easy to confuse:

**A. The card mixes different question types** (e.g. 20 client deposits + 3 equity contributions all lumped as "Wells Fargo deposits").
Use **Split into subgroups** at the bottom of the card → check the equity rows → **Ask separately**. They peel off into their own card; answer each with the right category.

**B. A single transaction needs to be split across categories** (e.g. one $500 Amazon charge = $450 office supplies + $50 sales tax).
Use the per-row **⋯** menu → **Split** → enter the line items and their accounts. This creates a multi-line journal entry for that one transaction.

Rule of thumb: **subgroups = different questions**, **row-split = one transaction, many lines**.`,
    },
    {
      key: "link-bill-invoice",
      icon: LinkIcon,
      tone: "text-emerald-700 bg-emerald-50",
      title: "Link to a bill or invoice",
      preview: "Match a payment to an open AR/AP doc.",
      action: { type: "playbook", key: "link-to-invoice", label: "Walk me through it" },
      a:
`If a payment matches an **open bill** (AP) or **open invoice** (AR), you can link it so the doc gets closed out and the AR/AP account clears automatically.

**On a single row:** click **⋯** on the row → **Link to bill or invoice** → pick the matching doc from the list. I'll show you the closest matches by amount and contact first.

**On a whole question card:** if I detected the match confidently, you'll see a **Link** action right on the card. Confirm and I'll post the payment against the AR/AP account instead of revenue/expense — no double-counting.

When in doubt, link — it's always safer than booking to a fresh income/expense account.`,
    },
  ];

  return (
    <div
      className="flex-1 overflow-y-auto p-4 space-y-2"
      data-testid="ai-panel-review-pane"
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">
        Common questions
      </div>
      {CARDS.map((c) => {
        const Icon = c.icon;
        const open = openKey === c.key;
        return (
          <div key={c.key} className="space-y-0">
            <button
              type="button"
              onClick={() => setOpenKey(open ? null : c.key)}
              className={`w-full text-left rounded-xl border transition-all p-3 flex items-start gap-3 group ${
                open
                  ? "border-indigo-300 bg-indigo-50/40 shadow-sm rounded-b-none"
                  : "border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md"
              }`}
              data-testid={`ai-panel-review-card-${c.key}`}
              aria-expanded={open}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${c.tone}`}>
                <Icon size={17} />
              </div>
              <div className="min-w-0 flex-1">
                <div className={`text-sm font-semibold ${open ? "text-indigo-700" : "text-slate-900 group-hover:text-indigo-700"}`}>
                  {c.title}
                </div>
                <div className="text-[12px] text-slate-500 leading-snug mt-0.5">
                  {c.preview}
                </div>
              </div>
              <ChevronDown
                size={16}
                className={`text-slate-400 shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
              />
            </button>
            {open && (
              <div
                className="rounded-b-xl border border-t-0 border-indigo-300 bg-white p-4 text-[13px] leading-relaxed text-slate-700 chat-md"
                data-testid={`ai-panel-review-answer-${c.key}`}
              >
                {c.action && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); runAction(c.action); }}
                    className="inline-flex items-center gap-1.5 mb-3 text-[12px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline underline-offset-2 transition"
                    data-testid={`ai-panel-review-action-${c.key}`}
                  >
                    <PlayCircle size={14} strokeWidth={2.25} />
                    {c.action.label}
                  </button>
                )}
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {c.a}
                </ReactMarkdown>
              </div>
            )}
          </div>
        );
      })}
      <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-3 text-[12px] text-slate-600 leading-relaxed">
        <b className="text-slate-800">Have your own scenario?</b><br />
        Type it in the box below — describe your situation and I'll walk you through the best steps.
      </div>
    </div>
  );
}
